import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  DollarSign, Loader2, Plus, Settings, Calculator, FileText, History,
  Users, Building2, CreditCard, Wallet, TrendingUp, TrendingDown,
  CheckCircle, Clock, AlertCircle, Download, Eye, Edit, Trash2,
  PiggyBank, Banknote, Receipt, Calendar, FileSpreadsheet, Upload,
  Printer, LayoutGrid, List, Table, Search, MoreVertical, Copy,
  Sparkles, Map, FileCode, RefreshCw, ChevronRight, ChevronLeft, ArrowRight,
  HeartPulse, UsersRound, Briefcase, Cake, ImagePlus
} from "lucide-react";

const TH_CLS = "px-6 py-3.5 text-left text-[11px] font-bold text-[#0C314C] dark:text-slate-300 uppercase tracking-wider whitespace-nowrap";
const TIPOS_BENEFICIO = ["Últimos Gastos", "Seguro Médico Privado", "Seguro de Vida", "Seguro Gastos Mayores"];
const TD_CLS = "px-6 py-4 text-sm text-slate-700 dark:text-slate-300 align-middle";

const KpiCard = ({ label, value, sub, icon: Icon, onClick, active, testId, small }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={!!active}
    data-testid={testId}
    className={`text-left w-full rounded-2xl p-5 border relative overflow-hidden group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#45E69A] focus-visible:ring-offset-2 transition-colors transition-shadow transition-transform duration-200 ${
      active
        ? "bg-gradient-to-br from-[#0C314C] to-slate-800 border-[#45E69A] text-white shadow-lg ring-2 ring-[#45E69A]/70"
        : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md hover:-translate-y-1 hover:border-[#45E69A]"
    } ${onClick ? "cursor-pointer" : "cursor-default"}`}
  >
    <span className={`absolute -right-5 -top-5 w-24 h-24 rounded-full transition-colors duration-200 ${active ? "bg-[#45E69A]/20" : "bg-[#0C314C]/5 group-hover:bg-[#45E69A]/10"}`} />
    <div className="flex items-start justify-between relative gap-2">
      <div className="min-w-0">
        <p className={`text-[11px] font-semibold uppercase tracking-wider ${active ? "text-slate-300" : "text-slate-500"}`}>{label}</p>
        <p className={`font-mono font-bold mt-1.5 tracking-tight truncate ${small ? "text-xl" : "text-3xl"} ${active ? "text-white" : "text-[#0C314C] dark:text-white"}`}>{value}</p>
        {sub && <p className={`text-xs mt-1 truncate font-medium ${active ? "text-[#45E69A]" : "text-slate-400"}`}>{sub}</p>}
      </div>
      <div className={`rounded-xl p-2.5 shrink-0 transition-colors duration-200 ${active ? "bg-[#45E69A]/20" : "bg-[#0C314C]/5 group-hover:bg-[#45E69A]/15"}`}>
        <Icon className={`w-5 h-5 ${active ? "text-[#45E69A]" : "text-[#0C314C] dark:text-slate-300"}`} />
      </div>
    </div>
  </button>
);

export default function NominaPage() {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  const [parametros, setParametros] = useState(null);
  const [empleadosNomina, setEmpleadosNomina] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [prestamos, setPrestamos] = useState([]);
  const [avances, setAvances] = useState([]);
  const [nominasHistorial, setNominasHistorial] = useState([]);
  const [nominaActual, setNominaActual] = useState(null);
  const [nominasConfig, setNominasConfig] = useState([]);
  const [plantillasNomina, setPlantillasNomina] = useState([]);
  const [bancosConfig, setBancosConfig] = useState([]);
  // Seguro Médico y Dependientes TSS
  const [segurosMedicos, setSegurosMedicos] = useState([]);
  const [dependientes, setDependientes] = useState([]);
  const [capitaDependiente, setCapitaDependiente] = useState(0);
  const [capitaInput, setCapitaInput] = useState(0);
  const [showSeguroDialog, setShowSeguroDialog] = useState(false);
  const [seguroEditMode, setSeguroEditMode] = useState(false);
  const [editingSeguro, setEditingSeguro] = useState(null);
  const [seguroForm, setSeguroForm] = useState({ user_id: "", tipo_seguro: "", monto: 0, frecuencia: "mensual", fecha_inicio: "", cantidad_dependientes: 0, costo_titular: 0, costo_dependiente: 0, titular_cubierto_empresa: false, dependientes_cubierto_empresa: false, aplica_descuento: false, pct_empresa: 100, pct_empleado: 0, observaciones: "", activo: true });
  const [searchSeguros, setSearchSeguros] = useState("");
  const [showDependienteDialog, setShowDependienteDialog] = useState(false);
  const [dependienteEditMode, setDependienteEditMode] = useState(false);
  const [editingDependiente, setEditingDependiente] = useState(null);
  const [dependienteForm, setDependienteForm] = useState({ user_id: "", nombre: "", parentesco: "padre", observaciones: "" });
  const [searchDependientes, setSearchDependientes] = useState("");
  // Departamentos y Cargos
  const [departamentos, setDepartamentos] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [showDeptDialog, setShowDeptDialog] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [deptForm, setDeptForm] = useState({ name: "", description: "" });
  const [searchDeptos, setSearchDeptos] = useState("");
  const [showCargoDialog, setShowCargoDialog] = useState(false);
  const [editingCargo, setEditingCargo] = useState(null);
  const [cargoForm, setCargoForm] = useState({ nombre: "", descripcion: "" });
  const [searchCargos, setSearchCargos] = useState("");
  // Cumpleaños del mes
  const [cumpleMes, setCumpleMes] = useState({ cantidad: 0, cumpleaneros: [], mes_nombre: "" });
  const [cumplePeriodo, setCumplePeriodo] = useState(() => new Date().toISOString().slice(0, 7));
  const [descargandoCumple, setDescargandoCumple] = useState(false);
  const [regenerandoCumple, setRegenerandoCumple] = useState(false);
  
  // Views & filtros dinámicos
  const [searchQuery, setSearchQuery] = useState("");
  const [empFilter, setEmpFilter] = useState(null);
  const [empBancoFilter, setEmpBancoFilter] = useState("todos");
  const [prestamosFilter, setPrestamosFilter] = useState(null);
  const [searchPrestamos, setSearchPrestamos] = useState("");
  const [avancesFilter, setAvancesFilter] = useState(null);
  const [searchAvances, setSearchAvances] = useState("");
  const [historialFilter, setHistorialFilter] = useState(null);
  const [searchHistorial, setSearchHistorial] = useState("");
  
  // Dialogs
  const [showParametrosDialog, setShowParametrosDialog] = useState(false);
  const [showEmpleadoDialog, setShowEmpleadoDialog] = useState(false);
  const [showPrestamoDialog, setShowPrestamoDialog] = useState(false);
  const [showAvanceDialog, setShowAvanceDialog] = useState(false);
  const [showCalcularDialog, setShowCalcularDialog] = useState(false);
  const [showNominaDetailDialog, setShowNominaDetailDialog] = useState(false);
  const [showEmpleadoDetailDialog, setShowEmpleadoDetailDialog] = useState(false);
  const [showConfigNominaDialog, setShowConfigNominaDialog] = useState(false);
  const [showPlantillaDialog, setShowPlantillaDialog] = useState(false);
  const [showBancoTxtDialog, setShowBancoTxtDialog] = useState(false);
  const [showNuevaNominaDialog, setShowNuevaNominaDialog] = useState(false);
  const [showPrestamoDetailDialog, setShowPrestamoDetailDialog] = useState(false);
  const [showAvanceDetailDialog, setShowAvanceDetailDialog] = useState(false);
  const [showNominaConfigDetailDialog, setShowNominaConfigDetailDialog] = useState(false);
  
  // IA Analysis states
  const [showPlantillaIADialog, setShowPlantillaIADialog] = useState(false);
  const [plantillaIAStep, setPlantillaIAStep] = useState(1); // 1: upload, 2: analysis, 3: mapping, 4: preview
  const [plantillaIAFile, setPlantillaIAFile] = useState(null);
  const [plantillaIAAnalysis, setPlantillaIAAnalysis] = useState(null);
  const [plantillaIAAnalyzing, setPlantillaIAAnalyzing] = useState(false);
  const [camposDisponibles, setCamposDisponibles] = useState([]);
  const [mapeosCampos, setMapeosCampos] = useState([]);
  const [plantillaIAPreview, setPlantillaIAPreview] = useState(null);
  
  // Banco TXT IA states
  const [showBancoTxtIADialog, setShowBancoTxtIADialog] = useState(false);
  const [bancoTxtFile, setBancoTxtFile] = useState(null);
  const [bancoTxtAnalysis, setBancoTxtAnalysis] = useState(null);
  const [bancoTxtAnalyzing, setBancoTxtAnalyzing] = useState(false);
  const [bancoTxtFormatos, setBancoTxtFormatos] = useState([]);
  
  // Document Generation AI states
  const [showGenerarDocumentoDialog, setShowGenerarDocumentoDialog] = useState(false);
  const [documentoTipo, setDocumentoTipo] = useState("comprobante");
  const [documentoEmpleadoId, setDocumentoEmpleadoId] = useState(null);
  const [documentoGenerado, setDocumentoGenerado] = useState(null);
  const [generandoDocumento, setGenerandoDocumento] = useState(false);
  const [tiposDocumento, setTiposDocumento] = useState([]);
  
  const [saving, setSaving] = useState(false);
  // Eliminar nómina (historial)
  const [showEliminarNominaDialog, setShowEliminarNominaDialog] = useState(false);
  const [nominaAEliminar, setNominaAEliminar] = useState(null);
  const [eliminarConfirmForm, setEliminarConfirmForm] = useState({ email: "", password: "" });
  const [eliminandoNomina, setEliminandoNomina] = useState(false);
  const [editingEmpleado, setEditingEmpleado] = useState(null);
  const [viewingEmpleado, setViewingEmpleado] = useState(null);
  const [editMode, setEditMode] = useState(false); // false = create, true = edit
  const [editingPrestamo, setEditingPrestamo] = useState(null);
  const [viewingPrestamo, setViewingPrestamo] = useState(null);
  const [editingAvance, setEditingAvance] = useState(null);
  const [viewingAvance, setViewingAvance] = useState(null);
  const [editingNominaConfig, setEditingNominaConfig] = useState(null);
  const [viewingNominaConfig, setViewingNominaConfig] = useState(null);
  const [prestamoEditMode, setPrestamoEditMode] = useState(false);
  const [avanceEditMode, setAvanceEditMode] = useState(false);
  const [nominaConfigEditMode, setNominaConfigEditMode] = useState(false);
  
  // Forms
  const [parametrosForm, setParametrosForm] = useState({
    tss_empleado_afp: 2.87,
    tss_empleado_sfs: 3.04,
    tss_empleador_afp: 7.10,
    tss_empleador_sfs: 7.09,
    tss_empleador_srl: 1.20,
    tss_empleador_infotep: 1.0,
    isr_exento_hasta: 416220.00,
    isr_tramo1_hasta: 624329.00,
    isr_tramo1_pct: 15.0,
    isr_tramo2_hasta: 867123.00,
    isr_tramo2_pct: 20.0,
    isr_tramo3_hasta: 1200000.00,
    isr_tramo3_pct: 25.0,
    salario_minimo_no_sectorizado: 21000.00,
    salario_minimo_pequeña_empresa: 12900.00,
    salario_minimo_grande_empresa: 23187.00,
    tope_cotizable_afp: 508479.00,
    tope_cotizable_sfs: 508479.00,
    nombre_empresa: "",
    rnc_empresa: "",
    direccion_empresa: "",
    telefono_empresa: "",
    descuento_tss_quincena: "ambas",
    descuento_isr_quincena: "ambas",
    descuento_beneficios_quincena: "ambas",
    descuento_dependientes_quincena: "ambas",
    descuento_prestamos_quincena: "ambas",
    descuento_avances_quincena: "ambas"
  });
  
  const [empleadoForm, setEmpleadoForm] = useState({
    user_id: "",
    es_externo: false,
    nombre: "",
    email: "",
    codigo_empleado: "",
    salario_bruto: 0,
    tipo_contrato: "fijo",
    tipo_salario: "mensual",
    fecha_ingreso: "",
    cuenta_banco: "",
    tipo_cuenta: "ahorro",
    banco: "",
    comisiones: 0,
    bonos: 0,
    horas_extra: 0,
    valor_hora_extra: 0,
    otras_deducciones: 0,
    descripcion_otras_deducciones: "",
    departamento_id: "",
    cargo_id: "",
    estado_civil: "",
    fecha_nacimiento: "",
    conyuge_nombre: "",
    conyuge_fecha_nacimiento: "",
    contactos_emergencia: [],
    hijos: [],
    info_adicional: "",
    nomina_id: "principal"
  });
  
  const [prestamoForm, setPrestamoForm] = useState({
    user_id: "",
    monto_total: 0,
    cuota: 0,
    tipo_cuota: "mensual", // quincenal o mensual
    plazo: 12,
    tasa_interes: 0,
    motivo: "",
    fecha_inicio: new Date().toISOString().split('T')[0]
  });
  
  const [avanceForm, setAvanceForm] = useState({
    user_id: "",
    monto: 0,
    tipo_descuento: "mensual", // quincenal o mensual
    cuotas: 1,
    motivo: "",
    fecha_inicio: new Date().toISOString().split('T')[0]
  });
  
  const [calcularForm, setCalcularForm] = useState({
    periodo: new Date().toISOString().slice(0, 7),
    tipo_periodo: "mensual",
    fecha_pago: "",
    notas: "",
    nomina_id: "principal"
  });

  const [nuevaNominaForm, setNuevaNominaForm] = useState({
    nombre: "",
    descripcion: "",
    tipo_periodo: "mensual",
    activa: true,
    es_default: false,
    banco_default: "",
    formato_txt_banco: "",
    cuenta_empresa: "",
    tipo_cuenta_empresa: "corriente",
    dia_pago: 15,
    dia_pago_1: 15,
    dia_pago_2: 30,
    dia_semana_pago: "viernes",
    incluir_tss: true,
    incluir_isr: true,
    plantilla_recibo_id: "",
    plantilla_resumen_id: "",
    notas: ""
  });

  const [plantillaForm, setPlantillaForm] = useState({
    nombre: "",
    descripcion: "",
    tipo: "recibo", // recibo, resumen, reporte
    archivo: null
  });

  const [bancoTxtForm, setBancoTxtForm] = useState({
    banco: "",
    formato: "banco_popular", // banco_popular, banreservas, bhd_leon, scotiabank
    nomina_id: ""
  });

  // Alertas state
  const [alertas, setAlertas] = useState({ alertas: [], total: 0 });
  
  const { getAuthHeaders, user } = useAuth();
  const rolUsuario = (user?.role || "").toLowerCase();
  const esAdminRol = rolUsuario === "admin" || rolUsuario === "administrador";
  const esGHRol = rolUsuario.includes("gestion") || rolUsuario.includes("gestión");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [paramsRes, empNomRes, usersRes, prestRes, avancesRes, histRes, nominasRes, plantillasRes, bancosRes, alertasRes] = await Promise.all([
        axios.get(`${API}/nomina/parametros`, getAuthHeaders()),
        axios.get(`${API}/nomina/empleados`, getAuthHeaders()),
        axios.get(`${API}/users`, getAuthHeaders()),
        axios.get(`${API}/nomina/prestamos`, getAuthHeaders()),
        axios.get(`${API}/nomina/avances`, getAuthHeaders()),
        axios.get(`${API}/nomina/historial`, getAuthHeaders()),
        axios.get(`${API}/nomina/config/nominas`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/nomina/plantillas`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/nomina/bancos-config`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/nomina/alertas`, getAuthHeaders()).catch(() => ({ data: { alertas: [], total: 0 } }))
      ]);
      const [segurosRes, depsRes, depsCfgRes, deptosRes, cargosRes] = await Promise.all([
        axios.get(`${API}/nomina/seguros-medicos`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/nomina/dependientes`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/nomina/dependientes/config`, getAuthHeaders()).catch(() => ({ data: { capita_dependiente: 0 } })),
        axios.get(`${API}/departamentos`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/nomina/cargos`, getAuthHeaders()).catch(() => ({ data: [] }))
      ]);
      setDepartamentos(deptosRes.data);
      setCargos(cargosRes.data);
      setSegurosMedicos(segurosRes.data);
      setDependientes(depsRes.data);
      setCapitaDependiente(depsCfgRes.data?.capita_dependiente || 0);
      setCapitaInput(depsCfgRes.data?.capita_dependiente || 0);
      setParametros(paramsRes.data);
      setParametrosForm(prev => ({ ...prev, ...paramsRes.data }));
      setEmpleadosNomina(empNomRes.data);
      setUsuarios(usersRes.data);
      setPrestamos(prestRes.data);
      setAvances(avancesRes.data);
      setNominasHistorial(histRes.data);
      setNominasConfig(nominasRes.data);
      setPlantillasNomina(plantillasRes.data);
      setBancosConfig(bancosRes.data);
      setAlertas(alertasRes.data);
    } catch (err) {
      console.error("Error fetching data:", err);
      toast.error("Error al cargar datos de nómina");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchCumpleMes = useCallback(async (periodo) => {
    try {
      const r = await axios.get(`${API}/nomina-cumpleanos`, { ...getAuthHeaders(), params: { periodo } });
      setCumpleMes(r.data);
    } catch (err) { /* silencioso */ }
  }, [getAuthHeaders]);

  // === Plantillas de Cumpleaños ===
  const [plantillasCumple, setPlantillasCumple] = useState([]);
  const [plantillasThumbs, setPlantillasThumbs] = useState({});
  const [subiendoPlantilla, setSubiendoPlantilla] = useState(false);

  const fetchPlantillasCumple = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/nomina-cumpleanos/plantillas`, getAuthHeaders());
      setPlantillasCumple(r.data.plantillas || []);
    } catch (err) { /* silencioso */ }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (activeTab === "plantillas_cumple") fetchPlantillasCumple();
  }, [activeTab, fetchPlantillasCumple]);

  useEffect(() => {
    plantillasCumple.forEach(async (p) => {
      if (plantillasThumbs[p.id]) return;
      try {
        const r = await axios.get(`${API}/nomina-cumpleanos/plantillas/${p.id}/imagen`, { ...getAuthHeaders(), responseType: "blob" });
        setPlantillasThumbs(prev => ({ ...prev, [p.id]: URL.createObjectURL(r.data) }));
      } catch (err) { /* silencioso */ }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantillasCumple]);

  const handleSubirPlantilla = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setSubiendoPlantilla(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await axios.post(`${API}/nomina-cumpleanos/plantillas`, fd, getAuthHeaders());
      }
      toast.success(`${files.length} plantilla(s) cargada(s)`);
      fetchPlantillasCumple();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir la plantilla");
    } finally {
      setSubiendoPlantilla(false);
      e.target.value = "";
    }
  };

  const handleEliminarPlantilla = async (id) => {
    if (!window.confirm("¿Eliminar esta plantilla? Los próximos diseños ya no la usarán.")) return;
    try {
      await axios.delete(`${API}/nomina-cumpleanos/plantillas/${id}`, getAuthHeaders());
      toast.success("Plantilla eliminada");
      fetchPlantillasCumple();
    } catch (err) {
      toast.error("Error al eliminar la plantilla");
    }
  };

  // === Envío automático del diseño de cumpleaños por correo (solo admin) ===
  const [showEnvioCumpleDialog, setShowEnvioCumpleDialog] = useState(false);
  const [envioCumpleCfg, setEnvioCumpleCfg] = useState({ enabled: false, dia: 1, hora: "08:00", correos: "", last_sent_periodo: null, last_sent_ok: null });
  const [guardandoEnvioCumple, setGuardandoEnvioCumple] = useState(false);
  const [enviandoCumpleAhora, setEnviandoCumpleAhora] = useState(false);

  const abrirEnvioCumpleDialog = async (e) => {
    e.stopPropagation();
    setShowEnvioCumpleDialog(true);
    try {
      const r = await axios.get(`${API}/nomina-cumpleanos/envio-config`, getAuthHeaders());
      setEnvioCumpleCfg({ ...r.data, correos: (r.data.correos || []).join("\n") });
    } catch (err) { /* usa defaults */ }
  };

  const parseCorreosCumple = (txt) => (txt || "").split(/[\n,;]+/).map(c => c.trim()).filter(Boolean);

  const guardarEnvioCumpleCfg = async () => {
    const correos = parseCorreosCumple(envioCumpleCfg.correos);
    await axios.put(`${API}/nomina-cumpleanos/envio-config`, {
      enabled: !!envioCumpleCfg.enabled,
      dia: Number(envioCumpleCfg.dia) || 1,
      hora: envioCumpleCfg.hora || "08:00",
      correos
    }, getAuthHeaders());
    return correos;
  };

  const handleGuardarEnvioCumple = async () => {
    if (envioCumpleCfg.enabled && parseCorreosCumple(envioCumpleCfg.correos).length === 0) {
      toast.error("Agregue al menos un correo destinatario");
      return;
    }
    setGuardandoEnvioCumple(true);
    try {
      await guardarEnvioCumpleCfg();
      toast.success("Configuración de envío guardada");
      setShowEnvioCumpleDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar la configuración");
    } finally {
      setGuardandoEnvioCumple(false);
    }
  };

  const handleEnviarCumpleAhora = async () => {
    if (parseCorreosCumple(envioCumpleCfg.correos).length === 0) {
      toast.error("Agregue al menos un correo destinatario");
      return;
    }
    setEnviandoCumpleAhora(true);
    try {
      const correos = await guardarEnvioCumpleCfg();
      await axios.post(`${API}/nomina-cumpleanos/envio-config/enviar-ahora`, {}, getAuthHeaders());
      toast.success(`Diseño enviado a ${correos.length} correo(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar el correo");
    } finally {
      setEnviandoCumpleAhora(false);
    }
  };

  useEffect(() => {
    fetchCumpleMes(cumplePeriodo);
  }, [cumplePeriodo, fetchCumpleMes]);

  // Save parameters
  const handleSaveParametros = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/nomina/parametros`, parametrosForm, getAuthHeaders());
      toast.success("Parámetros guardados");
      setShowParametrosDialog(false);
      fetchData();
    } catch (err) {
      toast.error("Error al guardar parámetros");
    } finally {
      setSaving(false);
    }
  };

  // Save employee nomina config
  const handleSaveEmpleado = async () => {
    if (empleadoForm.es_externo) {
      if (!empleadoForm.nombre?.trim()) {
        toast.error("Ingrese el nombre del empleado externo");
        return;
      }
    } else if (!empleadoForm.user_id) {
      toast.error("Seleccione un empleado del sistema o márquelo como externo");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...empleadoForm };
      delete payload.es_externo;
      payload.departamento_id = payload.departamento_id || null;
      payload.cargo_id = payload.cargo_id || null;
      payload.departamento_nombre = departamentos.find(d => d.id === payload.departamento_id)?.name || "";
      payload.cargo_nombre = cargos.find(c => c.id === payload.cargo_id)?.nombre || "";
      if (empleadoForm.es_externo && !editMode) payload.user_id = null;
      await axios.post(`${API}/nomina/empleados`, payload, getAuthHeaders());
      toast.success(editMode ? "Empleado actualizado" : "Empleado agregado a nómina");
      setShowEmpleadoDialog(false);
      resetEmpleadoForm();
      fetchData();
      axios.post(`${API}/nomina-cumpleanos/generar`, {}, getAuthHeaders())
        .then(() => fetchCumpleMes(cumplePeriodo))
        .catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const resetEmpleadoForm = () => {
    setEmpleadoForm({
      user_id: "", es_externo: false, nombre: "", email: "", codigo_empleado: "",
      salario_bruto: 0, tipo_contrato: "fijo", tipo_salario: "mensual",
      fecha_ingreso: "", cuenta_banco: "", tipo_cuenta: "ahorro", banco: "",
      comisiones: 0, bonos: 0, horas_extra: 0, valor_hora_extra: 0,
      otras_deducciones: 0, descripcion_otras_deducciones: "", departamento_id: "", cargo_id: "",
      estado_civil: "", fecha_nacimiento: "", conyuge_nombre: "", conyuge_fecha_nacimiento: "", contactos_emergencia: [], hijos: [], info_adicional: "", nomina_id: "principal"
    });
    setEditMode(false);
  };

  const openEditEmpleado = (emp) => {
    setEmpleadoForm({
      user_id: emp.user_id,
      es_externo: !!emp.externo,
      nombre: emp.nombre || "",
      email: emp.email || "",
      codigo_empleado: emp.codigo_empleado || "",
      salario_bruto: emp.salario_bruto || 0,
      tipo_contrato: emp.tipo_contrato || "fijo",
      tipo_salario: emp.tipo_salario || "mensual",
      fecha_ingreso: emp.fecha_ingreso || "",
      cuenta_banco: emp.cuenta_banco || "",
      tipo_cuenta: emp.tipo_cuenta || "ahorro",
      banco: emp.banco || "",
      comisiones: emp.comisiones || 0,
      bonos: emp.bonos || 0,
      horas_extra: emp.horas_extra || 0,
      valor_hora_extra: emp.valor_hora_extra || 0,
      otras_deducciones: emp.otras_deducciones || 0,
      descripcion_otras_deducciones: emp.descripcion_otras_deducciones || "",
      departamento_id: emp.departamento_id || "",
      cargo_id: emp.cargo_id || "",
      estado_civil: emp.estado_civil || "",
      fecha_nacimiento: emp.fecha_nacimiento || "",
      conyuge_nombre: emp.conyuge_nombre || "",
      conyuge_fecha_nacimiento: emp.conyuge_fecha_nacimiento || "",
      contactos_emergencia: emp.contactos_emergencia || [],
      hijos: emp.hijos || [],
      info_adicional: emp.info_adicional || "",
      nomina_id: emp.nomina_id || "principal"
    });
    setEditMode(true);
    setShowEmpleadoDialog(true);
  };

  const [importandoUsuarios, setImportandoUsuarios] = useState(false);
  const handleImportarUsuarios = async () => {
    setImportandoUsuarios(true);
    try {
      const res = await axios.post(`${API}/nomina/empleados/importar-usuarios`, {}, getAuthHeaders());
      const n = res.data?.importados || 0;
      toast.success(n > 0 ? `${n} usuario(s) importado(s) a nómina` : "Todos los usuarios ya están en nómina");
      fetchData();
    } catch (err) {
      toast.error("Error al importar usuarios");
    } finally {
      setImportandoUsuarios(false);
    }
  };

  // ===== Otros Beneficios =====
  const resetSeguroForm = () => {
    setSeguroForm({ user_id: "", tipo_seguro: "", monto: 0, frecuencia: "mensual", fecha_inicio: "", cantidad_dependientes: 0, costo_titular: 0, costo_dependiente: 0, titular_cubierto_empresa: false, dependientes_cubierto_empresa: false, aplica_descuento: false, pct_empresa: 100, pct_empleado: 0, observaciones: "", activo: true });
    setSeguroEditMode(false);
    setEditingSeguro(null);
  };

  const openEditSeguro = (s) => {
    setSeguroForm({
      user_id: s.user_id, tipo_seguro: s.tipo_seguro || "", monto: s.monto || 0,
      frecuencia: s.frecuencia || "mensual",
      fecha_inicio: s.fecha_inicio || "",
      cantidad_dependientes: s.cantidad_dependientes || 0,
      costo_titular: s.costo_titular || 0,
      costo_dependiente: s.costo_dependiente || 0,
      titular_cubierto_empresa: !!s.titular_cubierto_empresa,
      dependientes_cubierto_empresa: !!s.dependientes_cubierto_empresa,
      aplica_descuento: !!s.aplica_descuento,
      pct_empresa: s.pct_empresa ?? 100,
      pct_empleado: s.pct_empleado ?? 0,
      observaciones: s.observaciones || "", activo: s.activo !== false
    });
    setEditingSeguro(s);
    setSeguroEditMode(true);
    setShowSeguroDialog(true);
  };

  // Auto-calcula el monto a pagar: excluye los componentes cubiertos por la empresa
  const updateSeguroCostos = (changes) => {
    const next = { ...seguroForm, ...changes };
    const titular = next.titular_cubierto_empresa ? 0 : (parseFloat(next.costo_titular) || 0);
    const deps = next.dependientes_cubierto_empresa ? 0 : (parseInt(next.cantidad_dependientes) || 0) * (parseFloat(next.costo_dependiente) || 0);
    next.monto = titular + deps;
    setSeguroForm(next);
  };

  const handleSaveSeguro = async () => {
    const hayCostos = (parseFloat(seguroForm.costo_titular) || 0) > 0 || ((parseFloat(seguroForm.costo_dependiente) || 0) > 0 && (parseInt(seguroForm.cantidad_dependientes) || 0) > 0);
    if (!seguroForm.user_id || !seguroForm.tipo_seguro.trim() || (!seguroForm.monto && !hayCostos)) {
      toast.error("Complete empleado, tipo de beneficio y costos");
      return;
    }
    setSaving(true);
    try {
      if (seguroEditMode && editingSeguro) {
        await axios.put(`${API}/nomina/seguros-medicos/${editingSeguro.id}`, seguroForm, getAuthHeaders());
        toast.success("Beneficio actualizado");
      } else {
        await axios.post(`${API}/nomina/seguros-medicos`, seguroForm, getAuthHeaders());
        toast.success("Beneficio registrado");
      }
      setShowSeguroDialog(false);
      resetSeguroForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar el beneficio");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSeguro = async (id) => {
    if (!confirm("¿Eliminar este beneficio?")) return;
    try {
      await axios.delete(`${API}/nomina/seguros-medicos/${id}`, getAuthHeaders());
      toast.success("Registro eliminado");
      fetchData();
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  // ===== Dependientes Adicionales TSS =====
  const resetDependienteForm = () => {
    setDependienteForm({ user_id: "", nombre: "", parentesco: "padre", observaciones: "" });
    setDependienteEditMode(false);
    setEditingDependiente(null);
  };

  const openEditDependiente = (d) => {
    setDependienteForm({ user_id: d.user_id, nombre: d.nombre || "", parentesco: d.parentesco || "padre", observaciones: d.observaciones || "" });
    setEditingDependiente(d);
    setDependienteEditMode(true);
    setShowDependienteDialog(true);
  };

  const handleSaveDependiente = async () => {
    if (!dependienteForm.user_id || !dependienteForm.nombre.trim()) {
      toast.error("Complete el empleado y el nombre del dependiente");
      return;
    }
    setSaving(true);
    try {
      if (dependienteEditMode && editingDependiente) {
        await axios.put(`${API}/nomina/dependientes/${editingDependiente.id}`, dependienteForm, getAuthHeaders());
        toast.success("Dependiente actualizado");
      } else {
        await axios.post(`${API}/nomina/dependientes`, dependienteForm, getAuthHeaders());
        toast.success("Dependiente registrado");
      }
      setShowDependienteDialog(false);
      resetDependienteForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar el dependiente");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDependiente = async (id) => {
    if (!confirm("¿Eliminar este dependiente adicional?")) return;
    try {
      await axios.delete(`${API}/nomina/dependientes/${id}`, getAuthHeaders());
      toast.success("Dependiente eliminado");
      fetchData();
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  const handleSaveCapita = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/nomina/dependientes/config`, { capita_dependiente: parseFloat(capitaInput) || 0 }, getAuthHeaders());
      setCapitaDependiente(parseFloat(capitaInput) || 0);
      toast.success("Cápita por dependiente guardada");
    } catch (err) {
      toast.error("Error al guardar la cápita");
    } finally {
      setSaving(false);
    }
  };

  const handleExportBeneficiosExcel = async (tipo) => {
    try {
      const endpoint = tipo === "dependientes" ? "dependientes" : "seguros-medicos";
      const filename = tipo === "dependientes" ? "dependientes_tss.xlsx" : "otros_beneficios.xlsx";
      const res = await axios.get(`${API}/nomina/${endpoint}/export/excel`, { ...getAuthHeaders(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel exportado");
    } catch (err) {
      toast.error("Error al exportar el Excel");
    }
  };

  const openViewEmpleado = (emp) => {
    setViewingEmpleado(emp);
    setShowEmpleadoDetailDialog(true);
  };

  const handleDeleteEmpleado = async (userId) => {
    if (!confirm("¿Está seguro de eliminar este empleado de la nómina?")) return;
    try {
      await axios.delete(`${API}/nomina/empleados/${userId}`, getAuthHeaders());
      toast.success("Empleado eliminado de nómina");
      fetchData();
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  // Cuota del préstamo = (Monto Total + interés) / Plazo, redondeada a 2 decimales
  const calcCuotaPrestamo = (monto, plazo, tasa) => {
    const p = parseInt(plazo) || 1;
    return Math.round(((parseFloat(monto) || 0) * (1 + (parseFloat(tasa) || 0) / 100) / p) * 100) / 100;
  };

  // Create/Update loan
  const handleSavePrestamo = async () => {
    if (!prestamoForm.user_id || !prestamoForm.monto_total || !prestamoForm.cuota) {
      toast.error("Complete los campos requeridos (empleado, monto total y cuota)");
      return;
    }
    setSaving(true);
    try {
      if (prestamoEditMode && editingPrestamo) {
        await axios.put(`${API}/nomina/prestamos/${editingPrestamo.id}`, {
          ...prestamoForm,
          cuota_mensual: prestamoForm.tipo_cuota === "quincenal" ? prestamoForm.cuota * 2 : prestamoForm.cuota,
          plazo_meses: prestamoForm.tipo_cuota === "quincenal" ? Math.ceil(prestamoForm.plazo / 2) : prestamoForm.plazo
        }, getAuthHeaders());
        toast.success("Préstamo actualizado");
      } else {
        await axios.post(`${API}/nomina/prestamos`, {
          ...prestamoForm,
          cuota_mensual: prestamoForm.tipo_cuota === "quincenal" ? prestamoForm.cuota * 2 : prestamoForm.cuota,
          plazo_meses: prestamoForm.tipo_cuota === "quincenal" ? Math.ceil(prestamoForm.plazo / 2) : prestamoForm.plazo
        }, getAuthHeaders());
        toast.success("Préstamo registrado");
      }
      setShowPrestamoDialog(false);
      resetPrestamoForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar préstamo");
    } finally {
      setSaving(false);
    }
  };

  const resetPrestamoForm = () => {
    setPrestamoForm({
      user_id: "", monto_total: 0, cuota: 0, tipo_cuota: "mensual",
      plazo: 12, tasa_interes: 0, motivo: "", fecha_inicio: new Date().toISOString().split('T')[0]
    });
    setPrestamoEditMode(false);
    setEditingPrestamo(null);
  };

  const openEditPrestamo = (prestamo) => {
    setPrestamoForm({
      user_id: prestamo.user_id,
      monto_total: prestamo.monto_total || 0,
      cuota: prestamo.cuota_mensual || 0,
      tipo_cuota: prestamo.tipo_cuota || "mensual",
      plazo: prestamo.plazo_meses || 12,
      tasa_interes: prestamo.tasa_interes || 0,
      motivo: prestamo.motivo || "",
      fecha_inicio: prestamo.fecha_inicio || new Date().toISOString().split('T')[0]
    });
    setEditingPrestamo(prestamo);
    setPrestamoEditMode(true);
    setShowPrestamoDialog(true);
  };

  const openViewPrestamo = (prestamo) => {
    setViewingPrestamo(prestamo);
    setShowPrestamoDetailDialog(true);
  };

  const handleDeletePrestamo = async (prestamoId) => {
    if (!confirm("¿Está seguro de eliminar este préstamo?")) return;
    try {
      await axios.delete(`${API}/nomina/prestamos/${prestamoId}`, getAuthHeaders());
      toast.success("Préstamo eliminado");
      fetchData();
    } catch (err) {
      toast.error("Error al eliminar préstamo");
    }
  };

  // Create/Update advance
  const handleSaveAvance = async () => {
    if (!avanceForm.user_id || !avanceForm.monto) {
      toast.error("Complete los campos requeridos");
      return;
    }
    setSaving(true);
    try {
      if (avanceEditMode && editingAvance) {
        await axios.put(`${API}/nomina/avances/${editingAvance.id}`, {
          ...avanceForm,
          periodo_descuento: new Date().toISOString().slice(0, 7)
        }, getAuthHeaders());
        toast.success("Avance actualizado");
      } else {
        await axios.post(`${API}/nomina/avances`, {
          ...avanceForm,
          periodo_descuento: new Date().toISOString().slice(0, 7)
        }, getAuthHeaders());
        toast.success("Avance registrado");
      }
      setShowAvanceDialog(false);
      resetAvanceForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar avance");
    } finally {
      setSaving(false);
    }
  };

  const resetAvanceForm = () => {
    setAvanceForm({
      user_id: "", monto: 0, tipo_descuento: "mensual", cuotas: 1,
      motivo: "", fecha_inicio: new Date().toISOString().split('T')[0]
    });
    setAvanceEditMode(false);
    setEditingAvance(null);
  };

  const openEditAvance = (avance) => {
    setAvanceForm({
      user_id: avance.user_id,
      monto: avance.monto || 0,
      tipo_descuento: avance.tipo_descuento || "mensual",
      cuotas: avance.cuotas || 1,
      motivo: avance.motivo || "",
      fecha_inicio: avance.fecha_inicio || new Date().toISOString().split('T')[0]
    });
    setEditingAvance(avance);
    setAvanceEditMode(true);
    setShowAvanceDialog(true);
  };

  const openViewAvance = (avance) => {
    setViewingAvance(avance);
    setShowAvanceDetailDialog(true);
  };

  const handleDeleteAvance = async (avanceId) => {
    if (!confirm("¿Está seguro de eliminar este avance?")) return;
    try {
      await axios.delete(`${API}/nomina/avances/${avanceId}`, getAuthHeaders());
      toast.success("Avance eliminado");
      fetchData();
    } catch (err) {
      toast.error("Error al eliminar avance");
    }
  };

  // Calculate payroll
  const handleCalcularNomina = async () => {
    if (!calcularForm.periodo || !calcularForm.fecha_pago) {
      toast.error("Ingrese el período y fecha de pago");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/nomina/calcular`, calcularForm, getAuthHeaders());
      setNominaActual(res.data);
      toast.success("Nómina calculada");
      setShowCalcularDialog(false);
      setShowNominaDetailDialog(true);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al calcular nómina");
    } finally {
      setSaving(false);
    }
  };

  // View nomina detail
  const handleViewNomina = async (nominaId) => {
    try {
      const res = await axios.get(`${API}/nomina/${nominaId}`, getAuthHeaders());
      setNominaActual(res.data);
      setShowNominaDetailDialog(true);
    } catch (err) {
      toast.error("Error al cargar nómina");
    }
  };

  // Approve/Pay nomina
  const handleRecalcularNomina = async () => {
    if (!nominaActual) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/nomina/${nominaActual.id}/recalcular`, {}, getAuthHeaders());
      setNominaActual(res.data);
      toast.success("Nómina recalculada con los datos actuales");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al recalcular la nómina");
    } finally {
      setSaving(false);
    }
  };

  const handleAprobarNomina = async () => {
    if (!nominaActual) return;
    try {
      await axios.put(`${API}/nomina/${nominaActual.id}/aprobar`, {}, getAuthHeaders());
      toast.success("Nómina aprobada");
      fetchData();
      setNominaActual({ ...nominaActual, estado: "aprobada" });
    } catch (err) {
      toast.error("Error al aprobar");
    }
  };

  const handlePagarNomina = async () => {
    if (!nominaActual) return;
    try {
      await axios.put(`${API}/nomina/${nominaActual.id}/pagar`, {}, getAuthHeaders());
      toast.success("Nómina marcada como pagada");
      fetchData();
      setNominaActual({ ...nominaActual, estado: "pagada" });
    } catch (err) {
      toast.error("Error al marcar como pagada");
    }
  };

  // Create/Update nomina config
  const handleSaveNominaConfig = async () => {
    if (!nuevaNominaForm.nombre) {
      toast.error("Ingrese un nombre para la nómina");
      return;
    }
    setSaving(true);
    try {
      if (nominaConfigEditMode && editingNominaConfig) {
        await axios.put(`${API}/nomina/config/nominas/${editingNominaConfig.id}`, nuevaNominaForm, getAuthHeaders());
        toast.success("Nómina actualizada");
      } else {
        await axios.post(`${API}/nomina/config/nominas`, nuevaNominaForm, getAuthHeaders());
        toast.success("Nómina creada");
      }
      setShowNuevaNominaDialog(false);
      resetNominaConfigForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar nómina");
    } finally {
      setSaving(false);
    }
  };

  const resetNominaConfigForm = () => {
    setNuevaNominaForm({
      nombre: "", descripcion: "", tipo_periodo: "mensual", activa: true,
      es_default: false, banco_default: "", formato_txt_banco: "",
      cuenta_empresa: "", tipo_cuenta_empresa: "corriente", dia_pago: 15,
      dia_pago_1: 15, dia_pago_2: 30, dia_semana_pago: "viernes",
      incluir_tss: true, incluir_isr: true, plantilla_recibo_id: "",
      plantilla_resumen_id: "", notas: ""
    });
    setNominaConfigEditMode(false);
    setEditingNominaConfig(null);
  };

  const openEditNominaConfig = (nomina) => {
    setNuevaNominaForm({
      nombre: nomina.nombre || "",
      descripcion: nomina.descripcion || "",
      tipo_periodo: nomina.tipo_periodo || "mensual",
      activa: nomina.activa !== false,
      es_default: nomina.es_default || false,
      banco_default: nomina.banco_default || "",
      formato_txt_banco: nomina.formato_txt_banco || "",
      cuenta_empresa: nomina.cuenta_empresa || "",
      tipo_cuenta_empresa: nomina.tipo_cuenta_empresa || "corriente",
      dia_pago: nomina.dia_pago || 15,
      dia_pago_1: nomina.dia_pago_1 || 15,
      dia_pago_2: nomina.dia_pago_2 || 30,
      dia_semana_pago: nomina.dia_semana_pago || "viernes",
      incluir_tss: nomina.incluir_tss !== false,
      incluir_isr: nomina.incluir_isr !== false,
      plantilla_recibo_id: nomina.plantilla_recibo_id || "",
      plantilla_resumen_id: nomina.plantilla_resumen_id || "",
      notas: nomina.notas || ""
    });
    setEditingNominaConfig(nomina);
    setNominaConfigEditMode(true);
    setShowNuevaNominaDialog(true);
  };

  const openViewNominaConfig = (nomina) => {
    setViewingNominaConfig(nomina);
    setShowNominaConfigDetailDialog(true);
  };

  const handleDeleteNominaConfig = async (nominaId) => {
    if (!confirm("¿Está seguro de eliminar esta configuración de nómina?")) return;
    try {
      await axios.delete(`${API}/nomina/config/nominas/${nominaId}`, getAuthHeaders());
      toast.success("Configuración de nómina eliminada");
      fetchData();
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  // ==== Departamentos ====
  const handleSaveDept = async () => {
    if (!deptForm.name?.trim()) { toast.error("Ingrese el nombre del departamento"); return; }
    setSaving(true);
    try {
      if (editingDept) {
        await axios.put(`${API}/departamentos/${editingDept.id}`, deptForm, getAuthHeaders());
        toast.success("Departamento actualizado");
      } else {
        await axios.post(`${API}/departamentos`, deptForm, getAuthHeaders());
        toast.success("Departamento creado");
      }
      setShowDeptDialog(false);
      setEditingDept(null);
      setDeptForm({ name: "", description: "" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar departamento");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDept = async (deptId) => {
    if (!confirm("¿Eliminar este departamento?")) return;
    try {
      await axios.delete(`${API}/departamentos/${deptId}`, getAuthHeaders());
      toast.success("Departamento eliminado");
      fetchData();
    } catch (err) {
      toast.error("Error al eliminar departamento");
    }
  };

  // ==== Cargos ====
  const handleSaveCargo = async () => {
    if (!cargoForm.nombre?.trim()) { toast.error("Ingrese el nombre del cargo"); return; }
    setSaving(true);
    try {
      if (editingCargo) {
        await axios.put(`${API}/nomina/cargos/${editingCargo.id}`, cargoForm, getAuthHeaders());
        toast.success("Cargo actualizado");
      } else {
        await axios.post(`${API}/nomina/cargos`, cargoForm, getAuthHeaders());
        toast.success("Cargo creado");
      }
      setShowCargoDialog(false);
      setEditingCargo(null);
      setCargoForm({ nombre: "", descripcion: "" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar cargo");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCargo = async (cargoId) => {
    if (!confirm("¿Eliminar este cargo?")) return;
    try {
      await axios.delete(`${API}/nomina/cargos/${cargoId}`, getAuthHeaders());
      toast.success("Cargo eliminado");
      fetchData();
    } catch (err) {
      toast.error("Error al eliminar cargo");
    }
  };

  // ==== Cumpleaños del mes ====
  const handleDescargarCumples = async (e) => {
    if (e) e.stopPropagation();
    setDescargandoCumple(true);
    try {
      const res = await axios.get(`${API}/nomina-cumpleanos/descargar`, { ...getAuthHeaders(), params: { periodo: cumplePeriodo }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `cumpleanos_${cumplePeriodo}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Diseño descargado");
    } catch (err) {
      toast.error("Error al descargar el diseño");
    } finally {
      setDescargandoCumple(false);
    }
  };

  const handleRegenerarCumples = async (e) => {
    if (e) e.stopPropagation();
    setRegenerandoCumple(true);
    try {
      await axios.post(`${API}/nomina-cumpleanos/generar`, {}, { ...getAuthHeaders(), params: { periodo: cumplePeriodo } });
      await fetchCumpleMes(cumplePeriodo);
      toast.success("Nuevo diseño generado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al regenerar el diseño");
    } finally {
      setRegenerandoCumple(false);
    }
  };

  const cambiarMesCumple = (delta, e) => {
    if (e) e.stopPropagation();
    const [y, m] = cumplePeriodo.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setCumplePeriodo(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };

  // ==== Eliminar nómina (historial) ====
  const abrirEliminarNomina = (nomina) => {
    setNominaAEliminar(nomina);
    setEliminarConfirmForm({ email: "", password: "" });
    setShowEliminarNominaDialog(true);
  };

  const handleEliminarNomina = async () => {
    if (!nominaAEliminar) return;
    if (!esAdminRol && (!eliminarConfirmForm.email.trim() || !eliminarConfirmForm.password)) {
      toast.error("Ingrese el correo y la clave del usuario de Gestión Humana que confirma");
      return;
    }
    setEliminandoNomina(true);
    try {
      const payload = esAdminRol ? {} : {
        confirmacion_email: eliminarConfirmForm.email.trim(),
        confirmacion_password: eliminarConfirmForm.password
      };
      const res = await axios.post(`${API}/nomina/${nominaAEliminar.id}/eliminar`, payload, getAuthHeaders());
      toast.success(res.data?.message || "Nómina eliminada");
      setShowEliminarNominaDialog(false);
      setNominaAEliminar(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar la nómina");
    } finally {
      setEliminandoNomina(false);
    }
  };

  // Export bank TXT
  const handleExportBancoTxt = async () => {
    if (!bancoTxtForm.formato || !bancoTxtForm.nomina_id) {
      toast.error("Seleccione el banco y la nómina");
      return;
    }
    try {
      const res = await axios.get(
        `${API}/nomina/export/banco-txt?formato=${bancoTxtForm.formato}&nomina_id=${bancoTxtForm.nomina_id}`,
        { ...getAuthHeaders(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `nomina_${bancoTxtForm.formato}_${new Date().toISOString().split('T')[0]}.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Archivo TXT generado");
      setShowBancoTxtDialog(false);
    } catch (err) {
      toast.error("Error al generar archivo");
    }
  };

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(value || 0);
  };

  // Stats
  const totalNomina = empleadosNomina.reduce((acc, e) => acc + (e.salario_bruto || 0), 0);
  const prestamosActivos = prestamos.filter(p => p.estado === "activo");
  const totalPrestamos = prestamosActivos.reduce((acc, p) => acc + (p.saldo_pendiente || 0), 0);
  const avancesPendientes = avances.filter(a => a.estado === "pendiente");

  const getEstadoBadge = (estado) => {
    const styles = {
      borrador: "bg-slate-100 text-slate-600 border-slate-200",
      aprobada: "bg-blue-100 text-[#0C314C] border-blue-200",
      pagada: "bg-[#0C314C]/10 text-[#0C314C] border-[#0C314C]/20",
      eliminada: "bg-red-100 text-red-700 border-red-200",
      activo: "bg-[#45E69A]/20 text-[#3F6212] border-[#45E69A]/40",
      pagado: "bg-[#0C314C]/10 text-[#0C314C] border-[#0C314C]/20",
      pendiente: "bg-amber-100 text-amber-800 border-amber-200",
      descontado: "bg-[#45E69A]/20 text-[#3F6212] border-[#45E69A]/40"
    };
    return <Badge className={`${styles[estado] || styles.borrador} border rounded-full px-3 py-0.5 text-xs font-semibold capitalize`}>{estado}</Badge>;
  };

  // Trazabilidad de nómina: combina eventos guardados + campos legacy
  const nombreUsuarioNomina = (idOrName) => {
    if (!idOrName) return "-";
    const u = usuarios.find(x => x.id === idOrName);
    return u ? u.name : idOrName;
  };

  const construirTrazabilidad = (n) => {
    if (!n) return [];
    const evs = [...(n.trazabilidad || [])];
    const has = (a) => evs.some(e => e.accion === a);
    if (!has("creada") && n.created_at) evs.push({ accion: "creada", usuario: n.created_by_nombre || n.created_by, fecha: n.created_at, detalle: "Nómina calculada" });
    if (!has("recalculada") && n.recalculada_at) evs.push({ accion: "recalculada", usuario: n.recalculada_por, fecha: n.recalculada_at, detalle: "Nómina recalculada" });
    if (!has("aprobada") && n.aprobado_at) evs.push({ accion: "aprobada", usuario: n.aprobado_por_nombre || n.aprobado_por, fecha: n.aprobado_at, detalle: "Nómina aprobada" });
    if (!has("pagada") && n.pagado_at) evs.push({ accion: "pagada", usuario: n.pagado_por_nombre || n.pagado_por, fecha: n.pagado_at, detalle: "Nómina marcada como pagada" });
    if (!has("eliminada") && n.eliminada_at) evs.push({ accion: "eliminada", usuario: n.eliminada_por, fecha: n.eliminada_at, detalle: n.confirmada_por ? `Eliminada · Confirmada por ${n.confirmada_por}` : "Eliminada" });
    return evs.sort((a, b) => (a.fecha || "").localeCompare(b.fecha || ""));
  };

  const formatFechaHora = (iso) => {
    if (!iso) return "-";
    try {
      return new Date(iso).toLocaleString("es-DO", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  // Desglose de beneficios por tipo en el detalle de nómina
  const tiposBeneficioNomina = (n) => [...new Set((n?.items || []).flatMap(i => (i.beneficios_detalle || []).map(b => b.tipo).filter(Boolean)))];
  const montoBeneficioTipo = (item, tipo) => (item.beneficios_detalle || []).filter(b => b.tipo === tipo).reduce((s, b) => s + (b.monto || 0), 0);

  const handleImprimirNomina = () => {
    if (!nominaActual) return;
    const tipos = tiposBeneficioNomina(nominaActual);
    const fmt = v => `RD$ ${(v || 0).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const filas = (nominaActual.items || []).map(it => `<tr>
      <td>${it.nombre || ""}</td>
      <td class="r">${fmt(it.total_ingresos)}</td>
      <td class="r">${fmt(it.afp_empleado)}</td>
      <td class="r">${fmt(it.sfs_empleado)}</td>
      <td class="r">${fmt(it.isr)}</td>
      <td class="r">${it.cuota_prestamo > 0 ? fmt(it.cuota_prestamo) : "-"}</td>
      <td class="r">${(it.avance_salario || 0) > 0 ? fmt(it.avance_salario) : "-"}</td>
      ${tipos.map(t => { const m = montoBeneficioTipo(it, t); return `<td class="r">${m > 0 ? fmt(m) : "-"}</td>`; }).join("")}
      <td class="r">${(it.descuento_dependientes_tss || 0) > 0 ? fmt(it.descuento_dependientes_tss) : "-"}</td>
      <td class="r">${(it.otras_deducciones || 0) > 0 ? fmt(it.otras_deducciones) : "-"}</td>
      <td class="r">${fmt(it.total_deducciones)}</td>
      <td class="r b">${fmt(it.salario_neto)}</td>
    </tr>`).join("");
    const win = window.open("", "_blank");
    if (!win) { toast.error("Permita ventanas emergentes para imprimir"); return; }
    win.document.write(`<html><head><title>Nómina ${nominaActual.periodo}</title><style>
      body{font-family:Arial,sans-serif;margin:24px;color:#0C314C}
      h1{font-size:18px;margin:0}
      p.meta{font-size:11px;color:#555;margin:4px 0 14px}
      table{width:100%;border-collapse:collapse;font-size:9px}
      th,td{border:1px solid #cbd5e1;padding:4px 5px;text-align:left;white-space:nowrap}
      th{background:#0C314C;color:#fff}
      .r{text-align:right} .b{font-weight:bold}
      tfoot td{font-weight:bold;background:#f1f5f9}
      @page{size:landscape;margin:8mm}
    </style></head><body>
    <h1>Nómina ${nominaActual.periodo}${nominaActual.quincena ? ` · ${nominaActual.quincena === "primera" ? "1ra" : "2da"} quincena` : ""}</h1>
    <p class="meta">Tipo: ${nominaActual.tipo_periodo || "mensual"} · Estado: ${nominaActual.estado} · Fecha de pago: ${nominaActual.fecha_pago || "-"} · Empleados: ${(nominaActual.items || []).length} · Impreso: ${new Date().toLocaleString("es-DO")}</p>
    <table>
      <thead><tr><th>Empleado</th><th>Bruto</th><th>AFP</th><th>SFS</th><th>ISR</th><th>Préstamo</th><th>Avance</th>${tipos.map(t => `<th>${t}</th>`).join("")}<th>Dep. TSS</th><th>Otros</th><th>Total Deducc.</th><th>Neto</th></tr></thead>
      <tbody>${filas}</tbody>
      <tfoot><tr><td>TOTALES</td><td class="r">${fmt(nominaActual.totales?.total_bruto)}</td><td colspan="${8 + tipos.length}" class="r">Total deducciones: ${fmt(nominaActual.totales?.total_deducciones)}</td><td class="r">${fmt(nominaActual.totales?.total_neto)}</td></tr></tfoot>
    </table>
    <script>window.onload = function(){ window.print(); }</script>
    </body></html>`);
    win.document.close();
  };

  // Fetch document types
  const fetchTiposDocumento = async () => {
    try {
      const response = await axios.get(`${API}/nomina/documento/tipos`, getAuthHeaders());
      setTiposDocumento(response.data);
    } catch (err) {
      console.error("Error fetching document types:", err);
    }
  };

  // Generate payroll document with AI
  const handleGenerarDocumento = async () => {
    if (!nominaActual?.id) {
      toast.error("No hay nómina seleccionada");
      return;
    }

    try {
      setGenerandoDocumento(true);
      setDocumentoGenerado(null);
      
      const response = await axios.post(`${API}/nomina/documento/generar`, {
        nomina_id: nominaActual.id,
        tipo_documento: documentoTipo,
        empleado_id: documentoEmpleadoId,
        formato: "html"
      }, getAuthHeaders());
      
      if (response.data.success) {
        setDocumentoGenerado(response.data.documento);
        toast.success("Documento generado exitosamente");
      } else {
        toast.error("Error al generar documento");
      }
    } catch (err) {
      console.error("Error generating document:", err);
      toast.error(err.response?.data?.detail || "Error al generar documento");
    } finally {
      setGenerandoDocumento(false);
    }
  };

  // Print/Download generated document
  const handlePrintDocumento = () => {
    if (!documentoGenerado) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Documento de Nómina</title>
          <style>
            @media print {
              body { margin: 0; padding: 20px; }
            }
          </style>
        </head>
        <body>
          ${documentoGenerado}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleDownloadDocumento = () => {
    if (!documentoGenerado) return;
    
    const blob = new Blob([`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>Documento de Nómina - ${nominaActual?.periodo}</title>
        </head>
        <body>
          ${documentoGenerado}
        </body>
      </html>
    `], { type: 'text/html' });
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `nomina_${documentoTipo}_${nominaActual?.periodo?.replace(/\//g, '-')}.html`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Documento descargado");
  };

  // Open document generator dialog
  const openGenerarDocumentoDialog = (nomina, empleadoId = null) => {
    setNominaActual(nomina);
    setDocumentoEmpleadoId(empleadoId);
    setDocumentoGenerado(null);
    setDocumentoTipo("comprobante");
    fetchTiposDocumento();
    setShowGenerarDocumentoDialog(true);
  };

  // Filter empleados (buscador + tarjeta activa + banco)
  const filteredEmpleados = empleadosNomina.filter(emp => {
    const q = searchQuery.toLowerCase();
    const matchQ = !q ||
      emp.nombre?.toLowerCase().includes(q) ||
      emp.email?.toLowerCase().includes(q) ||
      emp.banco?.toLowerCase().includes(q);
    const matchCard = !empFilter ||
      (empFilter === "fijo" ? emp.tipo_contrato === "fijo" :
       empFilter === "con_prestamo" ? emp.tiene_prestamo :
       empFilter.startsWith("cumpleanos") ? (() => {
         const mesFiltro = parseInt(empFilter.split(":")[1]) || new Date().getMonth() + 1;
         return emp.fecha_nacimiento && parseInt(emp.fecha_nacimiento.slice(5, 7)) === mesFiltro;
       })() : true);
    const matchBanco = empBancoFilter === "todos" || (emp.banco || "") === empBancoFilter;
    return matchQ && matchCard && matchBanco;
  });
  const bancosEnUso = [...new Set(empleadosNomina.map(e => e.banco).filter(Boolean))];

  // Filter préstamos
  const filteredPrestamos = prestamos.filter(p => {
    const q = searchPrestamos.toLowerCase();
    const matchQ = !q || p.nombre_empleado?.toLowerCase().includes(q) || p.motivo?.toLowerCase().includes(q);
    const matchF = !prestamosFilter || (prestamosFilter === "activo" ? p.estado === "activo" : p.estado !== "activo");
    return matchQ && matchF;
  });

  // Filter avances
  const filteredAvances = avances.filter(a => {
    const q = searchAvances.toLowerCase();
    const matchQ = !q || a.nombre_empleado?.toLowerCase().includes(q) || a.motivo?.toLowerCase().includes(q);
    const matchF = !avancesFilter || a.estado === avancesFilter;
    return matchQ && matchF;
  });

  // Filter historial
  const filteredHistorial = nominasHistorial.filter(n => {
    const q = searchHistorial.toLowerCase();
    const matchQ = !q || n.periodo?.toLowerCase().includes(q) || n.tipo_periodo?.toLowerCase().includes(q);
    const matchF = !historialFilter || n.estado === historialFilter;
    return matchQ && matchF;
  });
  const totalNetoPagado = nominasHistorial.filter(n => n.estado === "pagada").reduce((acc, n) => acc + (n.totales?.total_neto || 0), 0);

  // Filter seguros médicos
  const filteredSeguros = segurosMedicos.filter(s => {
    const q = searchSeguros.toLowerCase();
    return !q || s.nombre_empleado?.toLowerCase().includes(q) || s.tipo_seguro?.toLowerCase().includes(q);
  });

  // Filter dependientes
  const filteredDependientes = dependientes.filter(d => {
    const q = searchDependientes.toLowerCase();
    return !q || d.nombre?.toLowerCase().includes(q) || d.nombre_empleado?.toLowerCase().includes(q) || d.parentesco?.toLowerCase().includes(q);
  });
  const dependientesDeEmpleado = (userId) => dependientes.filter(d => d.user_id === userId);
  const segurosDeEmpleado = (userId) => segurosMedicos.filter(s => s.user_id === userId);
  const descuentoEmpleadoSeguro = (s) => s.aplica_descuento ? (s.monto || 0) * ((s.pct_empleado || 0) / 100) : 0;

  // Montos a pagar por tipo de beneficio (Dashboard) — cada descuento se aplica completo en su frecuencia (sin prorrateo).
  // Se anulan si la nómina del período actual ya está cerrada (pagada)
  const periodoActual = new Date().toISOString().slice(0, 7);
  const nominaPeriodoCerrada = nominasHistorial.some(n => (n.periodo || "").startsWith(periodoActual) && n.estado === "pagada");
  const tiposBeneficioCards = [...new Set([...TIPOS_BENEFICIO, ...segurosMedicos.map(s => s.tipo_seguro).filter(Boolean)])].map(tipo => {
    const items = segurosMedicos.filter(s => s.tipo_seguro === tipo && s.activo !== false);
    const monto = nominaPeriodoCerrada ? 0 : items.reduce((acc, s) => acc + (s.monto || 0), 0);
    return { tipo, count: items.length, monto };
  });

  // Bancos disponibles en RD
  const bancosRD = [
    { id: "banco_popular", nombre: "Banco Popular Dominicano" },
    { id: "banreservas", nombre: "Banco de Reservas" },
    { id: "bhd_leon", nombre: "BHD León" },
    { id: "scotiabank", nombre: "Scotiabank" },
    { id: "banco_santa_cruz", nombre: "Banco Santa Cruz" },
    { id: "banco_caribe", nombre: "Banco Caribe" },
    { id: "banco_lopez_haro", nombre: "Banco López de Haro" },
    { id: "asociacion_popular", nombre: "Asociación Popular" },
    { id: "banco_promerica", nombre: "Banco Promerica" },
    { id: "banco_vimenca", nombre: "Banco Vimenca" }
  ];

  // ========== PLANTILLA IA FUNCTIONS ==========
  
  const fetchCamposDisponibles = async () => {
    try {
      const res = await axios.get(`${API}/nomina/plantillas/campos-disponibles`, getAuthHeaders());
      setCamposDisponibles(res.data.campos || []);
    } catch (err) {
      console.error("Error fetching campos:", err);
    }
  };

  const handlePlantillaIAAnalyze = async () => {
    if (!plantillaIAFile) {
      toast.error("Seleccione un archivo primero");
      return;
    }
    setPlantillaIAAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append('file', plantillaIAFile);
      
      const res = await axios.post(`${API}/nomina/plantillas/analyze`, formData, {
        ...getAuthHeaders(),
        headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' }
      });
      
      if (res.data.success) {
        setPlantillaIAAnalysis(res.data.analysis);
        // Pre-populate mappings from AI analysis
        const mappings = (res.data.analysis.campos_detectados || []).map(c => ({
          texto_original: c.texto_original,
          campo_sistema: c.campo_sugerido,
          confianza: c.confianza
        }));
        setMapeosCampos(mappings);
        setPlantillaIAStep(2);
        toast.success("Análisis completado");
      } else {
        toast.error(res.data.error || "Error al analizar");
      }
    } catch (err) {
      toast.error("Error al analizar plantilla");
    } finally {
      setPlantillaIAAnalyzing(false);
    }
  };

  const handleSavePlantillaIA = async (nombre, tipo) => {
    if (!plantillaIAFile) return;
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('file', plantillaIAFile);
      formData.append('nombre', nombre || plantillaIAFile.name);
      formData.append('tipo', tipo || 'recibo');
      formData.append('mapeo_campos', JSON.stringify(mapeosCampos));
      
      await axios.post(`${API}/nomina/plantillas/upload-with-mapping`, formData, {
        ...getAuthHeaders(),
        headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success("Plantilla guardada exitosamente");
      setShowPlantillaIADialog(false);
      resetPlantillaIA();
      fetchData();
    } catch (err) {
      toast.error("Error al guardar plantilla");
    } finally {
      setSaving(false);
    }
  };

  const resetPlantillaIA = () => {
    setPlantillaIAStep(1);
    setPlantillaIAFile(null);
    setPlantillaIAAnalysis(null);
    setMapeosCampos([]);
    setPlantillaIAPreview(null);
  };

  // ========== BANCO TXT IA FUNCTIONS ==========
  
  const handleBancoTxtAnalyze = async () => {
    if (!bancoTxtFile) {
      toast.error("Seleccione un archivo TXT");
      return;
    }
    setBancoTxtAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append('file', bancoTxtFile);
      
      const res = await axios.post(`${API}/nomina/banco-txt/analyze`, formData, {
        ...getAuthHeaders(),
        headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' }
      });
      
      if (res.data.success) {
        setBancoTxtAnalysis(res.data);
        toast.success("Análisis de formato completado");
      } else {
        toast.error(res.data.error || "Error al analizar");
      }
    } catch (err) {
      toast.error("Error al analizar formato TXT");
    } finally {
      setBancoTxtAnalyzing(false);
    }
  };

  const handleSaveBancoTxtFormat = async (nombre, banco) => {
    if (!bancoTxtAnalysis?.analysis) return;
    setSaving(true);
    try {
      await axios.post(`${API}/nomina/banco-txt/save-format`, {
        nombre,
        banco,
        ...bancoTxtAnalysis.analysis
      }, getAuthHeaders());
      
      toast.success("Formato guardado");
      setShowBancoTxtIADialog(false);
      setBancoTxtFile(null);
      setBancoTxtAnalysis(null);
    } catch (err) {
      toast.error("Error al guardar formato");
    } finally {
      setSaving(false);
    }
  };

  const updateMapeo = (index, campo) => {
    const newMapeos = [...mapeosCampos];
    newMapeos[index] = { ...newMapeos[index], campo_sistema: campo };
    setMapeosCampos(newMapeos);
  };

  const addMapeoManual = () => {
    setMapeosCampos([...mapeosCampos, { texto_original: "", campo_sistema: "", confianza: "manual" }]);
  };

  const removeMapeo = (index) => {
    setMapeosCampos(mapeosCampos.filter((_, i) => i !== index));
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="nomina-page">
        {/* Header */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0C314C] via-[#24466f] to-slate-800 p-6 md:p-8 mb-8 shadow-lg" data-testid="nomina-header">
          <span className="absolute -right-10 -top-16 w-64 h-64 rounded-full bg-[#45E69A]/10" />
          <span className="absolute right-24 -bottom-20 w-48 h-48 rounded-full bg-white/5" />
          <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
            <div className="flex items-center gap-4">
              <div className="hidden sm:flex w-14 h-14 rounded-2xl bg-[#45E69A]/15 border border-[#45E69A]/30 items-center justify-center shrink-0">
                <Wallet className="w-7 h-7 text-[#45E69A]" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white font-['Manrope'] tracking-tight">Nómina</h1>
                <p className="text-slate-300 mt-1 text-sm">Gestión de nómina · República Dominicana</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2.5">
              <Button variant="outline" className="bg-white/5 border-white/20 text-white hover:bg-white/15 hover:text-white" onClick={() => setShowConfigNominaDialog(true)} data-testid="config-general-btn">
                <Settings className="w-4 h-4 mr-2" /> Configuración
              </Button>
              <Button variant="outline" className="bg-white/5 border-white/20 text-white hover:bg-white/15 hover:text-white" onClick={() => setShowBancoTxtDialog(true)} data-testid="banco-txt-btn">
                <Download className="w-4 h-4 mr-2" /> TXT Banco
              </Button>
              <Button variant="outline" className="bg-white/5 border-white/20 text-white hover:bg-white/15 hover:text-white" onClick={() => setShowPlantillaDialog(true)} data-testid="plantillas-btn">
                <Printer className="w-4 h-4 mr-2" /> Plantillas
              </Button>
              <Button className="bg-[#45E69A] hover:bg-[#b6e344] text-[#0C314C] font-semibold shadow-md" onClick={() => setShowCalcularDialog(true)} data-testid="calcular-btn">
                <Calculator className="w-4 h-4 mr-2" /> Calcular Nómina
              </Button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-10 h-10 animate-spin text-[#0C314C]" />
          </div>
        ) : (
          <>
            {/* Stats globales — tarjetas dinámicas que navegan y filtran */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
              <KpiCard
                label="Empleados en Nómina"
                value={empleadosNomina.length}
                sub="Click para ver empleados"
                icon={Users}
                testId="kpi-card-total-empleados"
                onClick={() => { setEmpFilter(null); setActiveTab("empleados"); }}
              />
              <KpiCard
                label="Masa Salarial Mensual"
                value={formatCurrency(totalNomina)}
                small
                sub="Total salarios brutos"
                icon={DollarSign}
                testId="kpi-card-masa-salarial"
                onClick={() => { setEmpFilter(null); setActiveTab("empleados"); }}
              />
              <KpiCard
                label="Préstamos Activos"
                value={prestamosActivos.length}
                sub={`${formatCurrency(totalPrestamos)} pendiente`}
                icon={PiggyBank}
                testId="kpi-card-prestamos-activos"
                onClick={() => { setPrestamosFilter("activo"); setActiveTab("prestamos"); }}
              />
              <KpiCard
                label="Avances Pendientes"
                value={avancesPendientes.length}
                sub="Por descontar en nómina"
                icon={Banknote}
                testId="kpi-card-avances-pendientes"
                onClick={() => { setAvancesFilter("pendiente"); setActiveTab("avances"); }}
              />
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-6 h-auto inline-flex bg-slate-100 dark:bg-slate-800 p-1.5 rounded-xl w-full sm:w-auto overflow-x-auto justify-start">
                {[
                  { v: "dashboard", icon: DollarSign, label: "Dashboard" },
                  { v: "empleados", icon: Users, label: "Empleados" },
                  { v: "prestamos", icon: PiggyBank, label: "Préstamos" },
                  { v: "avances", icon: Banknote, label: "Avances" },
                  { v: "seguros", icon: HeartPulse, label: "Otros Beneficios" },
                  { v: "dependientes", icon: UsersRound, label: "Dependientes TSS" },
                  { v: "departamentos", icon: Building2, label: "Departamentos" },
                  { v: "cargos", icon: Briefcase, label: "Cargos" },
                  { v: "historial", icon: History, label: "Historial" },
                  { v: "nominas", icon: FileSpreadsheet, label: "Mis Nóminas" },
                  { v: "plantillas_cumple", icon: ImagePlus, label: "Plantilla de Cumpleaños" },
                ].map(t => (
                  <TabsTrigger
                    key={t.v}
                    value={t.v}
                    data-testid={`tab-trigger-${t.v}`}
                    className="gap-2 px-5 py-2.5 text-sm font-medium rounded-lg whitespace-nowrap transition-colors duration-200 data-[state=active]:bg-[#0C314C] data-[state=active]:text-white data-[state=active]:shadow-sm data-[state=active]:ring-1 data-[state=active]:ring-[#45E69A]/50 hover:text-[#0C314C]"
                  >
                    <t.icon className="w-4 h-4" /> {t.label}
                  </TabsTrigger>
                ))}
              </TabsList>

              {/* Dashboard Tab */}
              <TabsContent value="dashboard" className="space-y-6">
                {/* Cumpleaños del mes */}
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => { setEmpFilter(`cumpleanos:${cumpleMes.mes || new Date().getMonth() + 1}`); setActiveTab("empleados"); }}
                  onKeyDown={e => { if (e.key === "Enter") { setEmpFilter(`cumpleanos:${cumpleMes.mes || new Date().getMonth() + 1}`); setActiveTab("empleados"); } }}
                  data-testid="card-cumpleanos-mes"
                  className="cursor-pointer rounded-2xl p-5 border border-slate-200 dark:border-slate-700 bg-gradient-to-r from-[#0C314C] to-slate-800 text-white shadow-sm hover:shadow-md hover:border-[#45E69A] transition-shadow transition-colors duration-200 relative overflow-hidden group"
                >
                  <span className="absolute -right-6 -top-6 w-32 h-32 rounded-full bg-[#45E69A]/10 group-hover:bg-[#45E69A]/20 transition-colors duration-200" />
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 relative">
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="rounded-xl p-3 bg-[#45E69A]/20 shrink-0">
                        <Cake className="w-7 h-7 text-[#45E69A]" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-300">Cumpleaños del Mes</p>
                          <div className="flex items-center gap-1 bg-white/10 rounded-full px-1 py-0.5">
                            <button
                              type="button"
                              onClick={e => cambiarMesCumple(-1, e)}
                              className="rounded-full p-0.5 hover:bg-white/20 transition-colors duration-150"
                              title="Mes anterior"
                              data-testid="cumpleanos-mes-anterior-btn"
                            >
                              <ChevronLeft className="w-4 h-4 text-[#45E69A]" />
                            </button>
                            <span className="text-xs font-semibold px-1 whitespace-nowrap" data-testid="cumpleanos-periodo-label">
                              {cumpleMes.mes_nombre || ""} {cumpleMes.anio || ""}
                            </span>
                            <button
                              type="button"
                              onClick={e => cambiarMesCumple(1, e)}
                              className="rounded-full p-0.5 hover:bg-white/20 transition-colors duration-150"
                              title="Mes siguiente"
                              data-testid="cumpleanos-mes-siguiente-btn"
                            >
                              <ChevronRight className="w-4 h-4 text-[#45E69A]" />
                            </button>
                          </div>
                        </div>
                        <p className="text-2xl font-bold mt-0.5" data-testid="cumpleanos-cantidad">
                          {cumpleMes.cantidad} {cumpleMes.cantidad === 1 ? "empleado cumple años" : "empleados cumplen años"}
                        </p>
                        {cumpleMes.cumpleaneros?.length > 0 && (
                          <p className="text-xs text-[#45E69A] mt-1 truncate">
                            {cumpleMes.cumpleaneros.slice(0, 3).map(c => `${c.nombre} (${c.dia})`).join(" · ")}
                            {cumpleMes.cumpleaneros.length > 3 ? ` · +${cumpleMes.cumpleaneros.length - 3} más` : ""}
                          </p>
                        )}
                        <p className="text-[11px] text-slate-400 mt-1">Click para filtrar los cumpleañeros de {cumpleMes.mes_nombre || "este mes"}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {esAdminRol && (
                        <Button
                          onClick={abrirEnvioCumpleDialog}
                          variant="outline"
                          size="icon"
                          className="border-white/30 bg-transparent text-white hover:bg-white/15 hover:text-white shrink-0"
                          title="Configurar envío automático por correo"
                          data-testid="config-envio-cumpleanos-btn"
                        >
                          <Settings className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        onClick={handleRegenerarCumples}
                        disabled={regenerandoCumple}
                        variant="outline"
                        className="border-[#45E69A]/50 bg-transparent text-[#45E69A] hover:bg-[#45E69A]/10 hover:text-[#45E69A] font-semibold"
                        title="Generar un nuevo diseño aleatorio"
                        data-testid="regenerar-cumpleanos-btn"
                      >
                        {regenerandoCumple ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                        Regenerar diseño
                      </Button>
                      <Button
                        onClick={handleDescargarCumples}
                        disabled={descargandoCumple}
                        className="bg-[#45E69A] hover:bg-[#8fb92c] text-[#0C314C] font-semibold"
                        data-testid="descargar-cumpleanos-btn"
                      >
                        {descargandoCumple ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                        Descargar diseño
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Montos a pagar por beneficio */}
                <div data-testid="dashboard-beneficios-cards">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-bold text-[#0C314C] dark:text-white font-['Manrope'] flex items-center gap-2">
                      <HeartPulse className="w-5 h-5 text-[#45E69A]" /> Montos a Pagar por Beneficio
                    </h3>
                    {nominaPeriodoCerrada && (
                      <Badge className="bg-[#0C314C]/10 text-[#0C314C] dark:text-slate-200 border border-[#0C314C]/20 rounded-full font-semibold" data-testid="badge-nomina-cerrada">
                        Nómina {periodoActual} cerrada — montos no contemplados
                      </Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {tiposBeneficioCards.map(t => (
                      <KpiCard
                        key={t.tipo}
                        label={t.tipo}
                        value={formatCurrency(t.monto)}
                        small
                        sub={nominaPeriodoCerrada ? `Nómina ${periodoActual} cerrada` : `${t.count} beneficio(s) · Click para filtrar`}
                        icon={HeartPulse}
                        testId={`kpi-beneficio-${t.tipo.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => { setSearchSeguros(t.tipo); setActiveTab("seguros"); }}
                      />
                    ))}
                  </div>
                </div>

                {/* Alertas Section */}
                {alertas.total > 0 && (
                  <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-amber-200 dark:border-amber-800 overflow-hidden" data-testid="dashboard-alertas">
                    <div className="px-6 py-4 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-amber-600" />
                      <h3 className="font-bold text-amber-800 dark:text-amber-300 font-['Manrope']">Alertas Activas ({alertas.total})</h3>
                    </div>
                    <div className="p-4 space-y-2.5">
                      {alertas.alertas.slice(0, 5).map((alerta, idx) => (
                        <div key={idx} className={`p-3.5 rounded-xl flex items-start gap-3 ${
                          alerta.prioridad === 'alta' ? 'bg-red-50 dark:bg-red-900/30' :
                          alerta.prioridad === 'media' ? 'bg-amber-50 dark:bg-amber-900/30' :
                          'bg-slate-50 dark:bg-slate-700/50'
                        }`}>
                          <div className={`w-2 h-2 rounded-full mt-2 shrink-0 ${
                            alerta.prioridad === 'alta' ? 'bg-red-500' :
                            alerta.prioridad === 'media' ? 'bg-amber-500' :
                            'bg-slate-400'
                          }`} />
                          <div className="flex-1 min-w-0">
                            <p className={`font-semibold text-sm ${
                              alerta.prioridad === 'alta' ? 'text-red-700 dark:text-red-400' :
                              alerta.prioridad === 'media' ? 'text-amber-700 dark:text-amber-400' :
                              'text-slate-700 dark:text-slate-300'
                            }`}>{alerta.titulo}</p>
                            <p className="text-xs text-slate-600 dark:text-slate-400">{alerta.mensaje}</p>
                          </div>
                          <Badge className={`text-xs border rounded-full capitalize ${
                            alerta.prioridad === 'alta' ? 'bg-red-100 text-red-700 border-red-200' :
                            alerta.prioridad === 'media' ? 'bg-amber-100 text-amber-700 border-amber-200' :
                            'bg-slate-100 text-slate-600 border-slate-200'
                          }`}>{alerta.prioridad}</Badge>
                        </div>
                      ))}
                      {alertas.total > 5 && (
                        <p className="text-center text-sm text-slate-500">Y {alertas.total - 5} alertas más...</p>
                      )}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                  {/* Últimas Nóminas */}
                  <div className="lg:col-span-3 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden" data-testid="dashboard-ultimas-nominas">
                    <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                      <h3 className="font-bold text-[#0C314C] dark:text-white font-['Manrope'] flex items-center gap-2">
                        <Receipt className="w-5 h-5 text-[#45E69A]" /> Últimas Nóminas
                      </h3>
                      <Button variant="ghost" size="sm" className="text-[#0C314C] dark:text-slate-300 hover:text-[#0C314C]" onClick={() => setActiveTab("historial")} data-testid="ver-historial-btn">
                        Ver historial <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                    {nominasHistorial.length === 0 ? (
                      <div className="text-center py-14 text-slate-400">
                        <Receipt className="w-12 h-12 mx-auto mb-3 opacity-40" />
                        <p className="text-sm">No hay nóminas procesadas</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-700">
                            <tr>
                              <th className={TH_CLS}>Período</th>
                              <th className={TH_CLS}>Neto</th>
                              <th className={TH_CLS}>Estado</th>
                              <th className={`${TH_CLS} text-center`}>Ver</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                            {nominasHistorial.slice(0, 6).map(n => (
                              <tr key={n.id} className="hover:bg-slate-50/80 dark:hover:bg-slate-700/50 transition-colors duration-150">
                                <td className={`${TD_CLS} font-semibold text-[#0C314C] dark:text-white`}>{n.periodo}</td>
                                <td className={`${TD_CLS} font-mono font-medium`}>{formatCurrency(n.totales?.total_neto || 0)}</td>
                                <td className={TD_CLS}>{getEstadoBadge(n.estado)}</td>
                                <td className={`${TD_CLS} text-center`}>
                                  <Button variant="ghost" size="sm" className="hover:bg-[#0C314C]/5" onClick={() => handleViewNomina(n.id)} data-testid={`dashboard-ver-nomina-${n.id}`}>
                                    <Eye className="w-4 h-4 text-[#0C314C] dark:text-slate-300" />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* TSS Summary */}
                  <div className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden" data-testid="dashboard-parametros-tss">
                    <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
                      <h3 className="font-bold text-[#0C314C] dark:text-white font-['Manrope'] flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-[#45E69A]" /> Parámetros TSS Actuales
                      </h3>
                    </div>
                    <div className="p-6 space-y-1">
                      {[
                        { label: "AFP Empleado", value: `${parametros?.tss_empleado_afp}%` },
                        { label: "SFS Empleado", value: `${parametros?.tss_empleado_sfs}%` },
                        { label: "AFP Empleador", value: `${parametros?.tss_empleador_afp}%` },
                        { label: "SFS Empleador", value: `${parametros?.tss_empleador_sfs}%` },
                        { label: "Tope Cotizable", value: formatCurrency(parametros?.tope_cotizable_afp) },
                      ].map((row, i, arr) => (
                        <div key={row.label} className={`flex justify-between items-center py-3 ${i < arr.length - 1 ? "border-b border-slate-100 dark:border-slate-700" : ""}`}>
                          <span className="text-sm text-slate-600 dark:text-slate-400">{row.label}</span>
                          <span className="font-mono font-semibold text-[#0C314C] dark:text-white text-sm">{row.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Empleados Tab */}
              <TabsContent value="empleados" className="space-y-6">
                {/* Tarjetas dinámicas filtrables */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
                  <KpiCard
                    label="Total Empleados"
                    value={empleadosNomina.length}
                    sub="Ver todos"
                    icon={Users}
                    active={!empFilter}
                    onClick={() => setEmpFilter(null)}
                    testId="kpi-emp-total"
                  />
                  <KpiCard
                    label="Contrato Fijo"
                    value={empleadosNomina.filter(e => e.tipo_contrato === "fijo").length}
                    sub="Filtrar por contrato fijo"
                    icon={CheckCircle}
                    active={empFilter === "fijo"}
                    onClick={() => setEmpFilter(empFilter === "fijo" ? null : "fijo")}
                    testId="kpi-emp-fijos"
                  />
                  <KpiCard
                    label="Con Préstamo"
                    value={empleadosNomina.filter(e => e.tiene_prestamo).length}
                    sub="Filtrar con préstamo activo"
                    icon={PiggyBank}
                    active={empFilter === "con_prestamo"}
                    onClick={() => setEmpFilter(empFilter === "con_prestamo" ? null : "con_prestamo")}
                    testId="kpi-emp-con-prestamo"
                  />
                  <KpiCard
                    label="Cumplen Este Mes"
                    value={empleadosNomina.filter(e => e.fecha_nacimiento && parseInt(e.fecha_nacimiento.slice(5, 7)) === new Date().getMonth() + 1).length}
                    sub="Filtrar cumpleañeros"
                    icon={Cake}
                    active={!!empFilter && empFilter.startsWith("cumpleanos")}
                    onClick={() => setEmpFilter(empFilter && empFilter.startsWith("cumpleanos") ? null : "cumpleanos")}
                    testId="kpi-emp-cumpleanos"
                  />
                  <KpiCard
                    label="Masa Salarial"
                    value={formatCurrency(totalNomina)}
                    small
                    sub="Salarios brutos mensuales"
                    icon={Wallet}
                    testId="kpi-emp-masa-salarial"
                  />
                </div>

                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden" data-testid="empleados-table-container">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 px-6 py-4 border-b border-slate-200 dark:border-slate-700">
                    <h3 className="font-bold text-[#0C314C] dark:text-white font-['Manrope']">Configuración de Empleados</h3>
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          placeholder="Buscar empleado..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-10 w-56 rounded-lg border-slate-200 focus-visible:ring-[#45E69A]"
                          data-testid="search-input-empleados"
                        />
                      </div>
                      <Select value={empBancoFilter} onValueChange={setEmpBancoFilter}>
                        <SelectTrigger className="w-44 rounded-lg" data-testid="filter-banco-select">
                          <SelectValue placeholder="Banco" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todos">Todos los bancos</SelectItem>
                          {bancosEnUso.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button variant="outline" className="border-[#0C314C]/20 text-[#0C314C] dark:text-slate-200 hover:bg-[#0C314C]/5" onClick={handleImportarUsuarios} disabled={importandoUsuarios} data-testid="importar-usuarios-btn">
                        {importandoUsuarios ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />} Importar Usuarios
                      </Button>
                      <Button className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white" onClick={() => { resetEmpleadoForm(); setShowEmpleadoDialog(true); }} data-testid="add-empleado-btn">
                        <Plus className="w-4 h-4 mr-2" /> Agregar Empleado
                      </Button>
                    </div>
                  </div>
                  {filteredEmpleados.length === 0 ? (
                    <div className="text-center py-14 text-slate-400">
                      <Users className="w-12 h-12 mx-auto mb-3 opacity-40" />
                      <p className="text-sm">{empleadosNomina.length === 0 ? "No hay empleados configurados para nómina" : "Sin resultados con los filtros actuales"}</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-700">
                          <tr>
                            <th className={TH_CLS}>Empleado</th>
                            <th className={TH_CLS}>Salario</th>
                            <th className={TH_CLS}>Tipo</th>
                            <th className={TH_CLS}>Préstamo</th>
                            <th className={TH_CLS}>Banco</th>
                            <th className={`${TH_CLS} text-center`}>Acciones</th>
                          </tr>
                        </thead>
                        <tbody key={`${empFilter}-${empBancoFilter}`} className="divide-y divide-slate-100 dark:divide-slate-700 animate-in fade-in duration-300">
                          {filteredEmpleados.map(emp => (
                            <tr key={emp.user_id} className="hover:bg-slate-50/80 dark:hover:bg-slate-700/50 transition-colors duration-150" data-testid={`empleado-row-${emp.user_id}`}>
                              <td className={TD_CLS}>
                                <div className="flex items-center gap-3">
                                  <div className="w-9 h-9 rounded-full bg-[#0C314C]/10 flex items-center justify-center shrink-0">
                                    <span className="text-xs font-bold text-[#0C314C] dark:text-slate-200">{(emp.nombre || "?").split(" ").map(w => w[0]).slice(0, 2).join("")}</span>
                                  </div>
                                  <div className="min-w-0">
                                    <p className="font-semibold text-[#0C314C] dark:text-white truncate">{emp.nombre}</p>
                                    <p className="text-xs text-slate-500 truncate">
                                      {emp.codigo_empleado && <span className="font-mono text-[#3F6212] dark:text-[#45E69A] mr-1.5">[{emp.codigo_empleado}]</span>}
                                      {emp.email}
                                    </p>
                                  </div>
                                </div>
                              </td>
                              <td className={`${TD_CLS} font-mono font-semibold text-[#3F6212] dark:text-[#45E69A]`}>{formatCurrency(emp.salario_bruto)}</td>
                              <td className={`${TD_CLS} capitalize`}>{emp.tipo_contrato} / {emp.tipo_salario}</td>
                              <td className={TD_CLS}>
                                {emp.tiene_prestamo ? (
                                  <Badge className="bg-amber-100 text-amber-800 border border-amber-200 rounded-full font-mono text-xs">{formatCurrency(emp.cuota_prestamo)}/mes</Badge>
                                ) : (
                                  <span className="text-slate-400">—</span>
                                )}
                              </td>
                              <td className={TD_CLS}>{emp.banco || "—"}</td>
                              <td className={`${TD_CLS} text-center`}>
                                <div className="flex justify-center gap-1">
                                  <Button variant="ghost" size="sm" className="hover:bg-[#0C314C]/5" onClick={() => openViewEmpleado(emp)} title="Ver detalles" data-testid={`ver-empleado-${emp.user_id}`}>
                                    <Eye className="w-4 h-4 text-[#0C314C] dark:text-slate-300" />
                                  </Button>
                                  <Button variant="ghost" size="sm" className="hover:bg-[#0C314C]/5" onClick={() => openEditEmpleado(emp)} title="Editar" data-testid={`editar-empleado-${emp.user_id}`}>
                                    <Edit className="w-4 h-4 text-[#0C314C] dark:text-slate-300" />
                                  </Button>
                                  <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeleteEmpleado(emp.user_id)} title="Eliminar" data-testid={`eliminar-empleado-${emp.user_id}`}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div className="px-6 py-3 text-xs text-slate-500 border-t border-slate-100 dark:border-slate-700">
                    Mostrando {filteredEmpleados.length} de {empleadosNomina.length} empleados
                  </div>
                </div>
              </TabsContent>

              {/* Préstamos Tab */}
              <TabsContent value="prestamos" className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  <KpiCard
                    label="Todos los Préstamos"
                    value={prestamos.length}
                    sub="Ver todos"
                    icon={PiggyBank}
                    active={!prestamosFilter}
                    onClick={() => setPrestamosFilter(null)}
                    testId="kpi-prestamos-todos"
                  />
                  <KpiCard
                    label="Activos"
                    value={prestamosActivos.length}
                    sub="Filtrar activos"
                    icon={Clock}
                    active={prestamosFilter === "activo"}
                    onClick={() => setPrestamosFilter(prestamosFilter === "activo" ? null : "activo")}
                    testId="kpi-prestamos-activos"
                  />
                  <KpiCard
                    label="Saldados"
                    value={prestamos.length - prestamosActivos.length}
                    sub="Filtrar saldados"
                    icon={CheckCircle}
                    active={prestamosFilter === "saldado"}
                    onClick={() => setPrestamosFilter(prestamosFilter === "saldado" ? null : "saldado")}
                    testId="kpi-prestamos-saldados"
                  />
                  <KpiCard
                    label="Saldo Pendiente"
                    value={formatCurrency(totalPrestamos)}
                    small
                    sub="Total por cobrar"
                    icon={TrendingDown}
                    testId="kpi-prestamos-saldo"
                  />
                </div>

                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden" data-testid="prestamos-table-container">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 px-6 py-4 border-b border-slate-200 dark:border-slate-700">
                    <h3 className="font-bold text-[#0C314C] dark:text-white font-['Manrope']">Préstamos a Empleados</h3>
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          placeholder="Buscar por empleado..."
                          value={searchPrestamos}
                          onChange={(e) => setSearchPrestamos(e.target.value)}
                          className="pl-10 w-56 rounded-lg border-slate-200 focus-visible:ring-[#45E69A]"
                          data-testid="search-input-prestamos"
                        />
                      </div>
                      <Button className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white" onClick={() => { resetPrestamoForm(); setShowPrestamoDialog(true); }} data-testid="add-prestamo-btn">
                        <Plus className="w-4 h-4 mr-2" /> Nuevo Préstamo
                      </Button>
                    </div>
                  </div>
                  {filteredPrestamos.length === 0 ? (
                    <div className="text-center py-14 text-slate-400">
                      <PiggyBank className="w-12 h-12 mx-auto mb-3 opacity-40" />
                      <p className="text-sm">{prestamos.length === 0 ? "No hay préstamos registrados" : "Sin resultados con los filtros actuales"}</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-700">
                          <tr>
                            <th className={TH_CLS}>Empleado</th>
                            <th className={TH_CLS}>Motivo</th>
                            <th className={TH_CLS}>Monto Total</th>
                            <th className={TH_CLS}>Cuota</th>
                            <th className={TH_CLS}>Pagado</th>
                            <th className={TH_CLS}>Saldo</th>
                            <th className={TH_CLS}>Estado</th>
                            <th className={`${TH_CLS} text-center`}>Acciones</th>
                          </tr>
                        </thead>
                        <tbody key={prestamosFilter || "todos"} className="divide-y divide-slate-100 dark:divide-slate-700 animate-in fade-in duration-300">
                          {filteredPrestamos.map(p => {
                            const pct = p.monto_total > 0 ? Math.min(100, Math.round(((p.monto_pagado || 0) / p.monto_total) * 100)) : 0;
                            return (
                              <tr key={p.id} className="hover:bg-slate-50/80 dark:hover:bg-slate-700/50 transition-colors duration-150" data-testid={`prestamo-row-${p.id}`}>
                                <td className={`${TD_CLS} font-semibold text-[#0C314C] dark:text-white`}>{p.nombre_empleado}</td>
                                <td className={`${TD_CLS} max-w-[220px] truncate`} title={p.motivo}>{p.motivo || "—"}</td>
                                <td className={`${TD_CLS} font-mono font-medium`}>{formatCurrency(p.monto_total)}</td>
                                <td className={`${TD_CLS} font-mono`}>{formatCurrency(p.cuota_mensual)}</td>
                                <td className={TD_CLS}>
                                  <div className="min-w-[110px]">
                                    <p className="font-mono text-xs font-medium text-[#3F6212] dark:text-[#45E69A] mb-1">{formatCurrency(p.monto_pagado)}</p>
                                    <div className="h-1.5 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                                      <div className="h-full rounded-full bg-[#45E69A] transition-[width] duration-300" style={{ width: `${pct}%` }} />
                                    </div>
                                  </div>
                                </td>
                                <td className={`${TD_CLS} font-mono font-semibold text-red-600`}>{formatCurrency(p.saldo_pendiente)}</td>
                                <td className={TD_CLS}>{getEstadoBadge(p.estado)}</td>
                                <td className={`${TD_CLS} text-center`}>
                                  <div className="flex justify-center gap-1">
                                    <Button variant="ghost" size="sm" className="hover:bg-[#0C314C]/5" onClick={() => openViewPrestamo(p)} title="Ver" data-testid={`ver-prestamo-${p.id}`}>
                                      <Eye className="w-4 h-4 text-[#0C314C] dark:text-slate-300" />
                                    </Button>
                                    <Button variant="ghost" size="sm" className="hover:bg-[#0C314C]/5" onClick={() => openEditPrestamo(p)} title="Editar" data-testid={`editar-prestamo-${p.id}`}>
                                      <Edit className="w-4 h-4 text-[#0C314C] dark:text-slate-300" />
                                    </Button>
                                    <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeletePrestamo(p.id)} title="Eliminar" data-testid={`eliminar-prestamo-${p.id}`}>
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div className="px-6 py-3 text-xs text-slate-500 border-t border-slate-100 dark:border-slate-700">
                    Mostrando {filteredPrestamos.length} de {prestamos.length} préstamos
                  </div>
                </div>
              </TabsContent>

              {/* Avances Tab */}
              <TabsContent value="avances" className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  <KpiCard
                    label="Todos los Avances"
                    value={avances.length}
                    sub="Ver todos"
                    icon={Banknote}
                    active={!avancesFilter}
                    onClick={() => setAvancesFilter(null)}
                    testId="kpi-avances-todos"
                  />
                  <KpiCard
                    label="Pendientes"
                    value={avancesPendientes.length}
                    sub="Filtrar pendientes"
                    icon={Clock}
                    active={avancesFilter === "pendiente"}
                    onClick={() => setAvancesFilter(avancesFilter === "pendiente" ? null : "pendiente")}
                    testId="kpi-avances-pendientes"
                  />
                  <KpiCard
                    label="Descontados"
                    value={avances.filter(a => a.estado === "descontado").length}
                    sub="Filtrar descontados"
                    icon={CheckCircle}
                    active={avancesFilter === "descontado"}
                    onClick={() => setAvancesFilter(avancesFilter === "descontado" ? null : "descontado")}
                    testId="kpi-avances-descontados"
                  />
                  <KpiCard
                    label="Monto Pendiente"
                    value={formatCurrency(avancesPendientes.reduce((acc, a) => acc + Math.max((a.monto || 0) - (a.monto_descontado || 0), 0), 0))}
                    small
                    sub="Por descontar"
                    icon={Wallet}
                    testId="kpi-avances-monto"
                  />
                </div>

                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden" data-testid="avances-table-container">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 px-6 py-4 border-b border-slate-200 dark:border-slate-700">
                    <h3 className="font-bold text-[#0C314C] dark:text-white font-['Manrope']">Avances de Salario</h3>
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          placeholder="Buscar por empleado..."
                          value={searchAvances}
                          onChange={(e) => setSearchAvances(e.target.value)}
                          className="pl-10 w-56 rounded-lg border-slate-200 focus-visible:ring-[#45E69A]"
                          data-testid="search-input-avances"
                        />
                      </div>
                      <Button className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white" onClick={() => { resetAvanceForm(); setShowAvanceDialog(true); }} data-testid="add-avance-btn">
                        <Plus className="w-4 h-4 mr-2" /> Nuevo Avance
                      </Button>
                    </div>
                  </div>
                  {filteredAvances.length === 0 ? (
                    <div className="text-center py-14 text-slate-400">
                      <Banknote className="w-12 h-12 mx-auto mb-3 opacity-40" />
                      <p className="text-sm">{avances.length === 0 ? "No hay avances registrados" : "Sin resultados con los filtros actuales"}</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-700">
                          <tr>
                            <th className={TH_CLS}>Empleado</th>
                            <th className={TH_CLS}>Período de Descuento</th>
                            <th className={TH_CLS}>Motivo</th>
                            <th className={TH_CLS}>Monto</th>
                            <th className={TH_CLS}>Pendiente</th>
                            <th className={TH_CLS}>Estado</th>
                            <th className={`${TH_CLS} text-center`}>Acciones</th>
                          </tr>
                        </thead>
                        <tbody key={avancesFilter || "todos"} className="divide-y divide-slate-100 dark:divide-slate-700 animate-in fade-in duration-300">
                          {filteredAvances.map(a => (
                            <tr key={a.id} className="hover:bg-slate-50/80 dark:hover:bg-slate-700/50 transition-colors duration-150" data-testid={`avance-row-${a.id}`}>
                              <td className={`${TD_CLS} font-semibold text-[#0C314C] dark:text-white`}>{a.nombre_empleado}</td>
                              <td className={TD_CLS}>{a.periodo_descuento}</td>
                              <td className={`${TD_CLS} max-w-[240px] truncate`} title={a.motivo}>{a.motivo || "—"}</td>
                              <td className={`${TD_CLS} font-mono font-semibold`}>{formatCurrency(a.monto)}</td>
                              <td className={`${TD_CLS} font-mono font-semibold text-red-600`} data-testid={`avance-pendiente-${a.id}`}>
                                {formatCurrency(a.estado === "descontado" ? 0 : Math.max((a.monto || 0) - (a.monto_descontado || 0), 0))}
                              </td>
                              <td className={TD_CLS}>{getEstadoBadge(a.estado)}</td>
                              <td className={`${TD_CLS} text-center`}>
                                <div className="flex justify-center gap-1">
                                  <Button variant="ghost" size="sm" className="hover:bg-[#0C314C]/5" onClick={() => openViewAvance(a)} title="Ver" data-testid={`ver-avance-${a.id}`}>
                                    <Eye className="w-4 h-4 text-[#0C314C] dark:text-slate-300" />
                                  </Button>
                                  <Button variant="ghost" size="sm" className="hover:bg-[#0C314C]/5" onClick={() => openEditAvance(a)} title="Editar" data-testid={`editar-avance-${a.id}`}>
                                    <Edit className="w-4 h-4 text-[#0C314C] dark:text-slate-300" />
                                  </Button>
                                  <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeleteAvance(a.id)} title="Eliminar" data-testid={`eliminar-avance-${a.id}`}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div className="px-6 py-3 text-xs text-slate-500 border-t border-slate-100 dark:border-slate-700">
                    Mostrando {filteredAvances.length} de {avances.length} avances
                  </div>
                </div>
              </TabsContent>

              {/* Otros Beneficios Tab */}
              <TabsContent value="seguros" className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  <KpiCard label="Empleados con Beneficios" value={new Set(segurosMedicos.filter(s => s.activo !== false).map(s => s.user_id)).size} sub="Beneficios activos" icon={HeartPulse} testId="kpi-seguros-empleados" />
                  <KpiCard label="Beneficios Registrados" value={segurosMedicos.length} sub="Total de registros" icon={FileText} testId="kpi-seguros-total" />
                  <KpiCard label="Descuento a Empleados" value={formatCurrency(segurosMedicos.filter(s => s.activo !== false && s.aplica_descuento).reduce((acc, s) => acc + descuentoEmpleadoSeguro(s), 0))} small sub="Según la frecuencia de cada beneficio" icon={Wallet} testId="kpi-seguros-monto" />
                  <KpiCard label="Tipos de Beneficio" value={new Set(segurosMedicos.map(s => (s.tipo_seguro || "").trim().toLowerCase()).filter(Boolean)).size} sub="Distintos conceptos" icon={CheckCircle} testId="kpi-seguros-tipos" />
                </div>

                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden" data-testid="seguros-table-container">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 px-6 py-4 border-b border-slate-200 dark:border-slate-700">
                    <h3 className="font-bold text-[#0C314C] dark:text-white font-['Manrope']">Otros Beneficios (Seguros y Descuentos)</h3>
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input placeholder="Buscar empleado o beneficio..." value={searchSeguros} onChange={(e) => setSearchSeguros(e.target.value)} className="pl-10 w-56 rounded-lg border-slate-200 focus-visible:ring-[#45E69A]" data-testid="search-input-seguros" />
                      </div>
                      <Button variant="outline" className="border-[#0C314C]/20 text-[#0C314C] dark:text-slate-200 hover:bg-[#0C314C]/5" onClick={() => handleExportBeneficiosExcel("seguros")} data-testid="export-seguros-btn">
                        <FileSpreadsheet className="w-4 h-4 mr-2" /> Exportar Excel
                      </Button>
                      <Button className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white" onClick={() => { resetSeguroForm(); setShowSeguroDialog(true); }} data-testid="add-seguro-btn">
                        <Plus className="w-4 h-4 mr-2" /> Nuevo Beneficio
                      </Button>
                    </div>
                  </div>
                  {filteredSeguros.length === 0 ? (
                    <div className="text-center py-14 text-slate-400">
                      <HeartPulse className="w-12 h-12 mx-auto mb-3 opacity-40" />
                      <p className="text-sm">{segurosMedicos.length === 0 ? "No hay beneficios registrados" : "Sin resultados con los filtros actuales"}</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-700">
                          <tr>
                            <th className={TH_CLS}>Empleado</th>
                            <th className={TH_CLS}>Beneficio</th>
                            <th className={TH_CLS}>Frecuencia</th>
                            <th className={TH_CLS}>Fecha Inicio</th>
                            <th className={TH_CLS}>Dependientes</th>
                            <th className={TH_CLS}>Costo Total</th>
                            <th className={TH_CLS}>Descuento Nómina</th>
                            <th className={TH_CLS}>Estado</th>
                            <th className={`${TH_CLS} text-center`}>Acciones</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-700 animate-in fade-in duration-300">
                          {filteredSeguros.map(s => (
                            <tr key={s.id} className="hover:bg-slate-50/80 dark:hover:bg-slate-700/50 transition-colors duration-150" data-testid={`seguro-row-${s.id}`}>
                              <td className={`${TD_CLS} font-semibold text-[#0C314C] dark:text-white`}>{s.nombre_empleado}</td>
                              <td className={TD_CLS}>
                                <p>{s.tipo_seguro}</p>
                                {(s.costo_titular > 0 || s.costo_dependiente > 0) && (
                                  <p className="text-xs text-slate-400 font-mono">Titular: {formatCurrency(s.costo_titular)} · Dep: {formatCurrency(s.costo_dependiente)}</p>
                                )}
                              </td>
                              <td className={`${TD_CLS} capitalize`}>{s.frecuencia}</td>
                              <td className={`${TD_CLS} font-mono text-xs`}>{s.fecha_inicio ? s.fecha_inicio.split("-").reverse().join("/") : "—"}</td>
                              <td className={`${TD_CLS} text-center font-mono`}>{s.cantidad_dependientes || 0}</td>
                              <td className={`${TD_CLS} font-mono font-semibold`}>{formatCurrency(s.monto)}</td>
                              <td className={TD_CLS}>
                                {s.aplica_descuento ? (
                                  <div>
                                    <Badge className="bg-amber-100 text-amber-800 border border-amber-200 rounded-full font-mono text-xs">-{formatCurrency(descuentoEmpleadoSeguro(s))} ({s.pct_empleado || 0}%)</Badge>
                                    <p className="text-[10px] text-slate-400 mt-0.5">Empresa paga {s.pct_empresa ?? 100}%</p>
                                  </div>
                                ) : (
                                  <Badge className="bg-[#45E69A]/20 text-[#3F6212] border border-[#45E69A]/40 rounded-full text-xs font-semibold">Empresa 100%</Badge>
                                )}
                              </td>
                              <td className={TD_CLS}>
                                <Badge className={`${s.activo !== false ? "bg-[#45E69A]/20 text-[#3F6212] border-[#45E69A]/40" : "bg-slate-100 text-slate-600 border-slate-200"} border rounded-full font-semibold`}>
                                  {s.activo !== false ? "Activo" : "Inactivo"}
                                </Badge>
                              </td>
                              <td className={`${TD_CLS} text-center`}>
                                <div className="flex justify-center gap-1">
                                  <Button variant="ghost" size="sm" className="hover:bg-[#0C314C]/5" onClick={() => openEditSeguro(s)} title="Editar" data-testid={`editar-seguro-${s.id}`}>
                                    <Edit className="w-4 h-4 text-[#0C314C] dark:text-slate-300" />
                                  </Button>
                                  <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeleteSeguro(s.id)} title="Eliminar" data-testid={`eliminar-seguro-${s.id}`}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div className="px-6 py-3 text-xs text-slate-500 border-t border-slate-100 dark:border-slate-700">
                    Mostrando {filteredSeguros.length} de {segurosMedicos.length} beneficios
                  </div>
                </div>
              </TabsContent>

              {/* Dependientes TSS Tab */}
              <TabsContent value="dependientes" className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  <KpiCard label="Dependientes Adicionales" value={dependientes.length} sub="Registrados" icon={UsersRound} testId="kpi-dep-total" />
                  <KpiCard label="Empleados con Dependientes" value={new Set(dependientes.map(d => d.user_id)).size} sub="Con al menos uno" icon={Users} testId="kpi-dep-empleados" />
                  <KpiCard label="Cápita por Dependiente" value={formatCurrency(capitaDependiente)} small sub="Configurable abajo" icon={Settings} testId="kpi-dep-capita" />
                  <KpiCard label="Descuento Total Mensual" value={formatCurrency(dependientes.length * capitaDependiente)} small sub={`${dependientes.length} × ${formatCurrency(capitaDependiente)}`} icon={Wallet} testId="kpi-dep-costo" />
                </div>

                {/* Configuración de cápita */}
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-5 flex flex-col sm:flex-row sm:items-end gap-4" data-testid="capita-config-container">
                  <div className="flex-1">
                    <p className="font-bold text-[#0C314C] dark:text-white font-['Manrope'] mb-1">Configuración TSS — Cápita por Dependiente Adicional</p>
                    <p className="text-xs text-slate-500">Monto que se descuenta al empleado por cada dependiente adicional (padre, madre, suegra, suegro) inscrito en TSS.</p>
                  </div>
                  <div className="flex items-end gap-3">
                    <div>
                      <Label>Cápita (RD$)</Label>
                      <Input type="number" className="w-40" value={capitaInput} onChange={e => setCapitaInput(e.target.value)} data-testid="capita-input" />
                    </div>
                    <Button className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white" onClick={handleSaveCapita} disabled={saving} data-testid="guardar-capita-btn">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
                    </Button>
                  </div>
                </div>

                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden" data-testid="dependientes-table-container">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 px-6 py-4 border-b border-slate-200 dark:border-slate-700">
                    <h3 className="font-bold text-[#0C314C] dark:text-white font-['Manrope']">Dependientes Adicionales (TSS)</h3>
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input placeholder="Buscar dependiente o empleado..." value={searchDependientes} onChange={(e) => setSearchDependientes(e.target.value)} className="pl-10 w-60 rounded-lg border-slate-200 focus-visible:ring-[#45E69A]" data-testid="search-input-dependientes" />
                      </div>
                      <Button variant="outline" className="border-[#0C314C]/20 text-[#0C314C] dark:text-slate-200 hover:bg-[#0C314C]/5" onClick={() => handleExportBeneficiosExcel("dependientes")} data-testid="export-dependientes-btn">
                        <FileSpreadsheet className="w-4 h-4 mr-2" /> Exportar Excel
                      </Button>
                      <Button className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white" onClick={() => { resetDependienteForm(); setShowDependienteDialog(true); }} data-testid="add-dependiente-btn">
                        <Plus className="w-4 h-4 mr-2" /> Nuevo Dependiente
                      </Button>
                    </div>
                  </div>
                  {filteredDependientes.length === 0 ? (
                    <div className="text-center py-14 text-slate-400">
                      <UsersRound className="w-12 h-12 mx-auto mb-3 opacity-40" />
                      <p className="text-sm">{dependientes.length === 0 ? "No hay dependientes adicionales registrados" : "Sin resultados con los filtros actuales"}</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-700">
                          <tr>
                            <th className={TH_CLS}>Dependiente</th>
                            <th className={TH_CLS}>Parentesco</th>
                            <th className={TH_CLS}>Empleado</th>
                            <th className={TH_CLS}>Costo (Cápita)</th>
                            <th className={`${TH_CLS} text-center`}>Acciones</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-700 animate-in fade-in duration-300">
                          {filteredDependientes.map(d => (
                            <tr key={d.id} className="hover:bg-slate-50/80 dark:hover:bg-slate-700/50 transition-colors duration-150" data-testid={`dependiente-row-${d.id}`}>
                              <td className={`${TD_CLS} font-semibold text-[#0C314C] dark:text-white`}>{d.nombre}</td>
                              <td className={TD_CLS}>
                                <Badge className="bg-[#0C314C]/10 text-[#0C314C] dark:text-slate-200 border border-[#0C314C]/20 rounded-full font-semibold capitalize">{d.parentesco}</Badge>
                              </td>
                              <td className={TD_CLS}>{d.nombre_empleado}</td>
                              <td className={`${TD_CLS} font-mono font-semibold`}>{formatCurrency(capitaDependiente)}</td>
                              <td className={`${TD_CLS} text-center`}>
                                <div className="flex justify-center gap-1">
                                  <Button variant="ghost" size="sm" className="hover:bg-[#0C314C]/5" onClick={() => openEditDependiente(d)} title="Editar" data-testid={`editar-dependiente-${d.id}`}>
                                    <Edit className="w-4 h-4 text-[#0C314C] dark:text-slate-300" />
                                  </Button>
                                  <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeleteDependiente(d.id)} title="Eliminar" data-testid={`eliminar-dependiente-${d.id}`}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div className="px-6 py-3 text-xs text-slate-500 border-t border-slate-100 dark:border-slate-700">
                    Mostrando {filteredDependientes.length} de {dependientes.length} dependientes
                  </div>
                </div>
              </TabsContent>

              {/* Historial Tab */}
              <TabsContent value="historial" className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  <KpiCard
                    label="Borrador"
                    value={nominasHistorial.filter(n => n.estado === "borrador").length}
                    sub="Filtrar borradores"
                    icon={FileText}
                    active={historialFilter === "borrador"}
                    onClick={() => setHistorialFilter(historialFilter === "borrador" ? null : "borrador")}
                    testId="kpi-hist-borrador"
                  />
                  <KpiCard
                    label="Aprobadas"
                    value={nominasHistorial.filter(n => n.estado === "aprobada").length}
                    sub="Filtrar aprobadas"
                    icon={CheckCircle}
                    active={historialFilter === "aprobada"}
                    onClick={() => setHistorialFilter(historialFilter === "aprobada" ? null : "aprobada")}
                    testId="kpi-hist-aprobadas"
                  />
                  <KpiCard
                    label="Pagadas"
                    value={nominasHistorial.filter(n => n.estado === "pagada").length}
                    sub="Filtrar pagadas"
                    icon={DollarSign}
                    active={historialFilter === "pagada"}
                    onClick={() => setHistorialFilter(historialFilter === "pagada" ? null : "pagada")}
                    testId="kpi-hist-pagadas"
                  />
                  <KpiCard
                    label="Total Neto Pagado"
                    value={formatCurrency(totalNetoPagado)}
                    small
                    sub="Acumulado en nóminas pagadas"
                    icon={TrendingUp}
                    testId="kpi-hist-total-pagado"
                  />
                </div>

                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden" data-testid="historial-table-container">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 px-6 py-4 border-b border-slate-200 dark:border-slate-700">
                    <h3 className="font-bold text-[#0C314C] dark:text-white font-['Manrope']">Historial de Nóminas</h3>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Buscar período..."
                        value={searchHistorial}
                        onChange={(e) => setSearchHistorial(e.target.value)}
                        className="pl-10 w-56 rounded-lg border-slate-200 focus-visible:ring-[#45E69A]"
                        data-testid="search-input-historial"
                      />
                    </div>
                  </div>
                  {filteredHistorial.length === 0 ? (
                    <div className="text-center py-14 text-slate-400">
                      <History className="w-12 h-12 mx-auto mb-3 opacity-40" />
                      <p className="text-sm">{nominasHistorial.length === 0 ? "No hay nóminas procesadas" : "Sin resultados con los filtros actuales"}</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-700">
                          <tr>
                            <th className={TH_CLS}>Período</th>
                            <th className={TH_CLS}>Tipo</th>
                            <th className={TH_CLS}>Total Bruto</th>
                            <th className={TH_CLS}>Deducciones</th>
                            <th className={TH_CLS}>Total Neto</th>
                            <th className={TH_CLS}>Estado</th>
                            <th className={`${TH_CLS} text-center`}>Acciones</th>
                          </tr>
                        </thead>
                        <tbody key={historialFilter || "todos"} className="divide-y divide-slate-100 dark:divide-slate-700 animate-in fade-in duration-300">
                          {filteredHistorial.map(n => (
                            <tr key={n.id} className="hover:bg-slate-50/80 dark:hover:bg-slate-700/50 transition-colors duration-150" data-testid={`historial-row-${n.id}`}>
                              <td className={`${TD_CLS} font-semibold text-[#0C314C] dark:text-white`}>{n.periodo}</td>
                              <td className={`${TD_CLS} capitalize`}>{n.tipo_periodo}</td>
                              <td className={`${TD_CLS} font-mono`}>{formatCurrency(n.totales?.total_bruto)}</td>
                              <td className={`${TD_CLS} font-mono text-red-600`}>{formatCurrency(n.totales?.total_deducciones)}</td>
                              <td className={`${TD_CLS} font-mono font-semibold text-[#3F6212] dark:text-[#45E69A]`}>{formatCurrency(n.totales?.total_neto)}</td>
                              <td className={TD_CLS}>{getEstadoBadge(n.estado)}</td>
                              <td className={`${TD_CLS} text-center`}>
                                <div className="flex justify-center gap-1">
                                  <Button variant="ghost" size="sm" className="hover:bg-[#0C314C]/5 text-[#0C314C] dark:text-slate-300" onClick={() => handleViewNomina(n.id)} data-testid={`ver-nomina-${n.id}`}>
                                    <Eye className="w-4 h-4 mr-1" /> Ver
                                  </Button>
                                  {(esAdminRol || (esGHRol && n.estado === "borrador")) && n.estado !== "eliminada" && (
                                    <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => abrirEliminarNomina(n)} title="Eliminar nómina" data-testid={`eliminar-nomina-${n.id}`}>
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div className="px-6 py-3 text-xs text-slate-500 border-t border-slate-100 dark:border-slate-700">
                    Mostrando {filteredHistorial.length} de {nominasHistorial.length} nóminas
                  </div>
                </div>
              </TabsContent>

              {/* Mis Nóminas Tab (Config) */}
              <TabsContent value="nominas" className="space-y-6">
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden" data-testid="nominas-config-container">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 px-6 py-4 border-b border-slate-200 dark:border-slate-700">
                    <div>
                      <h3 className="font-bold text-[#0C314C] dark:text-white font-['Manrope']">Configuración de Nóminas</h3>
                      <p className="text-sm text-slate-500 mt-0.5">Administre múltiples nóminas para diferentes grupos de empleados</p>
                    </div>
                    <Button className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white" onClick={() => { resetNominaConfigForm(); setShowNuevaNominaDialog(true); }} data-testid="add-nomina-config-btn">
                      <Plus className="w-4 h-4 mr-2" /> Nueva Nómina
                    </Button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-700">
                        <tr>
                          <th className={TH_CLS}>Nómina</th>
                          <th className={TH_CLS}>Tipo</th>
                          <th className={TH_CLS}>Banco</th>
                          <th className={TH_CLS}>Día de Pago</th>
                          <th className={TH_CLS}>TSS/ISR</th>
                          <th className={TH_CLS}>Estado</th>
                          <th className={`${TH_CLS} text-center`}>Acciones</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                        {/* Nómina Principal (default) */}
                        <tr className="bg-[#0C314C]/[0.03] dark:bg-slate-700/30" data-testid="nomina-config-default-row">
                          <td className={TD_CLS}>
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-[#0C314C] dark:text-white">Nómina Principal</p>
                              <Badge className="bg-[#45E69A]/20 text-[#3F6212] border border-[#45E69A]/40 rounded-full text-[10px]">Default</Badge>
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5">{empleadosNomina.length} empleados · {formatCurrency(totalNomina)}</p>
                          </td>
                          <td className={TD_CLS}>Mensual</td>
                          <td className={TD_CLS}>{parametros?.banco_default || "No configurado"}</td>
                          <td className={`${TD_CLS} font-mono`}>—</td>
                          <td className={TD_CLS}>TSS + ISR</td>
                          <td className={TD_CLS}><Badge className="bg-[#45E69A]/20 text-[#3F6212] border border-[#45E69A]/40 rounded-full font-semibold">Activa</Badge></td>
                          <td className={`${TD_CLS} text-center text-slate-400 text-xs`}>Sistema</td>
                        </tr>
                        {nominasConfig.map(nom => (
                          <tr key={nom.id} className="hover:bg-slate-50/80 dark:hover:bg-slate-700/50 transition-colors duration-150" data-testid={`nomina-config-row-${nom.id}`}>
                            <td className={TD_CLS}>
                              <div className="flex items-center gap-2">
                                <p className="font-semibold text-[#0C314C] dark:text-white">{nom.nombre}</p>
                                {nom.es_default && <Badge className="bg-[#45E69A]/20 text-[#3F6212] border border-[#45E69A]/40 rounded-full text-[10px]">Default</Badge>}
                              </div>
                              {nom.descripcion && <p className="text-xs text-slate-500 mt-0.5 max-w-[280px] truncate" title={nom.descripcion}>{nom.descripcion}</p>}
                            </td>
                            <td className={`${TD_CLS} capitalize`}>{nom.tipo_periodo}</td>
                            <td className={TD_CLS}>{nom.banco_default || "No configurado"}</td>
                            <td className={`${TD_CLS} font-mono`}>{nom.tipo_periodo === "quincenal" ? `${nom.dia_pago_1 || 15} y ${nom.dia_pago_2 || 30}` : nom.tipo_periodo === "semanal" ? <span className="capitalize font-sans">{nom.dia_semana_pago || "viernes"}</span> : (nom.dia_pago || 15)}</td>
                            <td className={TD_CLS}>
                              {nom.incluir_tss !== false ? "TSS" : ""}{nom.incluir_tss !== false && nom.incluir_isr !== false ? " + " : ""}{nom.incluir_isr !== false ? "ISR" : ""}
                            </td>
                            <td className={TD_CLS}>
                              <Badge className={`${nom.activa ? "bg-[#45E69A]/20 text-[#3F6212] border-[#45E69A]/40" : "bg-slate-100 text-slate-600 border-slate-200"} border rounded-full font-semibold`}>
                                {nom.activa ? "Activa" : "Inactiva"}
                              </Badge>
                            </td>
                            <td className={`${TD_CLS} text-center`}>
                              <div className="flex justify-center gap-1">
                                <Button variant="ghost" size="sm" className="hover:bg-[#0C314C]/5" onClick={() => openViewNominaConfig(nom)} title="Ver detalles" data-testid={`ver-nomina-config-${nom.id}`}>
                                  <Eye className="w-4 h-4 text-[#0C314C] dark:text-slate-300" />
                                </Button>
                                <Button variant="ghost" size="sm" className="hover:bg-[#0C314C]/5" onClick={() => openEditNominaConfig(nom)} title="Editar" data-testid={`editar-nomina-config-${nom.id}`}>
                                  <Edit className="w-4 h-4 text-[#0C314C] dark:text-slate-300" />
                                </Button>
                                <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeleteNominaConfig(nom.id)} title="Eliminar" data-testid={`eliminar-nomina-config-${nom.id}`}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </TabsContent>

              {/* Departamentos Tab */}
              <TabsContent value="departamentos" className="space-y-6">
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden" data-testid="departamentos-container">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 px-6 py-4 border-b border-slate-200 dark:border-slate-700">
                    <div>
                      <h3 className="font-bold text-[#0C314C] dark:text-white font-['Manrope']">Departamentos</h3>
                      <p className="text-sm text-slate-500 mt-0.5">Estructura organizacional para asignar a los empleados</p>
                    </div>
                    <div className="flex items-center gap-3 w-full sm:w-auto">
                      <div className="relative flex-1 sm:flex-none">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input className="pl-9 sm:w-56" placeholder="Buscar departamento..." value={searchDeptos} onChange={e => setSearchDeptos(e.target.value)} data-testid="search-departamentos-input" />
                      </div>
                      <Button className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white" onClick={() => { setEditingDept(null); setDeptForm({ name: "", description: "" }); setShowDeptDialog(true); }} data-testid="add-departamento-btn">
                        <Plus className="w-4 h-4 mr-2" /> Nuevo Departamento
                      </Button>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-700">
                        <tr>
                          <th className={TH_CLS}>Nombre</th>
                          <th className={TH_CLS}>Descripción</th>
                          <th className={TH_CLS}>Empleados</th>
                          <th className={`${TH_CLS} text-center`}>Acciones</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                        {departamentos.filter(d => !searchDeptos || d.name?.toLowerCase().includes(searchDeptos.toLowerCase())).map(d => (
                          <tr key={d.id} className="hover:bg-slate-50/80 dark:hover:bg-slate-700/50 transition-colors duration-150" data-testid={`departamento-row-${d.id}`}>
                            <td className={`${TD_CLS} font-semibold text-[#0C314C] dark:text-white`}>{d.name}</td>
                            <td className={TD_CLS}>{d.description || "—"}</td>
                            <td className={`${TD_CLS} font-mono`}>{empleadosNomina.filter(e => e.departamento_id === d.id).length}</td>
                            <td className={`${TD_CLS} text-center`}>
                              <div className="flex justify-center gap-1">
                                <Button variant="ghost" size="sm" className="hover:bg-[#0C314C]/5" onClick={() => { setEditingDept(d); setDeptForm({ name: d.name || "", description: d.description || "" }); setShowDeptDialog(true); }} title="Editar" data-testid={`editar-departamento-${d.id}`}>
                                  <Edit className="w-4 h-4 text-[#0C314C] dark:text-slate-300" />
                                </Button>
                                <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeleteDept(d.id)} title="Eliminar" data-testid={`eliminar-departamento-${d.id}`}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {departamentos.length === 0 && (
                          <tr><td colSpan={4} className="px-6 py-10 text-center text-slate-400 text-sm">No hay departamentos registrados</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </TabsContent>

              {/* Cargos Tab */}
              <TabsContent value="cargos" className="space-y-6">
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden" data-testid="cargos-container">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 px-6 py-4 border-b border-slate-200 dark:border-slate-700">
                    <div>
                      <h3 className="font-bold text-[#0C314C] dark:text-white font-['Manrope']">Cargos</h3>
                      <p className="text-sm text-slate-500 mt-0.5">Puestos de trabajo para asignar a los empleados</p>
                    </div>
                    <div className="flex items-center gap-3 w-full sm:w-auto">
                      <div className="relative flex-1 sm:flex-none">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input className="pl-9 sm:w-56" placeholder="Buscar cargo..." value={searchCargos} onChange={e => setSearchCargos(e.target.value)} data-testid="search-cargos-input" />
                      </div>
                      <Button className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white" onClick={() => { setEditingCargo(null); setCargoForm({ nombre: "", descripcion: "" }); setShowCargoDialog(true); }} data-testid="add-cargo-btn">
                        <Plus className="w-4 h-4 mr-2" /> Nuevo Cargo
                      </Button>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-700">
                        <tr>
                          <th className={TH_CLS}>Nombre</th>
                          <th className={TH_CLS}>Descripción</th>
                          <th className={TH_CLS}>Empleados</th>
                          <th className={`${TH_CLS} text-center`}>Acciones</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                        {cargos.filter(c => !searchCargos || c.nombre?.toLowerCase().includes(searchCargos.toLowerCase())).map(c => (
                          <tr key={c.id} className="hover:bg-slate-50/80 dark:hover:bg-slate-700/50 transition-colors duration-150" data-testid={`cargo-row-${c.id}`}>
                            <td className={`${TD_CLS} font-semibold text-[#0C314C] dark:text-white`}>{c.nombre}</td>
                            <td className={TD_CLS}>{c.descripcion || "—"}</td>
                            <td className={`${TD_CLS} font-mono`}>{empleadosNomina.filter(e => e.cargo_id === c.id).length}</td>
                            <td className={`${TD_CLS} text-center`}>
                              <div className="flex justify-center gap-1">
                                <Button variant="ghost" size="sm" className="hover:bg-[#0C314C]/5" onClick={() => { setEditingCargo(c); setCargoForm({ nombre: c.nombre || "", descripcion: c.descripcion || "" }); setShowCargoDialog(true); }} title="Editar" data-testid={`editar-cargo-${c.id}`}>
                                  <Edit className="w-4 h-4 text-[#0C314C] dark:text-slate-300" />
                                </Button>
                                <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeleteCargo(c.id)} title="Eliminar" data-testid={`eliminar-cargo-${c.id}`}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {cargos.length === 0 && (
                          <tr><td colSpan={4} className="px-6 py-10 text-center text-slate-400 text-sm">No hay cargos registrados</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </TabsContent>

              {/* Plantilla de Cumpleaños Tab */}
              <TabsContent value="plantillas_cumple" className="space-y-6">
                <div className="bg-white dark:bg-slate-900 rounded-xl border shadow-sm p-6" data-testid="plantillas-cumple-tab">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <Cake className="w-5 h-5 text-[#0C314C]" />
                      Plantillas de Cumpleaños
                    </h3>
                    <label className="cursor-pointer">
                      <input type="file" accept="image/png,image/jpeg" multiple className="hidden" onChange={handleSubirPlantilla} disabled={subiendoPlantilla} data-testid="input-subir-plantilla-cumple" />
                      <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white ${subiendoPlantilla ? "bg-slate-400" : "bg-[#0C314C] hover:bg-[#16304f]"}`} data-testid="btn-subir-plantilla-cumple">
                        {subiendoPlantilla ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        {subiendoPlantilla ? "Subiendo..." : "Cargar Plantillas"}
                      </span>
                    </label>
                  </div>
                  <p className="text-sm text-slate-500 mb-5">
                    Sube imágenes con el logo de QAsesores y una zona blanca amplia (la más grande de la imagen). Al generar el diseño del mes, el sistema elegirá una plantilla al azar y escribirá el mes y los cumpleañeros en esa zona, variando el tipo de letra en cada regeneración. Si no hay plantillas cargadas, se usa el diseño automático.
                  </p>
                  {plantillasCumple.length === 0 ? (
                    <div className="text-center py-12 text-slate-400 border-2 border-dashed rounded-xl" data-testid="plantillas-cumple-empty">
                      <ImagePlus className="w-10 h-10 mx-auto mb-3" />
                      <p className="text-sm">No hay plantillas cargadas.</p>
                      <p className="text-xs mt-1">Mientras tanto, los diseños se generan automáticamente.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {plantillasCumple.map((p) => (
                        <div key={p.id} className="border rounded-xl overflow-hidden group relative" data-testid={`plantilla-cumple-${p.id}`}>
                          {plantillasThumbs[p.id] ? (
                            <img src={plantillasThumbs[p.id]} alt={p.filename} className="w-full h-44 object-cover" />
                          ) : (
                            <div className="w-full h-44 flex items-center justify-center bg-slate-100 dark:bg-slate-800">
                              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                            </div>
                          )}
                          <div className="p-2.5 flex items-center justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-xs font-medium truncate" title={p.filename}>{p.filename}</p>
                              <p className="text-[10px] text-slate-400">{((p.size_bytes || 0) / 1024).toFixed(0)} KB · {p.created_by || ""}</p>
                            </div>
                            <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50 shrink-0" onClick={() => handleEliminarPlantilla(p.id)} data-testid={`btn-eliminar-plantilla-${p.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </>
        )}

        {/* Eliminar Nómina Dialog */}
        <Dialog open={showEliminarNominaDialog} onOpenChange={(o) => { setShowEliminarNominaDialog(o); if (!o) setNominaAEliminar(null); }}>
          <DialogContent className="max-w-md" data-testid="eliminar-nomina-dialog">
            <DialogHeader>
              <DialogTitle className="text-red-600 flex items-center gap-2"><Trash2 className="w-5 h-5" /> Eliminar Nómina</DialogTitle>
              <DialogDescription>
                {nominaAEliminar && (
                  <>Está a punto de eliminar la nómina del período <b>{nominaAEliminar.periodo}</b> (estado: {nominaAEliminar.estado}). Esta acción no se puede deshacer.</>
                )}
              </DialogDescription>
            </DialogHeader>
            {!esAdminRol && (
              <div className="space-y-4">
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-sm text-amber-800 dark:text-amber-200">
                  Por seguridad, otro usuario con rol de <b>Gestión Humana</b> debe confirmar esta eliminación con sus credenciales.
                </div>
                <div>
                  <Label>Correo del usuario que confirma *</Label>
                  <Input type="email" value={eliminarConfirmForm.email} onChange={e => setEliminarConfirmForm({ ...eliminarConfirmForm, email: e.target.value })} placeholder="correo@qasesores.com.do" data-testid="eliminar-confirm-email-input" />
                </div>
                <div>
                  <Label>Clave de confirmación *</Label>
                  <Input type="password" value={eliminarConfirmForm.password} onChange={e => setEliminarConfirmForm({ ...eliminarConfirmForm, password: e.target.value })} placeholder="Contraseña del usuario que confirma" data-testid="eliminar-confirm-password-input" />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEliminarNominaDialog(false)}>Cancelar</Button>
              <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={handleEliminarNomina} disabled={eliminandoNomina} data-testid="eliminar-nomina-confirmar-btn">
                {eliminandoNomina ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />} Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Departamento Form Dialog */}
        <Dialog open={showDeptDialog} onOpenChange={(o) => { setShowDeptDialog(o); if (!o) { setEditingDept(null); setDeptForm({ name: "", description: "" }); } }}>
          <DialogContent className="max-w-md" data-testid="departamento-dialog">
            <DialogHeader>
              <DialogTitle>{editingDept ? "Editar Departamento" : "Nuevo Departamento"}</DialogTitle>
              <DialogDescription>Los departamentos se pueden asignar a los empleados de nómina</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Nombre *</Label>
                <Input value={deptForm.name} onChange={e => setDeptForm({ ...deptForm, name: e.target.value })} placeholder="Ej: Contabilidad" data-testid="departamento-nombre-input" />
              </div>
              <div>
                <Label>Descripción</Label>
                <Textarea value={deptForm.description} onChange={e => setDeptForm({ ...deptForm, description: e.target.value })} placeholder="Descripción del departamento" data-testid="departamento-descripcion-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeptDialog(false)}>Cancelar</Button>
              <Button className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white" onClick={handleSaveDept} disabled={saving} data-testid="departamento-guardar-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} {editingDept ? "Actualizar" : "Crear"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Cargo Form Dialog */}
        <Dialog open={showCargoDialog} onOpenChange={(o) => { setShowCargoDialog(o); if (!o) { setEditingCargo(null); setCargoForm({ nombre: "", descripcion: "" }); } }}>
          <DialogContent className="max-w-md" data-testid="cargo-dialog">
            <DialogHeader>
              <DialogTitle>{editingCargo ? "Editar Cargo" : "Nuevo Cargo"}</DialogTitle>
              <DialogDescription>Los cargos se pueden asignar a los empleados de nómina</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Nombre *</Label>
                <Input value={cargoForm.nombre} onChange={e => setCargoForm({ ...cargoForm, nombre: e.target.value })} placeholder="Ej: Analista de Riesgos" data-testid="cargo-nombre-input" />
              </div>
              <div>
                <Label>Descripción</Label>
                <Textarea value={cargoForm.descripcion} onChange={e => setCargoForm({ ...cargoForm, descripcion: e.target.value })} placeholder="Descripción del cargo" data-testid="cargo-descripcion-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCargoDialog(false)}>Cancelar</Button>
              <Button className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white" onClick={handleSaveCargo} disabled={saving} data-testid="cargo-guardar-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} {editingCargo ? "Actualizar" : "Crear"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Otros Beneficios Form Dialog */}
        <Dialog open={showSeguroDialog} onOpenChange={(o) => { setShowSeguroDialog(o); if (!o) resetSeguroForm(); }}>
          <DialogContent className="max-w-xl" data-testid="seguro-dialog">
            <DialogHeader>
              <DialogTitle>{seguroEditMode ? "Editar Beneficio" : "Nuevo Beneficio"}</DialogTitle>
              <DialogDescription>Registre el beneficio (seguro u otro) que aplica al empleado</DialogDescription>
            </DialogHeader>
            <ScrollArea className="max-h-[65vh]">
              <div className="space-y-4 pr-4">
                <div>
                  <Label>Empleado *</Label>
                  <Select value={seguroForm.user_id} onValueChange={v => setSeguroForm({ ...seguroForm, user_id: v })}>
                    <SelectTrigger data-testid="seguro-empleado-select"><SelectValue placeholder="Seleccionar empleado" /></SelectTrigger>
                    <SelectContent>
                      {empleadosNomina.map(e => <SelectItem key={e.user_id} value={e.user_id}>{e.codigo_empleado ? `[${e.codigo_empleado}] ` : ""}{e.nombre}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Tipo de Beneficio *</Label>
                    <Select value={seguroForm.tipo_seguro} onValueChange={v => setSeguroForm({ ...seguroForm, tipo_seguro: v })}>
                      <SelectTrigger data-testid="seguro-tipo-select"><SelectValue placeholder="Seleccionar tipo" /></SelectTrigger>
                      <SelectContent>
                        {TIPOS_BENEFICIO.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        {seguroForm.tipo_seguro && !TIPOS_BENEFICIO.includes(seguroForm.tipo_seguro) && (
                          <SelectItem value={seguroForm.tipo_seguro}>{seguroForm.tipo_seguro}</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Frecuencia</Label>
                    <Select value={seguroForm.frecuencia} onValueChange={v => setSeguroForm({ ...seguroForm, frecuencia: v })}>
                      <SelectTrigger data-testid="seguro-frecuencia-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mensual">Mensual</SelectItem>
                        <SelectItem value="quincenal">Quincenal</SelectItem>
                        <SelectItem value="trimestral">Trimestral</SelectItem>
                        <SelectItem value="semestral">Semestral</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Fecha de Inicio</Label>
                    <Input type="date" value={seguroForm.fecha_inicio} onChange={e => setSeguroForm({ ...seguroForm, fecha_inicio: e.target.value })} data-testid="seguro-fecha-inicio-input" />
                    <p className="text-xs text-slate-400 mt-1">A partir de esta fecha se incluye el descuento en la nómina</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Costo Titular (RD$)</Label>
                    <Input type="number" value={seguroForm.costo_titular} onChange={e => updateSeguroCostos({ costo_titular: parseFloat(e.target.value) || 0 })} data-testid="seguro-costo-titular-input" />
                    <label className="flex items-center gap-2 mt-2 cursor-pointer">
                      <Checkbox checked={seguroForm.titular_cubierto_empresa} onCheckedChange={v => updateSeguroCostos({ titular_cubierto_empresa: !!v })} data-testid="seguro-titular-cubierto-check" />
                      <span className="text-xs text-slate-600 dark:text-slate-400">Cubierto por la empresa</span>
                    </label>
                  </div>
                  <div>
                    <Label>Cant. Dependientes</Label>
                    <Input type="number" min="0" value={seguroForm.cantidad_dependientes} onChange={e => updateSeguroCostos({ cantidad_dependientes: parseInt(e.target.value) || 0 })} data-testid="seguro-cant-dependientes-input" />
                    <label className="flex items-center gap-2 mt-2 cursor-pointer">
                      <Checkbox checked={seguroForm.dependientes_cubierto_empresa} onCheckedChange={v => updateSeguroCostos({ dependientes_cubierto_empresa: !!v })} data-testid="seguro-dependientes-cubierto-check" />
                      <span className="text-xs text-slate-600 dark:text-slate-400">Cubierto por la empresa</span>
                    </label>
                  </div>
                  <div>
                    <Label>Costo por Dependiente (RD$)</Label>
                    <Input type="number" value={seguroForm.costo_dependiente} onChange={e => updateSeguroCostos({ costo_dependiente: parseFloat(e.target.value) || 0 })} data-testid="seguro-costo-dependiente-input" />
                  </div>
                </div>
                <div>
                  <Label>Monto a Pagar (RD$) *</Label>
                  <Input type="number" value={seguroForm.monto} onChange={e => setSeguroForm({ ...seguroForm, monto: parseFloat(e.target.value) || 0 })} data-testid="seguro-monto-input" />
                  <p className="text-xs text-slate-400 mt-1">Se calcula automáticamente excluyendo los costos cubiertos por la empresa. Puede ajustarlo manualmente.</p>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600">
                  <div>
                    <Label className="font-medium">Aplica descuento en nómina</Label>
                    <p className="text-xs text-slate-500">Desactivado = la empresa paga el 100% del beneficio</p>
                  </div>
                  <Switch checked={seguroForm.aplica_descuento} onCheckedChange={v => setSeguroForm({ ...seguroForm, aplica_descuento: v, pct_empresa: v ? seguroForm.pct_empresa : 100, pct_empleado: v ? seguroForm.pct_empleado : 0 })} data-testid="seguro-aplica-descuento-switch" />
                </div>
                {seguroForm.aplica_descuento && (
                  <div className="grid grid-cols-2 gap-4 p-3 rounded-lg border border-amber-200 bg-amber-50/50 dark:bg-amber-900/10 dark:border-amber-800">
                    <div>
                      <Label>% que paga la Empresa</Label>
                      <Input type="number" min="0" max="100" value={seguroForm.pct_empresa} onChange={e => { const v = Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)); setSeguroForm({ ...seguroForm, pct_empresa: v, pct_empleado: Math.round((100 - v) * 100) / 100 }); }} data-testid="seguro-pct-empresa-input" />
                    </div>
                    <div>
                      <Label>% descuento al Empleado</Label>
                      <Input type="number" min="0" max="100" value={seguroForm.pct_empleado} onChange={e => { const v = Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)); setSeguroForm({ ...seguroForm, pct_empleado: v, pct_empresa: Math.round((100 - v) * 100) / 100 }); }} data-testid="seguro-pct-empleado-input" />
                      <p className="text-xs text-amber-700 dark:text-amber-400 mt-1 font-mono">Descuento: {formatCurrency((seguroForm.monto || 0) * ((seguroForm.pct_empleado || 0) / 100))}</p>
                    </div>
                  </div>
                )}
                <div>
                  <Label>Observaciones</Label>
                  <Textarea value={seguroForm.observaciones} onChange={e => setSeguroForm({ ...seguroForm, observaciones: e.target.value })} rows={2} />
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600">
                  <Label className="font-medium">Beneficio activo</Label>
                  <Switch checked={seguroForm.activo} onCheckedChange={v => setSeguroForm({ ...seguroForm, activo: v })} data-testid="seguro-activo-switch" />
                </div>
              </div>
            </ScrollArea>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSeguroDialog(false)}>Cancelar</Button>
              <Button className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white" onClick={handleSaveSeguro} disabled={saving} data-testid="guardar-seguro-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} {seguroEditMode ? "Actualizar" : "Guardar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dependiente Form Dialog */}
        <Dialog open={showDependienteDialog} onOpenChange={(o) => { setShowDependienteDialog(o); if (!o) resetDependienteForm(); }}>
          <DialogContent className="max-w-lg" data-testid="dependiente-dialog">
            <DialogHeader>
              <DialogTitle>{dependienteEditMode ? "Editar Dependiente Adicional" : "Nuevo Dependiente Adicional"}</DialogTitle>
              <DialogDescription>Dependiente adicional de TSS vinculado a un empleado. Cápita actual: {formatCurrency(capitaDependiente)}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Empleado *</Label>
                <Select value={dependienteForm.user_id} onValueChange={v => setDependienteForm({ ...dependienteForm, user_id: v })}>
                  <SelectTrigger data-testid="dependiente-empleado-select"><SelectValue placeholder="Seleccionar empleado" /></SelectTrigger>
                  <SelectContent>
                    {empleadosNomina.map(e => <SelectItem key={e.user_id} value={e.user_id}>{e.codigo_empleado ? `[${e.codigo_empleado}] ` : ""}{e.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nombre del Dependiente *</Label>
                  <Input value={dependienteForm.nombre} onChange={e => setDependienteForm({ ...dependienteForm, nombre: e.target.value })} placeholder="Nombre completo" data-testid="dependiente-nombre-input" />
                </div>
                <div>
                  <Label>Parentesco *</Label>
                  <Select value={dependienteForm.parentesco} onValueChange={v => setDependienteForm({ ...dependienteForm, parentesco: v })}>
                    <SelectTrigger data-testid="dependiente-parentesco-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="padre">Padre</SelectItem>
                      <SelectItem value="madre">Madre</SelectItem>
                      <SelectItem value="suegra">Suegra</SelectItem>
                      <SelectItem value="suegro">Suegro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Observaciones</Label>
                <Textarea value={dependienteForm.observaciones} onChange={e => setDependienteForm({ ...dependienteForm, observaciones: e.target.value })} rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDependienteDialog(false)}>Cancelar</Button>
              <Button className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white" onClick={handleSaveDependiente} disabled={saving} data-testid="guardar-dependiente-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} {dependienteEditMode ? "Actualizar" : "Guardar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Empleado Form Dialog */}
        <Dialog open={showEmpleadoDialog} onOpenChange={setShowEmpleadoDialog}>
          <DialogContent className="max-w-2xl dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle>{editMode ? "Editar Empleado en Nómina" : "Agregar Empleado a Nómina"}</DialogTitle>
            </DialogHeader>
            <ScrollArea className="max-h-[70vh]">
              <div className="space-y-4 pr-4">
                {!editMode && (
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600">
                    <div>
                      <Label className="font-medium">Empleado externo</Label>
                      <p className="text-xs text-slate-500">No es usuario del sistema — ingresa sus datos manualmente</p>
                    </div>
                    <Switch
                      checked={empleadoForm.es_externo}
                      onCheckedChange={v => setEmpleadoForm({ ...empleadoForm, es_externo: v, user_id: "" })}
                      data-testid="empleado-externo-switch"
                    />
                  </div>
                )}
                {empleadoForm.es_externo || (editMode && empleadoForm.es_externo) ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Nombre *</Label>
                      <Input value={empleadoForm.nombre} onChange={e => setEmpleadoForm({...empleadoForm, nombre: e.target.value})} placeholder="Nombre completo" data-testid="empleado-nombre-input" />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input type="email" value={empleadoForm.email} onChange={e => setEmpleadoForm({...empleadoForm, email: e.target.value})} placeholder="correo@ejemplo.com" data-testid="empleado-email-input" />
                    </div>
                  </div>
                ) : editMode ? (
                  <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600">
                    <p className="font-semibold text-[#0C314C] dark:text-white">{empleadoForm.nombre}</p>
                    <p className="text-xs text-slate-500">{empleadoForm.email}</p>
                  </div>
                ) : (
                  <div>
                    <Label>Empleado *</Label>
                    <Select 
                      value={empleadoForm.user_id} 
                      onValueChange={v => setEmpleadoForm({...empleadoForm, user_id: v})}
                    >
                      <SelectTrigger data-testid="empleado-user-select"><SelectValue placeholder="Seleccionar empleado" /></SelectTrigger>
                      <SelectContent>
                        {usuarios.map(u => <SelectItem key={u.id} value={u.id}>{u.name} - {u.email}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Código de Empleado</Label>
                    <Input value={empleadoForm.codigo_empleado} onChange={e => setEmpleadoForm({...empleadoForm, codigo_empleado: e.target.value})} placeholder="Ej: EMP-001" data-testid="empleado-codigo-input" />
                  </div>
                  <div>
                    <Label>Salario Bruto (RD$) *</Label>
                    <Input type="number" value={empleadoForm.salario_bruto} onChange={e => setEmpleadoForm({...empleadoForm, salario_bruto: parseFloat(e.target.value) || 0})} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Tipo de Contrato</Label>
                    <Select value={empleadoForm.tipo_contrato} onValueChange={v => setEmpleadoForm({...empleadoForm, tipo_contrato: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fijo">Fijo</SelectItem>
                        <SelectItem value="temporal">Temporal</SelectItem>
                        <SelectItem value="por_hora">Por Hora</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Tipo de Salario</Label>
                    <Select value={empleadoForm.tipo_salario} onValueChange={v => setEmpleadoForm({...empleadoForm, tipo_salario: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mensual">Mensual</SelectItem>
                        <SelectItem value="quincenal">Quincenal</SelectItem>
                        <SelectItem value="semanal">Semanal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Departamento</Label>
                    <Select value={empleadoForm.departamento_id || "ninguno"} onValueChange={v => setEmpleadoForm({...empleadoForm, departamento_id: v === "ninguno" ? "" : v})}>
                      <SelectTrigger data-testid="empleado-departamento-select"><SelectValue placeholder="Seleccionar departamento" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ninguno">Sin departamento</SelectItem>
                        {departamentos.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Cargo</Label>
                    <Select value={empleadoForm.cargo_id || "ninguno"} onValueChange={v => setEmpleadoForm({...empleadoForm, cargo_id: v === "ninguno" ? "" : v})}>
                      <SelectTrigger data-testid="empleado-cargo-select"><SelectValue placeholder="Seleccionar cargo" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ninguno">Sin cargo</SelectItem>
                        {cargos.map(c => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Fecha de Ingreso</Label>
                    <Input type="date" value={empleadoForm.fecha_ingreso} onChange={e => setEmpleadoForm({...empleadoForm, fecha_ingreso: e.target.value})} />
                  </div>
                  <div>
                    <Label>Fecha de Nacimiento</Label>
                    <Input type="date" value={empleadoForm.fecha_nacimiento} onChange={e => setEmpleadoForm({...empleadoForm, fecha_nacimiento: e.target.value})} data-testid="empleado-fecha-nacimiento-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Estado Civil</Label>
                    <Select value={empleadoForm.estado_civil || "no_especificado"} onValueChange={v => setEmpleadoForm({...empleadoForm, estado_civil: v === "no_especificado" ? "" : v})}>
                      <SelectTrigger data-testid="empleado-estado-civil-select"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="no_especificado">No especificado</SelectItem>
                        <SelectItem value="soltero">Soltero/a</SelectItem>
                        <SelectItem value="casado">Casado/a</SelectItem>
                        <SelectItem value="union_libre">Unión Libre</SelectItem>
                        <SelectItem value="divorciado">Divorciado/a</SelectItem>
                        <SelectItem value="viudo">Viudo/a</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {(empleadoForm.estado_civil === "casado" || empleadoForm.estado_civil === "union_libre") && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>{empleadoForm.estado_civil === "casado" ? "Cónyuge" : "Compañero(a) de Vida"}</Label>
                      <Input value={empleadoForm.conyuge_nombre} onChange={e => setEmpleadoForm({...empleadoForm, conyuge_nombre: e.target.value})} placeholder="Nombre completo" data-testid="empleado-conyuge-input" />
                    </div>
                    <div>
                      <Label>Fecha de Nacimiento</Label>
                      <Input type="date" value={empleadoForm.conyuge_fecha_nacimiento} onChange={e => setEmpleadoForm({...empleadoForm, conyuge_fecha_nacimiento: e.target.value})} data-testid="empleado-conyuge-fecha-input" />
                    </div>
                  </div>
                )}

                {/* Información Personal */}
                <div className="border-t pt-4 mt-4" data-testid="empleado-info-personal-section">
                  <p className="font-medium mb-3">Información Personal</p>

                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <Label>Hijos ({(empleadoForm.hijos || []).length})</Label>
                      <Button type="button" size="sm" variant="outline" onClick={() => setEmpleadoForm({...empleadoForm, hijos: [...(empleadoForm.hijos || []), { nombre: "", edad: "", estudia: false }]})} data-testid="agregar-hijo-btn">
                        <Plus className="w-3.5 h-3.5 mr-1" /> Agregar hijo
                      </Button>
                    </div>
                    {(empleadoForm.hijos || []).map((h, i) => (
                      <div key={i} className="flex items-center gap-2 mb-2" data-testid={`hijo-row-${i}`}>
                        <Input className="flex-1" placeholder="Nombre" value={h.nombre} onChange={e => {
                          const hijos = [...empleadoForm.hijos]; hijos[i] = { ...hijos[i], nombre: e.target.value };
                          setEmpleadoForm({ ...empleadoForm, hijos });
                        }} data-testid={`hijo-nombre-${i}`} />
                        <Input className="w-40" type="date" title="Fecha de nacimiento" value={h.fecha_nacimiento || ""} onChange={e => {
                          const hijos = [...empleadoForm.hijos];
                          const fn = e.target.value;
                          let edad = hijos[i].edad;
                          if (fn) {
                            const d = new Date(fn); const hoy = new Date();
                            edad = hoy.getFullYear() - d.getFullYear() - ((hoy.getMonth() < d.getMonth() || (hoy.getMonth() === d.getMonth() && hoy.getDate() < d.getDate())) ? 1 : 0);
                            if (edad < 0) edad = 0;
                          }
                          hijos[i] = { ...hijos[i], fecha_nacimiento: fn, edad };
                          setEmpleadoForm({ ...empleadoForm, hijos });
                        }} data-testid={`hijo-fecha-${i}`} />
                        <Input className="w-20" type="number" min={0} placeholder="Edad" value={h.edad} onChange={e => {
                          const hijos = [...empleadoForm.hijos]; hijos[i] = { ...hijos[i], edad: e.target.value === "" ? "" : parseInt(e.target.value) || 0 };
                          setEmpleadoForm({ ...empleadoForm, hijos });
                        }} data-testid={`hijo-edad-${i}`} />
                        <label className="flex items-center gap-1.5 text-sm whitespace-nowrap cursor-pointer">
                          <Checkbox checked={!!h.estudia} onCheckedChange={v => {
                            const hijos = [...empleadoForm.hijos]; hijos[i] = { ...hijos[i], estudia: !!v };
                            setEmpleadoForm({ ...empleadoForm, hijos });
                          }} data-testid={`hijo-estudia-${i}`} /> Estudia
                        </label>
                        <Button type="button" variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => setEmpleadoForm({ ...empleadoForm, hijos: empleadoForm.hijos.filter((_, j) => j !== i) })} data-testid={`eliminar-hijo-${i}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>

                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <Label>Contactos de Emergencia ({(empleadoForm.contactos_emergencia || []).length})</Label>
                      <Button type="button" size="sm" variant="outline" onClick={() => setEmpleadoForm({...empleadoForm, contactos_emergencia: [...(empleadoForm.contactos_emergencia || []), { nombre: "", parentesco: "", telefono: "" }]})} data-testid="agregar-contacto-btn">
                        <Plus className="w-3.5 h-3.5 mr-1" /> Agregar contacto
                      </Button>
                    </div>
                    {(empleadoForm.contactos_emergencia || []).map((c, i) => (
                      <div key={i} className="flex items-center gap-2 mb-2" data-testid={`contacto-row-${i}`}>
                        <Input className="flex-1" placeholder="Nombre" value={c.nombre} onChange={e => {
                          const contactos = [...empleadoForm.contactos_emergencia]; contactos[i] = { ...contactos[i], nombre: e.target.value };
                          setEmpleadoForm({ ...empleadoForm, contactos_emergencia: contactos });
                        }} data-testid={`contacto-nombre-${i}`} />
                        <Input className="w-32" placeholder="Parentesco" value={c.parentesco} onChange={e => {
                          const contactos = [...empleadoForm.contactos_emergencia]; contactos[i] = { ...contactos[i], parentesco: e.target.value };
                          setEmpleadoForm({ ...empleadoForm, contactos_emergencia: contactos });
                        }} data-testid={`contacto-parentesco-${i}`} />
                        <Input className="w-36" placeholder="Teléfono" value={c.telefono} onChange={e => {
                          const contactos = [...empleadoForm.contactos_emergencia]; contactos[i] = { ...contactos[i], telefono: e.target.value };
                          setEmpleadoForm({ ...empleadoForm, contactos_emergencia: contactos });
                        }} data-testid={`contacto-telefono-${i}`} />
                        <Button type="button" variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => setEmpleadoForm({ ...empleadoForm, contactos_emergencia: empleadoForm.contactos_emergencia.filter((_, j) => j !== i) })} data-testid={`eliminar-contacto-${i}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>

                  <div>
                    <Label>Otros Datos de Interés</Label>
                    <Textarea value={empleadoForm.info_adicional} onChange={e => setEmpleadoForm({...empleadoForm, info_adicional: e.target.value})} placeholder="Alergias, condiciones médicas, hobbies, fechas importantes, etc." data-testid="empleado-info-adicional-input" />
                  </div>
                </div>
                
                <div className="border-t pt-4 mt-4">
                  <p className="font-medium mb-3">Datos Bancarios</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Banco</Label>
                      <Select value={empleadoForm.banco} onValueChange={v => setEmpleadoForm({...empleadoForm, banco: v})}>
                        <SelectTrigger><SelectValue placeholder="Seleccionar banco" /></SelectTrigger>
                        <SelectContent>
                          {bancosRD.map(b => <SelectItem key={b.id} value={b.nombre}>{b.nombre}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Tipo de Cuenta</Label>
                      <Select value={empleadoForm.tipo_cuenta} onValueChange={v => setEmpleadoForm({...empleadoForm, tipo_cuenta: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ahorro">Ahorro</SelectItem>
                          <SelectItem value="corriente">Corriente</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="mt-4">
                    <Label>Número de Cuenta</Label>
                    <Input value={empleadoForm.cuenta_banco} onChange={e => setEmpleadoForm({...empleadoForm, cuenta_banco: e.target.value})} placeholder="Número de cuenta bancaria" />
                  </div>
                </div>
                
                <div className="border-t pt-4 mt-4">
                  <p className="font-medium mb-3">Ingresos Adicionales</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Comisiones (RD$)</Label>
                      <Input type="number" value={empleadoForm.comisiones} onChange={e => setEmpleadoForm({...empleadoForm, comisiones: parseFloat(e.target.value) || 0})} />
                    </div>
                    <div>
                      <Label>Bonos (RD$)</Label>
                      <Input type="number" value={empleadoForm.bonos} onChange={e => setEmpleadoForm({...empleadoForm, bonos: parseFloat(e.target.value) || 0})} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <Label>Horas Extra</Label>
                      <Input type="number" value={empleadoForm.horas_extra} onChange={e => setEmpleadoForm({...empleadoForm, horas_extra: parseInt(e.target.value) || 0})} />
                    </div>
                    <div>
                      <Label>Valor Hora Extra (RD$)</Label>
                      <Input type="number" value={empleadoForm.valor_hora_extra} onChange={e => setEmpleadoForm({...empleadoForm, valor_hora_extra: parseFloat(e.target.value) || 0})} />
                    </div>
                  </div>
                </div>
                
                <div className="border-t pt-4 mt-4">
                  <p className="font-medium mb-3">Otras Deducciones</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Monto (RD$)</Label>
                      <Input type="number" value={empleadoForm.otras_deducciones} onChange={e => setEmpleadoForm({...empleadoForm, otras_deducciones: parseFloat(e.target.value) || 0})} />
                    </div>
                    <div>
                      <Label>Descripción</Label>
                      <Input value={empleadoForm.descripcion_otras_deducciones} onChange={e => setEmpleadoForm({...empleadoForm, descripcion_otras_deducciones: e.target.value})} placeholder="Descripción de la deducción" />
                    </div>
                  </div>
                </div>
              </div>
            </ScrollArea>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEmpleadoDialog(false)}>Cancelar</Button>
              <Button onClick={handleSaveEmpleado} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />} {editMode ? "Actualizar" : "Guardar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Empleado Detail Dialog */}
        <Dialog open={showEmpleadoDetailDialog} onOpenChange={setShowEmpleadoDetailDialog}>
          <DialogContent className="max-w-2xl dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle>Detalles del Empleado</DialogTitle>
            </DialogHeader>
            {viewingEmpleado && (
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <div className="w-14 h-14 rounded-full bg-[#0C314C] flex items-center justify-center">
                    <span className="text-xl font-semibold text-white">{viewingEmpleado.nombre?.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="font-medium text-lg">{viewingEmpleado.nombre}</p>
                    <p className="text-sm text-slate-500">
                      {viewingEmpleado.codigo_empleado && <span className="font-mono text-[#3F6212] dark:text-[#45E69A] mr-1.5">[{viewingEmpleado.codigo_empleado}]</span>}
                      {viewingEmpleado.email}
                    </p>
                  </div>
                </div>

                <Tabs defaultValue="general">
                  <TabsList className="w-full grid grid-cols-3">
                    <TabsTrigger value="general" data-testid="emp-detail-tab-general">General</TabsTrigger>
                    <TabsTrigger value="personal" data-testid="emp-detail-tab-personal">Personal</TabsTrigger>
                    <TabsTrigger value="beneficios" data-testid="emp-detail-tab-beneficios">Beneficios y Dependientes</TabsTrigger>
                  </TabsList>

                  <TabsContent value="general" className="space-y-4 pt-3">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-slate-500">Código de Empleado</p>
                        <p className="font-medium font-mono" data-testid="detalle-codigo-empleado">{viewingEmpleado.codigo_empleado || "-"}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Salario Bruto</p>
                        <p className="font-medium text-green-600">{formatCurrency(viewingEmpleado.salario_bruto)}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Tipo de Contrato</p>
                        <p className="font-medium">{viewingEmpleado.tipo_contrato}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Tipo de Salario</p>
                        <p className="font-medium">{viewingEmpleado.tipo_salario}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Fecha de Ingreso</p>
                        <p className="font-medium">{viewingEmpleado.fecha_ingreso || "-"}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Departamento</p>
                        <p className="font-medium" data-testid="detalle-departamento">{viewingEmpleado.departamento_nombre || "-"}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Cargo</p>
                        <p className="font-medium" data-testid="detalle-cargo">{viewingEmpleado.cargo_nombre || "-"}</p>
                      </div>
                    </div>

                    <div className="border-t pt-4">
                      <p className="font-medium mb-2">Datos Bancarios</p>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-slate-500">Banco</p>
                          <p className="font-medium">{viewingEmpleado.banco || "-"}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Cuenta</p>
                          <p className="font-medium">{viewingEmpleado.cuenta_banco || "-"}</p>
                        </div>
                      </div>
                    </div>

                    {(viewingEmpleado.comisiones > 0 || viewingEmpleado.bonos > 0) && (
                      <div className="border-t pt-4">
                        <p className="font-medium mb-2">Ingresos Adicionales</p>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-slate-500">Comisiones</p>
                            <p className="font-medium">{formatCurrency(viewingEmpleado.comisiones)}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Bonos</p>
                            <p className="font-medium">{formatCurrency(viewingEmpleado.bonos)}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="personal" className="space-y-4 pt-3" data-testid="detalle-personal-tab">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-slate-500">Estado Civil</p>
                        <p className="font-medium capitalize" data-testid="detalle-estado-civil">{(viewingEmpleado.estado_civil || "").replace("_", " ") || "-"}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Fecha de Nacimiento</p>
                        <p className="font-medium" data-testid="detalle-fecha-nacimiento">{viewingEmpleado.fecha_nacimiento || "-"}</p>
                      </div>
                      {(viewingEmpleado.estado_civil === "casado" || viewingEmpleado.estado_civil === "union_libre") && (
                        <div>
                          <p className="text-slate-500">{viewingEmpleado.estado_civil === "casado" ? "Cónyuge" : "Compañero(a) de Vida"}</p>
                          <p className="font-medium" data-testid="detalle-conyuge">{viewingEmpleado.conyuge_nombre || "-"}{viewingEmpleado.conyuge_fecha_nacimiento ? ` · ${viewingEmpleado.conyuge_fecha_nacimiento}` : ""}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-slate-500">Hijos</p>
                        <p className="font-medium">{(viewingEmpleado.hijos || []).length}</p>
                      </div>
                    </div>

                    {(viewingEmpleado.hijos || []).length > 0 && (
                      <div className="border-t pt-4">
                        <p className="font-medium mb-2">Hijos</p>
                        <div className="space-y-1.5">
                          {viewingEmpleado.hijos.map((h, i) => (
                            <div key={i} className="flex items-center justify-between text-sm bg-slate-50 dark:bg-slate-700/40 rounded-lg px-3 py-2">
                              <span className="font-medium">{h.nombre || `Hijo ${i + 1}`}</span>
                              <span className="text-slate-500">{h.fecha_nacimiento ? h.fecha_nacimiento : (h.edad !== "" && h.edad !== undefined ? `${h.edad} años` : "-")}{h.fecha_nacimiento && h.edad !== "" && h.edad !== undefined ? ` (${h.edad} años)` : ""}</span>
                              <Badge className={h.estudia ? "bg-[#45E69A]/20 text-[#3F6212] border border-[#45E69A]/40 rounded-full" : "bg-slate-100 text-slate-500 border border-slate-200 rounded-full"}>
                                {h.estudia ? "Estudia" : "No estudia"}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="border-t pt-4">
                      <p className="font-medium mb-2">Contactos de Emergencia</p>
                      {(viewingEmpleado.contactos_emergencia || []).length === 0 ? (
                        <p className="text-sm text-slate-400">No hay contactos registrados</p>
                      ) : (
                        <div className="space-y-1.5">
                          {viewingEmpleado.contactos_emergencia.map((c, i) => (
                            <div key={i} className="flex items-center justify-between text-sm bg-slate-50 dark:bg-slate-700/40 rounded-lg px-3 py-2">
                              <span className="font-medium">{c.nombre || "-"}</span>
                              <span className="text-slate-500">{c.parentesco || "-"}</span>
                              <span className="font-mono">{c.telefono || "-"}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {viewingEmpleado.info_adicional && (
                      <div className="border-t pt-4">
                        <p className="font-medium mb-2">Otros Datos de Interés</p>
                        <p className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap">{viewingEmpleado.info_adicional}</p>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="beneficios" className="pt-3">
                    <ScrollArea className="max-h-[45vh]">
                      <div className="space-y-4 pr-3">
                        {/* Otros Beneficios */}
                        <div data-testid="detalle-beneficios-section">
                          <p className="font-medium mb-2 flex items-center gap-2"><HeartPulse className="w-4 h-4 text-[#0C314C]" /> Otros Beneficios</p>
                          {segurosDeEmpleado(viewingEmpleado.user_id).length === 0 ? (
                            <p className="text-sm text-slate-400">No tiene beneficios registrados</p>
                          ) : (
                            <div className="space-y-2">
                              {segurosDeEmpleado(viewingEmpleado.user_id).map(s => (
                                <div key={s.id} className="p-3 rounded-lg bg-slate-50 dark:bg-slate-700/50 border border-slate-100 dark:border-slate-600">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <p className="text-sm font-medium">{s.tipo_seguro}</p>
                                      <p className="text-xs text-slate-500 capitalize">{s.frecuencia} · {s.cantidad_dependientes || 0} dependiente(s){s.fecha_inicio ? ` · desde ${s.fecha_inicio.split("-").reverse().join("/")}` : ""}{s.activo === false ? " · Inactivo" : ""}</p>
                                    </div>
                                    <span className="font-mono text-sm font-semibold">{formatCurrency(s.monto)}</span>
                                  </div>
                                  <div className="flex items-center justify-between mt-1.5">
                                    {s.aplica_descuento ? (
                                      <>
                                        <span className="text-xs text-slate-500">Empresa {s.pct_empresa ?? 100}% · Empleado {s.pct_empleado || 0}%</span>
                                        <span className="font-mono text-xs font-semibold text-red-600">Descuento: -{formatCurrency(descuentoEmpleadoSeguro(s))}</span>
                                      </>
                                    ) : (
                                      <Badge className="bg-[#45E69A]/20 text-[#3F6212] border border-[#45E69A]/40 rounded-full text-[10px] font-semibold">Empresa paga 100%</Badge>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Dependientes Adicionales TSS */}
                        <div className="border-t pt-3" data-testid="detalle-dependientes-section">
                          <p className="font-medium mb-2 flex items-center gap-2"><UsersRound className="w-4 h-4 text-[#0C314C]" /> Dependientes Adicionales (TSS)</p>
                          {dependientesDeEmpleado(viewingEmpleado.user_id).length === 0 ? (
                            <p className="text-sm text-slate-400">No tiene dependientes adicionales registrados</p>
                          ) : (
                            <div className="space-y-2">
                              {dependientesDeEmpleado(viewingEmpleado.user_id).map(d => (
                                <div key={d.id} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50 dark:bg-slate-700/50 border border-slate-100 dark:border-slate-600">
                                  <div>
                                    <p className="text-sm font-medium">{d.nombre}</p>
                                    <p className="text-xs text-slate-500 capitalize">{d.parentesco}</p>
                                  </div>
                                  <span className="font-mono text-sm font-semibold text-red-600">-{formatCurrency(capitaDependiente)}</span>
                                </div>
                              ))}
                              <div className="flex justify-between items-center px-2.5 pt-1 text-sm">
                                <span className="text-slate-500">Subtotal TSS ({dependientesDeEmpleado(viewingEmpleado.user_id).length} dependiente(s))</span>
                                <span className="font-mono font-bold text-red-600" data-testid="detalle-dependientes-total">-{formatCurrency(dependientesDeEmpleado(viewingEmpleado.user_id).length * capitaDependiente)}</span>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Resumen */}
                        <div className="border-t pt-3 flex justify-between items-center p-3 rounded-lg bg-[#0C314C]/5 dark:bg-slate-700/60">
                          <span className="text-sm font-semibold text-[#0C314C] dark:text-white">Descuento total (beneficios + dependientes)</span>
                          <span className="font-mono font-bold text-red-600" data-testid="detalle-descuento-total">
                            -{formatCurrency(
                              segurosDeEmpleado(viewingEmpleado.user_id).filter(s => s.activo !== false && s.aplica_descuento).reduce((acc, s) => acc + descuentoEmpleadoSeguro(s), 0)
                              + dependientesDeEmpleado(viewingEmpleado.user_id).length * capitaDependiente
                            )}
                          </span>
                        </div>
                      </div>
                    </ScrollArea>
                  </TabsContent>
                </Tabs>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEmpleadoDetailDialog(false)}>Cerrar</Button>
              <Button onClick={() => { setShowEmpleadoDetailDialog(false); openEditEmpleado(viewingEmpleado); }}>
                <Edit className="w-4 h-4 mr-2" /> Editar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Préstamo Dialog - Updated */}
        <Dialog open={showPrestamoDialog} onOpenChange={setShowPrestamoDialog}>
          <DialogContent className="dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle>{prestamoEditMode ? "Editar Préstamo" : "Nuevo Préstamo"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Empleado *</Label>
                <Select value={prestamoForm.user_id} onValueChange={v => setPrestamoForm({...prestamoForm, user_id: v})} disabled={prestamoEditMode}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>
                    {usuarios.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Monto Total (RD$) *</Label>
                  <Input type="number" value={prestamoForm.monto_total} onChange={e => {
                    const monto = parseFloat(e.target.value) || 0;
                    setPrestamoForm({...prestamoForm, monto_total: monto, cuota: calcCuotaPrestamo(monto, prestamoForm.plazo, prestamoForm.tasa_interes)});
                  }} />
                </div>
                <div>
                  <Label>Tipo de Cuota *</Label>
                  <Select value={prestamoForm.tipo_cuota} onValueChange={v => setPrestamoForm({...prestamoForm, tipo_cuota: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="quincenal">Quincenal</SelectItem>
                      <SelectItem value="mensual">Mensual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Plazo ({prestamoForm.tipo_cuota === "quincenal" ? "quincenas" : "meses"})</Label>
                  <Input type="number" value={prestamoForm.plazo} onChange={e => {
                    const plazo = parseInt(e.target.value) || 12;
                    setPrestamoForm({...prestamoForm, plazo, cuota: calcCuotaPrestamo(prestamoForm.monto_total, plazo, prestamoForm.tasa_interes)});
                  }} />
                </div>
                <div>
                  <Label>Monto por Cuota (RD$) *</Label>
                  <Input 
                    type="number" 
                    value={prestamoForm.cuota} 
                    onChange={e => setPrestamoForm({...prestamoForm, cuota: parseFloat(e.target.value) || 0})}
                    placeholder="Se calcula automáticamente"
                  />
                  <p className="text-[11px] text-slate-400 mt-1">Calculada automáticamente (Monto ÷ Plazo, ajustable)</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Fecha Inicio</Label>
                  <Input type="date" value={prestamoForm.fecha_inicio} onChange={e => setPrestamoForm({...prestamoForm, fecha_inicio: e.target.value})} />
                </div>
                <div>
                  <Label>Tasa de Interés (%)</Label>
                  <Input type="number" step="0.01" value={prestamoForm.tasa_interes} onChange={e => {
                    const tasa = parseFloat(e.target.value) || 0;
                    setPrestamoForm({...prestamoForm, tasa_interes: tasa, cuota: calcCuotaPrestamo(prestamoForm.monto_total, prestamoForm.plazo, tasa)});
                  }} />
                </div>
              </div>
              <div>
                <Label>Motivo</Label>
                <Textarea value={prestamoForm.motivo} onChange={e => setPrestamoForm({...prestamoForm, motivo: e.target.value})} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPrestamoDialog(false)}>Cancelar</Button>
              <Button onClick={handleSavePrestamo} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />} {prestamoEditMode ? "Actualizar" : "Crear"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Préstamo Detail Dialog */}
        <Dialog open={showPrestamoDetailDialog} onOpenChange={setShowPrestamoDetailDialog}>
          <DialogContent className="dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle>Detalles del Préstamo</DialogTitle>
            </DialogHeader>
            {viewingPrestamo && (
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <PiggyBank className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-medium text-lg">{viewingPrestamo.nombre_empleado}</p>
                    <p className="text-sm text-slate-500">{viewingPrestamo.motivo}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Monto Total</p>
                    <p className="font-medium text-lg">{formatCurrency(viewingPrestamo.monto_total)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Cuota</p>
                    <p className="font-medium text-lg">{formatCurrency(viewingPrestamo.cuota_mensual)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Monto Pagado</p>
                    <p className="font-medium text-green-600">{formatCurrency(viewingPrestamo.monto_pagado)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Saldo Pendiente</p>
                    <p className="font-medium text-red-600">{formatCurrency(viewingPrestamo.saldo_pendiente)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Plazo</p>
                    <p className="font-medium">{viewingPrestamo.plazo_meses} meses</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Estado</p>
                    {getEstadoBadge(viewingPrestamo.estado)}
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPrestamoDetailDialog(false)}>Cerrar</Button>
              <Button onClick={() => { setShowPrestamoDetailDialog(false); openEditPrestamo(viewingPrestamo); }}>
                <Edit className="w-4 h-4 mr-2" /> Editar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Avance Dialog - Updated */}
        <Dialog open={showAvanceDialog} onOpenChange={setShowAvanceDialog}>
          <DialogContent className="dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle>Nuevo Avance de Salario</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Empleado *</Label>
                <Select value={avanceForm.user_id} onValueChange={v => setAvanceForm({...avanceForm, user_id: v})} disabled={avanceEditMode}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>
                    {usuarios.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Monto Total (RD$) *</Label>
                  <Input type="number" value={avanceForm.monto} onChange={e => setAvanceForm({...avanceForm, monto: parseFloat(e.target.value) || 0})} />
                </div>
                <div>
                  <Label>Tipo de Descuento *</Label>
                  <Select value={avanceForm.tipo_descuento} onValueChange={v => setAvanceForm({...avanceForm, tipo_descuento: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="quincenal">Quincenal</SelectItem>
                      <SelectItem value="mensual">Mensual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Número de Cuotas</Label>
                  <Input type="number" value={avanceForm.cuotas} onChange={e => setAvanceForm({...avanceForm, cuotas: parseInt(e.target.value) || 1})} min={1} />
                </div>
                <div>
                  <Label>Monto por Cuota (calculado)</Label>
                  <Input 
                    type="number" 
                    value={(avanceForm.monto / avanceForm.cuotas || 0).toFixed(2)} 
                    disabled
                    className="bg-slate-100 dark:bg-slate-700"
                  />
                </div>
              </div>
              <div>
                <Label>Fecha Inicio</Label>
                <Input type="date" value={avanceForm.fecha_inicio} onChange={e => setAvanceForm({...avanceForm, fecha_inicio: e.target.value})} />
              </div>
              <div>
                <Label>Motivo</Label>
                <Textarea value={avanceForm.motivo} onChange={e => setAvanceForm({...avanceForm, motivo: e.target.value})} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAvanceDialog(false)}>Cancelar</Button>
              <Button onClick={handleSaveAvance} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />} {avanceEditMode ? "Actualizar" : "Crear"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Avance Detail Dialog */}
        <Dialog open={showAvanceDetailDialog} onOpenChange={setShowAvanceDetailDialog}>
          <DialogContent className="dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle>Detalles del Avance</DialogTitle>
            </DialogHeader>
            {viewingAvance && (
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                    <Banknote className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium text-lg">{viewingAvance.nombre_empleado}</p>
                    <p className="text-sm text-slate-500">{viewingAvance.motivo || "Sin motivo especificado"}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Monto</p>
                    <p className="font-medium text-lg">{formatCurrency(viewingAvance.monto)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Tipo de Descuento</p>
                    <p className="font-medium capitalize">{viewingAvance.tipo_descuento}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Período de Descuento</p>
                    <p className="font-medium">{viewingAvance.periodo_descuento}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Estado</p>
                    {getEstadoBadge(viewingAvance.estado)}
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAvanceDetailDialog(false)}>Cerrar</Button>
              <Button onClick={() => { setShowAvanceDetailDialog(false); openEditAvance(viewingAvance); }}>
                <Edit className="w-4 h-4 mr-2" /> Editar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Calcular Nómina Dialog */}
        <Dialog open={showCalcularDialog} onOpenChange={setShowCalcularDialog}>
          <DialogContent className="dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle>Calcular Nómina</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Período *</Label>
                  <Input type="month" value={calcularForm.periodo} onChange={e => setCalcularForm({...calcularForm, periodo: e.target.value})} />
                </div>
                <div>
                  <Label>Fecha de Pago *</Label>
                  <Input type="date" value={calcularForm.fecha_pago} onChange={e => setCalcularForm({...calcularForm, fecha_pago: e.target.value})} />
                </div>
              </div>
              <div>
                <Label>Tipo de Período</Label>
                <Select value={calcularForm.tipo_periodo} onValueChange={v => setCalcularForm({...calcularForm, tipo_periodo: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mensual">Mensual</SelectItem>
                    <SelectItem value="quincenal">Quincenal</SelectItem>
                    <SelectItem value="semanal">Semanal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notas</Label>
                <Textarea value={calcularForm.notas} onChange={e => setCalcularForm({...calcularForm, notas: e.target.value})} placeholder="Notas adicionales..." />
              </div>

              {/* Descuentos manuales para esta nómina */}
              <div className="p-3 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50" data-testid="descuentos-manuales-section">
                <p className="font-medium text-sm text-[#0C314C] dark:text-white mb-1">Agregar descuentos manuales</p>
                <p className="text-xs text-slate-500 mb-3">Cree aquí un préstamo o avance desde cero. En esta nómina solo se cargará el primer descuento (la cuota o el avance del período); el resto queda pendiente para nóminas futuras.</p>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="outline" size="sm" className="border-[#0C314C]/20 text-[#0C314C] dark:text-slate-200 hover:bg-[#0C314C]/5" onClick={() => { resetPrestamoForm(); setShowPrestamoDialog(true); }} data-testid="calcular-add-prestamo-btn">
                    <PiggyBank className="w-4 h-4 mr-2" /> Crear Préstamo
                  </Button>
                  <Button type="button" variant="outline" size="sm" className="border-[#0C314C]/20 text-[#0C314C] dark:text-slate-200 hover:bg-[#0C314C]/5" onClick={() => { resetAvanceForm(); setShowAvanceDialog(true); }} data-testid="calcular-add-avance-btn">
                    <Banknote className="w-4 h-4 mr-2" /> Crear Avance
                  </Button>
                </div>
                <p className="text-xs text-slate-400 mt-2">Préstamos activos: {prestamosActivos.length} · Avances pendientes: {avancesPendientes.length} — se incluirán automáticamente al calcular.</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCalcularDialog(false)}>Cancelar</Button>
              <Button onClick={handleCalcularNomina} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                <Calculator className="w-4 h-4 mr-2" /> Calcular
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Nómina Detail Dialog */}
        <Dialog open={showNominaDetailDialog} onOpenChange={setShowNominaDetailDialog}>
          <DialogContent className="max-w-[95vw] xl:max-w-[1350px] dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span>Detalle de Nómina - {nominaActual?.periodo}</span>
                {nominaActual && getEstadoBadge(nominaActual.estado)}
              </DialogTitle>
            </DialogHeader>
            {nominaActual && (
              <div className="max-h-[60vh] overflow-y-auto">
                <div className="space-y-4 pr-4">
                  {/* Totals */}
                  <div className="grid grid-cols-4 gap-4">
                    <Card className="dark:bg-slate-700">
                      <CardContent className="pt-4">
                        <p className="text-sm text-slate-500">Total Bruto</p>
                        <p className="text-xl font-bold">{formatCurrency(nominaActual.totales?.total_bruto)}</p>
                      </CardContent>
                    </Card>
                    <Card className="dark:bg-slate-700">
                      <CardContent className="pt-4">
                        <p className="text-sm text-slate-500">Total Deducciones</p>
                        <p className="text-xl font-bold text-red-600">{formatCurrency(nominaActual.totales?.total_deducciones)}</p>
                      </CardContent>
                    </Card>
                    <Card className="dark:bg-slate-700">
                      <CardContent className="pt-4">
                        <p className="text-sm text-slate-500">Total Neto</p>
                        <p className="text-xl font-bold text-green-600">{formatCurrency(nominaActual.totales?.total_neto)}</p>
                      </CardContent>
                    </Card>
                    <Card className="dark:bg-slate-700">
                      <CardContent className="pt-4">
                        <p className="text-sm text-slate-500">TSS Empleador</p>
                        <p className="text-xl font-bold text-amber-600">{formatCurrency(nominaActual.totales?.total_tss_empleador)}</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Detail Table: scroll propio (vertical + horizontal visible) para que
                      las columnas de la derecha siempre sean alcanzables */}
                  <div className="overflow-auto max-h-[45vh] border border-slate-200 dark:border-slate-700 rounded" data-testid="nomina-detalle-tabla-scroll">
                    <table className="w-full text-sm min-w-max">
                      <thead className="bg-slate-100 dark:bg-slate-700 sticky top-0 z-10">
                        <tr>
                          <th className="px-2 py-2 text-left whitespace-nowrap">Empleado</th>
                          <th className="px-2 py-2 text-right whitespace-nowrap">Bruto</th>
                          <th className="px-2 py-2 text-right whitespace-nowrap">AFP</th>
                          <th className="px-2 py-2 text-right whitespace-nowrap">SFS</th>
                          <th className="px-2 py-2 text-right whitespace-nowrap">ISR</th>
                          <th className="px-2 py-2 text-right whitespace-nowrap">Préstamo</th>
                          <th className="px-2 py-2 text-right whitespace-nowrap">Avance</th>
                          {tiposBeneficioNomina(nominaActual).map(t => (
                            <th key={t} className="px-2 py-2 text-right whitespace-nowrap">{t}</th>
                          ))}
                          <th className="px-2 py-2 text-right whitespace-nowrap">Dep. TSS</th>
                          <th className="px-2 py-2 text-right whitespace-nowrap">Otros</th>
                          <th className="px-2 py-2 text-right font-bold whitespace-nowrap">Neto</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y dark:divide-slate-700">
                        {nominaActual.items?.map((item, idx) => (
                          <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                            <td className="px-2 py-2 font-medium whitespace-nowrap">{item.nombre}</td>
                            <td className="px-2 py-2 text-right whitespace-nowrap">{formatCurrency(item.total_ingresos)}</td>
                            <td className="px-2 py-2 text-right text-red-600 whitespace-nowrap">{formatCurrency(item.afp_empleado)}</td>
                            <td className="px-2 py-2 text-right text-red-600 whitespace-nowrap">{formatCurrency(item.sfs_empleado)}</td>
                            <td className="px-2 py-2 text-right text-red-600 whitespace-nowrap">{formatCurrency(item.isr)}</td>
                            <td className="px-2 py-2 text-right text-amber-600 whitespace-nowrap">{item.cuota_prestamo > 0 ? formatCurrency(item.cuota_prestamo) : '-'}</td>
                            <td className="px-2 py-2 text-right text-amber-600 whitespace-nowrap">{(item.avance_salario || 0) > 0 ? formatCurrency(item.avance_salario) : '-'}</td>
                            {tiposBeneficioNomina(nominaActual).map(t => {
                              const m = montoBeneficioTipo(item, t);
                              return <td key={t} className="px-2 py-2 text-right text-amber-600 whitespace-nowrap">{m > 0 ? formatCurrency(m) : '-'}</td>;
                            })}
                            <td className="px-2 py-2 text-right text-amber-600 whitespace-nowrap" title={item.dependientes_adicionales > 0 ? `${item.dependientes_adicionales} dependiente(s) × cápita` : ""}>
                              {(item.descuento_dependientes_tss || 0) > 0 ? formatCurrency(item.descuento_dependientes_tss) : '-'}
                            </td>
                            <td className="px-2 py-2 text-right whitespace-nowrap">{(item.otras_deducciones || 0) > 0 ? formatCurrency(item.otras_deducciones) : '-'}</td>
                            <td className="px-2 py-2 text-right font-bold text-green-600 whitespace-nowrap">{formatCurrency(item.salario_neto)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Trazabilidad */}
                  <div className="border-t border-slate-200 dark:border-slate-600 pt-4" data-testid="nomina-trazabilidad-section">
                    <p className="font-semibold text-[#0C314C] dark:text-white mb-3 flex items-center gap-2">
                      <History className="w-4 h-4 text-[#45E69A]" /> Trazabilidad de la Nómina
                    </p>
                    {construirTrazabilidad(nominaActual).length === 0 ? (
                      <p className="text-sm text-slate-400">Sin eventos registrados</p>
                    ) : (
                      <div>
                        {construirTrazabilidad(nominaActual).map((ev, i, arr) => (
                          <div key={i} className="flex gap-3" data-testid={`trazabilidad-evento-${ev.accion}`}>
                            <div className="flex flex-col items-center">
                              <span className={`w-3 h-3 rounded-full mt-1 shrink-0 ${
                                ev.accion === "eliminada" ? "bg-red-500" :
                                ev.accion === "pagada" ? "bg-[#0C314C]" :
                                ev.accion === "aprobada" ? "bg-blue-500" :
                                ev.accion === "recalculada" ? "bg-amber-500" : "bg-[#45E69A]"
                              }`} />
                              {i < arr.length - 1 && <span className="w-px flex-1 bg-slate-200 dark:bg-slate-600" />}
                            </div>
                            <div className="pb-4">
                              <p className="text-sm font-semibold capitalize text-[#0C314C] dark:text-white">{ev.accion}</p>
                              {ev.detalle && <p className="text-xs text-slate-500">{ev.detalle}</p>}
                              <p className="text-xs text-slate-400 mt-0.5">
                                Por: <span className="font-medium text-slate-600 dark:text-slate-300">{nombreUsuarioNomina(ev.usuario)}</span> · {formatFechaHora(ev.fecha)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNominaDetailDialog(false)}>Cerrar</Button>
              <Button 
                variant="outline" 
                onClick={() => openGenerarDocumentoDialog(nominaActual)}
                className="text-purple-600 border-purple-200 hover:bg-purple-50"
              >
                <Sparkles className="w-4 h-4 mr-2" /> Generar Documento IA
              </Button>
              {nominaActual?.estado === "borrador" && (
                <>
                  <Button variant="outline" onClick={handleRecalcularNomina} disabled={saving} className="border-[#0C314C]/20 text-[#0C314C] hover:bg-[#0C314C]/5" data-testid="recalcular-nomina-btn">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />} Recalcular
                  </Button>
                  <Button onClick={handleAprobarNomina} className="bg-blue-600 hover:bg-blue-700">
                    <CheckCircle className="w-4 h-4 mr-2" /> Aprobar
                  </Button>
                </>
              )}
              {nominaActual?.estado === "aprobada" && (
                <Button onClick={handlePagarNomina} className="bg-green-600 hover:bg-green-700">
                  <DollarSign className="w-4 h-4 mr-2" /> Marcar como Pagada
                </Button>
              )}
              <Button variant="outline" onClick={handleImprimirNomina} className="border-[#0C314C]/30 text-[#0C314C] dark:text-slate-200" data-testid="imprimir-nomina-btn">
                <Printer className="w-4 h-4 mr-2" /> Imprimir
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Generar Documento IA Dialog */}
        <Dialog open={showGenerarDocumentoDialog} onOpenChange={setShowGenerarDocumentoDialog}>
          <DialogContent className="max-w-4xl dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                Generar Documento de Nómina con IA
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Document Type Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo de Documento</Label>
                  <Select value={documentoTipo} onValueChange={setDocumentoTipo}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar tipo..." />
                    </SelectTrigger>
                    <SelectContent>
                      {tiposDocumento.map(tipo => (
                        <SelectItem key={tipo.id} value={tipo.id}>
                          <div>
                            <p className="font-medium">{tipo.nombre}</p>
                            <p className="text-xs text-slate-500">{tipo.descripcion}</p>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Empleado (opcional)</Label>
                  <Select value={documentoEmpleadoId || "all"} onValueChange={(v) => setDocumentoEmpleadoId(v === "all" ? null : v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Todos los empleados" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los empleados</SelectItem>
                      {nominaActual?.items?.map(item => (
                        <SelectItem key={item.user_id} value={item.user_id}>
                          {item.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Generate Button */}
              {!documentoGenerado && (
                <Button 
                  onClick={handleGenerarDocumento} 
                  disabled={generandoDocumento}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                >
                  {generandoDocumento ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generando documento con IA...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generar Documento
                    </>
                  )}
                </Button>
              )}

              {/* Document Preview */}
              {documentoGenerado && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-green-600 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Documento generado exitosamente
                    </h3>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setDocumentoGenerado(null)}>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Regenerar
                      </Button>
                      <Button variant="outline" size="sm" onClick={handlePrintDocumento}>
                        <Printer className="w-4 h-4 mr-2" />
                        Imprimir
                      </Button>
                      <Button size="sm" onClick={handleDownloadDocumento} className="bg-green-600 hover:bg-green-700">
                        <Download className="w-4 h-4 mr-2" />
                        Descargar
                      </Button>
                    </div>
                  </div>
                  
                  <div className="border rounded-lg overflow-hidden bg-white">
                    <ScrollArea className="h-[400px]">
                      <div 
                        className="p-4"
                        dangerouslySetInnerHTML={{ __html: documentoGenerado }}
                      />
                    </ScrollArea>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowGenerarDocumentoDialog(false)}>
                Cerrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Configuración General Dialog */}
        <Dialog open={showConfigNominaDialog} onOpenChange={setShowConfigNominaDialog}>
          <DialogContent className="max-w-2xl dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle>Configuración General de Nómina</DialogTitle>
            </DialogHeader>
            <ScrollArea className="max-h-[70vh]">
              <div className="space-y-6 pr-4">
                {/* Datos de Empresa */}
                <div>
                  <h3 className="font-medium mb-3">Datos de la Empresa</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Nombre de la Empresa</Label>
                      <Input value={parametrosForm.nombre_empresa} onChange={e => setParametrosForm({...parametrosForm, nombre_empresa: e.target.value})} />
                    </div>
                    <div>
                      <Label>RNC</Label>
                      <Input value={parametrosForm.rnc_empresa} onChange={e => setParametrosForm({...parametrosForm, rnc_empresa: e.target.value})} />
                    </div>
                    <div className="col-span-2">
                      <Label>Dirección</Label>
                      <Input value={parametrosForm.direccion_empresa} onChange={e => setParametrosForm({...parametrosForm, direccion_empresa: e.target.value})} />
                    </div>
                  </div>
                </div>

                {/* TSS */}
                <div className="border-t pt-4">
                  <h3 className="font-medium mb-3">Parámetros TSS (Seguridad Social)</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>AFP Empleado (%)</Label>
                      <Input type="number" step="0.01" value={parametrosForm.tss_empleado_afp} onChange={e => setParametrosForm({...parametrosForm, tss_empleado_afp: parseFloat(e.target.value)})} />
                    </div>
                    <div>
                      <Label>SFS Empleado (%)</Label>
                      <Input type="number" step="0.01" value={parametrosForm.tss_empleado_sfs} onChange={e => setParametrosForm({...parametrosForm, tss_empleado_sfs: parseFloat(e.target.value)})} />
                    </div>
                    <div>
                      <Label>AFP Empleador (%)</Label>
                      <Input type="number" step="0.01" value={parametrosForm.tss_empleador_afp} onChange={e => setParametrosForm({...parametrosForm, tss_empleador_afp: parseFloat(e.target.value)})} />
                    </div>
                    <div>
                      <Label>SFS Empleador (%)</Label>
                      <Input type="number" step="0.01" value={parametrosForm.tss_empleador_sfs} onChange={e => setParametrosForm({...parametrosForm, tss_empleador_sfs: parseFloat(e.target.value)})} />
                    </div>
                    <div>
                      <Label>SRL Empleador (%)</Label>
                      <Input type="number" step="0.01" value={parametrosForm.tss_empleador_srl} onChange={e => setParametrosForm({...parametrosForm, tss_empleador_srl: parseFloat(e.target.value)})} />
                    </div>
                    <div>
                      <Label>INFOTEP Empleador (%)</Label>
                      <Input type="number" step="0.01" value={parametrosForm.tss_empleador_infotep} onChange={e => setParametrosForm({...parametrosForm, tss_empleador_infotep: parseFloat(e.target.value)})} />
                    </div>
                  </div>
                </div>

                {/* Distribución de descuentos por quincena */}
                <div className="border-t pt-4" data-testid="distribucion-quincenal-section">
                  <h3 className="font-medium mb-1">Distribución de Descuentos (Nóminas Quincenales)</h3>
                  <p className="text-xs text-slate-500 mb-3">Indique en qué quincena se aplica cada descuento. "Ambas" divide el monto mitad y mitad entre los dos pagos. Solo aplica a nóminas quincenales.</p>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { campo: "descuento_tss_quincena", label: "TSS (AFP y SFS/ARS)" },
                      { campo: "descuento_isr_quincena", label: "ISR" },
                      { campo: "descuento_beneficios_quincena", label: "Otros Beneficios (seguros)" },
                      { campo: "descuento_dependientes_quincena", label: "Dependientes TSS" },
                      { campo: "descuento_prestamos_quincena", label: "Préstamos" },
                      { campo: "descuento_avances_quincena", label: "Avances de Salario" }
                    ].map(d => (
                      <div key={d.campo}>
                        <Label>{d.label}</Label>
                        <Select value={parametrosForm[d.campo] || "ambas"} onValueChange={v => setParametrosForm({ ...parametrosForm, [d.campo]: v })}>
                          <SelectTrigger data-testid={`${d.campo}-select`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="primera">Solo 1ra quincena</SelectItem>
                            <SelectItem value="segunda">Solo 2da quincena</SelectItem>
                            <SelectItem value="ambas">Ambas (mitad y mitad)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                </div>

                {/* ISR */}
                <div className="border-t pt-4">
                  <h3 className="font-medium mb-3">Parámetros ISR (Impuesto Sobre la Renta)</h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Exento Hasta (RD$)</Label>
                        <Input type="number" value={parametrosForm.isr_exento_hasta} onChange={e => setParametrosForm({...parametrosForm, isr_exento_hasta: parseFloat(e.target.value)})} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Tramo 1 Hasta (RD$)</Label>
                        <Input type="number" value={parametrosForm.isr_tramo1_hasta} onChange={e => setParametrosForm({...parametrosForm, isr_tramo1_hasta: parseFloat(e.target.value)})} />
                      </div>
                      <div>
                        <Label>Tramo 1 Porcentaje (%)</Label>
                        <Input type="number" value={parametrosForm.isr_tramo1_pct} onChange={e => setParametrosForm({...parametrosForm, isr_tramo1_pct: parseFloat(e.target.value)})} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Tramo 2 Hasta (RD$)</Label>
                        <Input type="number" value={parametrosForm.isr_tramo2_hasta} onChange={e => setParametrosForm({...parametrosForm, isr_tramo2_hasta: parseFloat(e.target.value)})} />
                      </div>
                      <div>
                        <Label>Tramo 2 Porcentaje (%)</Label>
                        <Input type="number" value={parametrosForm.isr_tramo2_pct} onChange={e => setParametrosForm({...parametrosForm, isr_tramo2_pct: parseFloat(e.target.value)})} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Tramo 3 Hasta (RD$)</Label>
                        <Input type="number" value={parametrosForm.isr_tramo3_hasta} onChange={e => setParametrosForm({...parametrosForm, isr_tramo3_hasta: parseFloat(e.target.value)})} />
                      </div>
                      <div>
                        <Label>Tramo 3 Porcentaje (%)</Label>
                        <Input type="number" value={parametrosForm.isr_tramo3_pct} onChange={e => setParametrosForm({...parametrosForm, isr_tramo3_pct: parseFloat(e.target.value)})} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Topes */}
                <div className="border-t pt-4">
                  <h3 className="font-medium mb-3">Topes Cotizables</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Tope AFP (RD$)</Label>
                      <Input type="number" value={parametrosForm.tope_cotizable_afp} onChange={e => setParametrosForm({...parametrosForm, tope_cotizable_afp: parseFloat(e.target.value)})} />
                    </div>
                    <div>
                      <Label>Tope SFS (RD$)</Label>
                      <Input type="number" value={parametrosForm.tope_cotizable_sfs} onChange={e => setParametrosForm({...parametrosForm, tope_cotizable_sfs: parseFloat(e.target.value)})} />
                    </div>
                  </div>
                </div>
              </div>
            </ScrollArea>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowConfigNominaDialog(false)}>Cancelar</Button>
              <Button onClick={handleSaveParametros} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Guardar Configuración
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Plantillas Dialog */}
        <Dialog open={showPlantillaDialog} onOpenChange={setShowPlantillaDialog}>
          <DialogContent className="max-w-2xl dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle>Plantillas de Nómina</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-500">
                Cargue plantillas personalizadas para la impresión y visualización de la nómina.
                Formatos soportados: HTML, Excel, Word, PDF.
              </p>
              
              {/* IA Import Button */}
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-800 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-purple-600 dark:text-purple-300" />
                  </div>
                  <div>
                    <p className="font-medium text-purple-700 dark:text-purple-300">Importar con IA</p>
                    <p className="text-xs text-purple-600 dark:text-purple-400">La IA analiza tu plantilla y mapea los campos automáticamente</p>
                  </div>
                </div>
                <Button 
                  onClick={() => { setShowPlantillaDialog(false); setShowPlantillaIADialog(true); }}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="import-with-ia-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" /> Usar IA
                </Button>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t dark:border-slate-600" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white dark:bg-slate-800 px-2 text-slate-500">o sube manualmente</span>
                </div>
              </div>
              
              {/* Upload Section */}
              <div className="p-4 border-2 border-dashed dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-700/30">
                <div className="flex flex-col items-center gap-3">
                  <Upload className="w-8 h-8 text-slate-400" />
                  <div className="text-center">
                    <p className="font-medium">Cargar nueva plantilla</p>
                    <p className="text-sm text-slate-500">Arrastre un archivo o haga clic para seleccionar</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Select value={plantillaForm.tipo} onValueChange={v => setPlantillaForm({...plantillaForm, tipo: v})}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="Tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="recibo">Recibo de Nómina</SelectItem>
                        <SelectItem value="resumen">Resumen General</SelectItem>
                        <SelectItem value="reporte_tss">Reporte TSS</SelectItem>
                        <SelectItem value="carta_trabajo">Carta de Trabajo</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="file"
                      accept=".html,.htm,.xlsx,.xls,.docx,.doc,.pdf"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setPlantillaForm({...plantillaForm, archivo: file, nombre: file.name.replace(/\.[^/.]+$/, "")});
                      }}
                      className="max-w-[200px]"
                    />
                    <Button
                      size="sm"
                      disabled={!plantillaForm.archivo || saving}
                      onClick={async () => {
                        if (!plantillaForm.archivo) return;
                        setSaving(true);
                        try {
                          const formData = new FormData();
                          formData.append('file', plantillaForm.archivo);
                          formData.append('nombre', plantillaForm.nombre || plantillaForm.archivo.name);
                          formData.append('descripcion', plantillaForm.descripcion || '');
                          formData.append('tipo', plantillaForm.tipo);
                          
                          await axios.post(`${API}/nomina/plantillas/upload`, formData, {
                            ...getAuthHeaders(),
                            headers: {
                              ...getAuthHeaders().headers,
                              'Content-Type': 'multipart/form-data'
                            }
                          });
                          toast.success("Plantilla cargada exitosamente");
                          setPlantillaForm({ nombre: "", descripcion: "", tipo: "recibo", archivo: null });
                          fetchData();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "Error al cargar plantilla");
                        } finally {
                          setSaving(false);
                        }
                      }}
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Subir"}
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Existing Templates */}
              <div className="space-y-3">
                <p className="font-medium text-sm text-slate-600 dark:text-slate-400">Plantillas Cargadas</p>
                
                {plantillasNomina.length === 0 ? (
                  <div className="text-center py-6 text-slate-500">
                    <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p>No hay plantillas cargadas</p>
                    <p className="text-xs">Suba una plantilla para comenzar</p>
                  </div>
                ) : (
                  <ScrollArea className="max-h-[300px]">
                    <div className="space-y-2 pr-4">
                      {plantillasNomina.map(p => (
                        <div key={p.id} className="p-3 border dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                p.archivo_extension === '.pdf' ? 'bg-red-100 text-red-600' :
                                p.archivo_extension === '.xlsx' || p.archivo_extension === '.xls' ? 'bg-green-100 text-green-600' :
                                p.archivo_extension === '.docx' || p.archivo_extension === '.doc' ? 'bg-blue-100 text-blue-600' :
                                'bg-amber-100 text-amber-600'
                              }`}>
                                <FileText className="w-5 h-5" />
                              </div>
                              <div>
                                <p className="font-medium">{p.nombre}</p>
                                <div className="flex items-center gap-2">
                                  <Badge className="text-xs bg-slate-100 text-slate-600 border-0">
                                    {p.tipo === 'recibo' ? 'Recibo' : 
                                     p.tipo === 'resumen' ? 'Resumen' : 
                                     p.tipo === 'reporte_tss' ? 'TSS' : p.tipo}
                                  </Badge>
                                  <span className="text-xs text-slate-400">
                                    {p.archivo_nombre || 'Sin archivo'}
                                  </span>
                                  {p.activa && (
                                    <Badge className="text-xs bg-green-100 text-green-600 border-0">Activa</Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              {p.archivo_path && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => window.open(`${API}/nomina/plantillas/${p.id}/download`, '_blank')}
                                  title="Descargar"
                                >
                                  <Download className="w-4 h-4" />
                                </Button>
                              )}
                              {!p.activa && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={async () => {
                                    try {
                                      await axios.put(`${API}/nomina/plantillas/${p.id}/activar`, {}, getAuthHeaders());
                                      toast.success("Plantilla activada");
                                      fetchData();
                                    } catch (err) {
                                      toast.error("Error al activar plantilla");
                                    }
                                  }}
                                  title="Activar como plantilla por defecto"
                                >
                                  <CheckCircle className="w-4 h-4 text-green-500" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-700"
                                onClick={async () => {
                                  if (!confirm("¿Eliminar esta plantilla?")) return;
                                  try {
                                    await axios.delete(`${API}/nomina/plantillas/${p.id}`, getAuthHeaders());
                                    toast.success("Plantilla eliminada");
                                    fetchData();
                                  } catch (err) {
                                    toast.error("Error al eliminar");
                                  }
                                }}
                                title="Eliminar"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </div>

              {/* Default Templates Info */}
              <div className="border-t pt-4">
                <p className="text-xs text-slate-500">
                  <strong>Tip:</strong> Use variables como {'{nombre}'}, {'{salario}'}, {'{periodo}'} en plantillas HTML para datos dinámicos.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPlantillaDialog(false)}>Cerrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Banco TXT Export Dialog */}
        <Dialog open={showBancoTxtDialog} onOpenChange={setShowBancoTxtDialog}>
          <DialogContent className="dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle>Exportar TXT para Banco</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-500">Genere archivos TXT en el formato requerido por cada banco para el pago de nómina.</p>
              
              {/* IA Analyze Button */}
              <div className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-blue-600 dark:text-blue-300" />
                  <div>
                    <p className="text-sm font-medium text-blue-700 dark:text-blue-300">¿Tienes un archivo TXT de ejemplo?</p>
                    <p className="text-xs text-blue-600 dark:text-blue-400">La IA puede analizar y aprender el formato</p>
                  </div>
                </div>
                <Button 
                  size="sm"
                  onClick={() => { setShowBancoTxtDialog(false); setShowBancoTxtIADialog(true); }}
                  variant="outline"
                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                  data-testid="analyze-banco-format-btn"
                >
                  <Sparkles className="w-4 h-4 mr-1" /> Analizar
                </Button>
              </div>
              
              <div>
                <Label>Seleccionar Banco *</Label>
                <Select value={bancoTxtForm.formato} onValueChange={v => setBancoTxtForm({...bancoTxtForm, formato: v})}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar formato de banco" /></SelectTrigger>
                  <SelectContent>
                    {bancosRD.map(b => <SelectItem key={b.id} value={b.id}>{b.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Nómina a Exportar *</Label>
                <Select value={bancoTxtForm.nomina_id} onValueChange={v => setBancoTxtForm({...bancoTxtForm, nomina_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar nómina" /></SelectTrigger>
                  <SelectContent>
                    {nominasHistorial.filter(n => n.estado === "aprobada" || n.estado === "pagada").map(n => (
                      <SelectItem key={n.id} value={n.id}>{n.periodo} - {formatCurrency(n.totales?.total_neto)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBancoTxtDialog(false)}>Cancelar</Button>
              <Button onClick={handleExportBancoTxt}>
                <Download className="w-4 h-4 mr-2" /> Generar TXT
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Nueva Nómina Dialog */}
        <Dialog open={showNuevaNominaDialog} onOpenChange={setShowNuevaNominaDialog}>
          <DialogContent className="max-w-2xl dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle>{nominaConfigEditMode ? "Editar Nómina" : "Crear Nueva Nómina"}</DialogTitle>
            </DialogHeader>
            <ScrollArea className="max-h-[70vh]">
              <div className="space-y-4 pr-4">
                {/* Información Básica */}
                <div className="space-y-4">
                  <h3 className="font-medium text-sm text-slate-600 dark:text-slate-400 border-b pb-2">Información Básica</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Nombre de la Nómina *</Label>
                      <Input value={nuevaNominaForm.nombre} onChange={e => setNuevaNominaForm({...nuevaNominaForm, nombre: e.target.value})} placeholder="Ej: Nómina Ejecutivos" />
                    </div>
                    <div>
                      <Label>Tipo de Período</Label>
                      <Select value={nuevaNominaForm.tipo_periodo} onValueChange={v => setNuevaNominaForm({...nuevaNominaForm, tipo_periodo: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="mensual">Mensual</SelectItem>
                          <SelectItem value="quincenal">Quincenal</SelectItem>
                          <SelectItem value="semanal">Semanal</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Descripción</Label>
                    <Textarea value={nuevaNominaForm.descripcion} onChange={e => setNuevaNominaForm({...nuevaNominaForm, descripcion: e.target.value})} placeholder="Descripción de esta nómina" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {nuevaNominaForm.tipo_periodo === "quincenal" ? (
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>1er Día de Pago</Label>
                          <Input type="number" min={1} max={31} value={nuevaNominaForm.dia_pago_1} onChange={e => setNuevaNominaForm({...nuevaNominaForm, dia_pago_1: parseInt(e.target.value) || 15})} data-testid="dia-pago-1-input" />
                        </div>
                        <div>
                          <Label>2do Día de Pago</Label>
                          <Input type="number" min={1} max={31} value={nuevaNominaForm.dia_pago_2} onChange={e => setNuevaNominaForm({...nuevaNominaForm, dia_pago_2: parseInt(e.target.value) || 30})} data-testid="dia-pago-2-input" />
                        </div>
                      </div>
                    ) : nuevaNominaForm.tipo_periodo === "semanal" ? (
                      <div>
                        <Label>Día de la Semana de Pago</Label>
                        <Select value={nuevaNominaForm.dia_semana_pago} onValueChange={v => setNuevaNominaForm({...nuevaNominaForm, dia_semana_pago: v})}>
                          <SelectTrigger data-testid="dia-semana-pago-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="lunes">Lunes</SelectItem>
                            <SelectItem value="martes">Martes</SelectItem>
                            <SelectItem value="miercoles">Miércoles</SelectItem>
                            <SelectItem value="jueves">Jueves</SelectItem>
                            <SelectItem value="viernes">Viernes</SelectItem>
                            <SelectItem value="sabado">Sábado</SelectItem>
                            <SelectItem value="domingo">Domingo</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    ) : (
                      <div>
                        <Label>Día de Pago</Label>
                        <Input type="number" min={1} max={31} value={nuevaNominaForm.dia_pago} onChange={e => setNuevaNominaForm({...nuevaNominaForm, dia_pago: parseInt(e.target.value) || 15})} data-testid="dia-pago-input" />
                      </div>
                    )}
                    <div className="flex items-center gap-4 pt-6">
                      <div className="flex items-center gap-2">
                        <Switch checked={nuevaNominaForm.es_default} onCheckedChange={v => setNuevaNominaForm({...nuevaNominaForm, es_default: v})} />
                        <Label className="font-normal">Es Default</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch checked={nuevaNominaForm.activa} onCheckedChange={v => setNuevaNominaForm({...nuevaNominaForm, activa: v})} />
                        <Label className="font-normal">Activa</Label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Configuración Bancaria */}
                <div className="space-y-4 pt-4">
                  <h3 className="font-medium text-sm text-slate-600 dark:text-slate-400 border-b pb-2">Configuración Bancaria</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Banco por Defecto</Label>
                      <Select value={nuevaNominaForm.banco_default} onValueChange={v => setNuevaNominaForm({...nuevaNominaForm, banco_default: v, formato_txt_banco: v})}>
                        <SelectTrigger><SelectValue placeholder="Seleccionar banco" /></SelectTrigger>
                        <SelectContent>
                          {bancosRD.map(b => <SelectItem key={b.id} value={b.id}>{b.nombre}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Formato TXT Banco</Label>
                      <Select value={nuevaNominaForm.formato_txt_banco} onValueChange={v => setNuevaNominaForm({...nuevaNominaForm, formato_txt_banco: v})}>
                        <SelectTrigger><SelectValue placeholder="Formato de archivo TXT" /></SelectTrigger>
                        <SelectContent>
                          {bancosRD.map(b => <SelectItem key={b.id} value={b.id}>{b.nombre}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Cuenta de la Empresa</Label>
                      <Input value={nuevaNominaForm.cuenta_empresa} onChange={e => setNuevaNominaForm({...nuevaNominaForm, cuenta_empresa: e.target.value})} placeholder="Número de cuenta" />
                    </div>
                    <div>
                      <Label>Tipo de Cuenta</Label>
                      <Select value={nuevaNominaForm.tipo_cuenta_empresa} onValueChange={v => setNuevaNominaForm({...nuevaNominaForm, tipo_cuenta_empresa: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="corriente">Corriente</SelectItem>
                          <SelectItem value="ahorro">Ahorro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Deducciones */}
                <div className="space-y-4 pt-4">
                  <h3 className="font-medium text-sm text-slate-600 dark:text-slate-400 border-b pb-2">Deducciones</h3>
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <Switch checked={nuevaNominaForm.incluir_tss} onCheckedChange={v => setNuevaNominaForm({...nuevaNominaForm, incluir_tss: v})} />
                      <Label className="font-normal">Incluir TSS (AFP, SFS)</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch checked={nuevaNominaForm.incluir_isr} onCheckedChange={v => setNuevaNominaForm({...nuevaNominaForm, incluir_isr: v})} />
                      <Label className="font-normal">Incluir ISR</Label>
                    </div>
                  </div>
                </div>

                {/* Plantillas */}
                <div className="space-y-4 pt-4">
                  <h3 className="font-medium text-sm text-slate-600 dark:text-slate-400 border-b pb-2">Plantillas de Impresión</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Plantilla de Recibo</Label>
                      <Select value={nuevaNominaForm.plantilla_recibo_id || "none"} onValueChange={v => setNuevaNominaForm({...nuevaNominaForm, plantilla_recibo_id: v === "none" ? "" : v})}>
                        <SelectTrigger><SelectValue placeholder="Seleccionar plantilla" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sin plantilla</SelectItem>
                          {plantillasNomina.filter(p => p.tipo === 'recibo').map(p => (
                            <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Plantilla de Resumen</Label>
                      <Select value={nuevaNominaForm.plantilla_resumen_id || "none"} onValueChange={v => setNuevaNominaForm({...nuevaNominaForm, plantilla_resumen_id: v === "none" ? "" : v})}>
                        <SelectTrigger><SelectValue placeholder="Seleccionar plantilla" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sin plantilla</SelectItem>
                          {plantillasNomina.filter(p => p.tipo === 'resumen').map(p => (
                            <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500">
                    Cargue plantillas en la sección "Plantillas" para seleccionarlas aquí.
                  </p>
                </div>

                {/* Notas */}
                <div className="pt-4">
                  <Label>Notas Adicionales</Label>
                  <Textarea value={nuevaNominaForm.notas} onChange={e => setNuevaNominaForm({...nuevaNominaForm, notas: e.target.value})} placeholder="Notas o comentarios sobre esta nómina" />
                </div>
              </div>
            </ScrollArea>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNuevaNominaDialog(false)}>Cancelar</Button>
              <Button onClick={handleSaveNominaConfig} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />} {nominaConfigEditMode ? "Actualizar" : "Crear Nómina"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Nomina Config Detail Dialog */}
        <Dialog open={showNominaConfigDetailDialog} onOpenChange={setShowNominaConfigDetailDialog}>
          <DialogContent className="max-w-lg dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle>Detalles de la Nómina</DialogTitle>
            </DialogHeader>
            {viewingNominaConfig && (
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <FileSpreadsheet className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-lg">{viewingNominaConfig.nombre}</p>
                      {viewingNominaConfig.es_default && <Badge className="bg-blue-100 text-blue-700 border-0">Default</Badge>}
                    </div>
                    <p className="text-sm text-slate-500">{viewingNominaConfig.descripcion}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Tipo de Período</p>
                    <p className="font-medium capitalize">{viewingNominaConfig.tipo_periodo}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">{viewingNominaConfig.tipo_periodo === "quincenal" ? "Días de Pago" : "Día de Pago"}</p>
                    <p className="font-medium capitalize">{viewingNominaConfig.tipo_periodo === "quincenal" ? `${viewingNominaConfig.dia_pago_1 || 15} y ${viewingNominaConfig.dia_pago_2 || 30}` : viewingNominaConfig.tipo_periodo === "semanal" ? (viewingNominaConfig.dia_semana_pago || "viernes") : (viewingNominaConfig.dia_pago || 15)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Banco</p>
                    <p className="font-medium">{bancosRD.find(b => b.id === viewingNominaConfig.banco_default)?.nombre || "No configurado"}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Estado</p>
                    <Badge className={viewingNominaConfig.activa ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-700"}>
                      {viewingNominaConfig.activa ? "Activa" : "Inactiva"}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-slate-500">Incluye TSS</p>
                    <p className="font-medium">{viewingNominaConfig.incluir_tss !== false ? "Sí" : "No"}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Incluye ISR</p>
                    <p className="font-medium">{viewingNominaConfig.incluir_isr !== false ? "Sí" : "No"}</p>
                  </div>
                </div>
                {viewingNominaConfig.notas && (
                  <div className="border-t pt-4">
                    <p className="text-slate-500 text-sm">Notas</p>
                    <p className="text-sm">{viewingNominaConfig.notas}</p>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNominaConfigDetailDialog(false)}>Cerrar</Button>
              <Button onClick={() => { setShowNominaConfigDetailDialog(false); openEditNominaConfig(viewingNominaConfig); }}>
                <Edit className="w-4 h-4 mr-2" /> Editar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Plantilla IA Dialog */}
        <Dialog open={showPlantillaIADialog} onOpenChange={(open) => { if (!open) resetPlantillaIA(); setShowPlantillaIADialog(open); }}>
          <DialogContent className="max-w-4xl dark:bg-slate-800 max-h-[90vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-500" />
                Importar Plantilla con IA
              </DialogTitle>
            </DialogHeader>
            
            {/* Steps indicator */}
            <div className="flex items-center justify-center gap-2 py-4 border-b dark:border-slate-700">
              {[
                { num: 1, label: "Subir Archivo" },
                { num: 2, label: "Análisis IA" },
                { num: 3, label: "Mapeo de Campos" },
                { num: 4, label: "Vista Previa" }
              ].map((step, i) => (
                <div key={step.num} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    plantillaIAStep >= step.num ? "bg-purple-500 text-white" : "bg-slate-200 text-slate-600"
                  }`}>
                    {step.num}
                  </div>
                  <span className={`ml-2 text-sm ${plantillaIAStep >= step.num ? "text-purple-600 font-medium" : "text-slate-400"}`}>
                    {step.label}
                  </span>
                  {i < 3 && <ChevronRight className="w-4 h-4 mx-4 text-slate-300" />}
                </div>
              ))}
            </div>

            <ScrollArea className="flex-1 pr-4">
              {/* Step 1: Upload */}
              {plantillaIAStep === 1 && (
                <div className="space-y-6 py-4">
                  <div className="p-6 border-2 border-dashed dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-700/30 text-center">
                    <Upload className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                    <p className="font-medium mb-2">Seleccione un archivo de plantilla</p>
                    <p className="text-sm text-slate-500 mb-4">Formatos: HTML, Excel (.xlsx), Word (.docx), PDF, TXT</p>
                    <Input
                      type="file"
                      accept=".html,.htm,.xlsx,.xls,.docx,.doc,.pdf,.txt"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setPlantillaIAFile(file);
                          fetchCamposDisponibles();
                        }
                      }}
                      className="max-w-xs mx-auto"
                      data-testid="plantilla-ia-file-input"
                    />
                    {plantillaIAFile && (
                      <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg inline-flex items-center gap-2">
                        <FileText className="w-5 h-5 text-green-600" />
                        <span className="font-medium">{plantillaIAFile.name}</span>
                        <span className="text-sm text-slate-500">({(plantillaIAFile.size / 1024).toFixed(1)} KB)</span>
                      </div>
                    )}
                  </div>
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      <strong>💡 Tip:</strong> Use variables como <code className="bg-blue-100 px-1 rounded">{"{{nombre_completo}}"}</code> en su plantilla.
                      La IA detectará los campos existentes y sugerirá el mapeo correcto.
                    </p>
                  </div>
                </div>
              )}

              {/* Step 2: Analysis */}
              {plantillaIAStep === 2 && plantillaIAAnalysis && (
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Card className="dark:bg-slate-700/50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Tipo Detectado</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Badge className="bg-purple-100 text-purple-700">{plantillaIAAnalysis.tipo_plantilla}</Badge>
                      </CardContent>
                    </Card>
                    <Card className="dark:bg-slate-700/50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Estructura</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm space-y-1">
                        {plantillaIAAnalysis.estructura?.tiene_encabezado && <Badge variant="outline">Encabezado</Badge>}
                        {plantillaIAAnalysis.estructura?.tiene_tabla && <Badge variant="outline" className="ml-1">Tabla</Badge>}
                        {plantillaIAAnalysis.estructura?.tiene_firma && <Badge variant="outline" className="ml-1">Firma</Badge>}
                      </CardContent>
                    </Card>
                  </div>

                  <Card className="dark:bg-slate-700/50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Map className="w-4 h-4" /> Campos Detectados ({plantillaIAAnalysis.campos_detectados?.length || 0})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {(plantillaIAAnalysis.campos_detectados || []).map((campo, i) => (
                          <div key={i} className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-600/50 rounded">
                            <span className="text-sm font-mono">{campo.texto_original}</span>
                            <div className="flex items-center gap-2">
                              <ArrowRight className="w-4 h-4 text-slate-400" />
                              <Badge className={
                                campo.confianza === "alta" ? "bg-green-100 text-green-700" :
                                campo.confianza === "media" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-700"
                              }>{campo.campo_sugerido}</Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {(plantillaIAAnalysis.variables_encontradas || []).length > 0 && (
                    <Card className="dark:bg-slate-700/50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Variables Existentes</CardTitle>
                      </CardHeader>
                      <CardContent className="flex flex-wrap gap-2">
                        {plantillaIAAnalysis.variables_encontradas.map((v, i) => (
                          <Badge key={i} variant="outline" className="font-mono">{v}</Badge>
                        ))}
                      </CardContent>
                    </Card>
                  )}

                  {(plantillaIAAnalysis.sugerencias || []).length > 0 && (
                    <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                      <p className="font-medium text-sm mb-2">💡 Sugerencias de la IA:</p>
                      <ul className="text-sm space-y-1 list-disc list-inside">
                        {plantillaIAAnalysis.sugerencias.map((s, i) => <li key={i}>{s}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: Mapping */}
              {plantillaIAStep === 3 && (
                <div className="space-y-4 py-4">
                  <div className="flex items-center justify-between">
                    <p className="font-medium">Mapeo de Campos</p>
                    <Button size="sm" variant="outline" onClick={addMapeoManual}>
                      <Plus className="w-4 h-4 mr-1" /> Agregar Campo
                    </Button>
                  </div>
                  
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {mapeosCampos.map((mapeo, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                        <Input
                          value={mapeo.texto_original}
                          onChange={(e) => {
                            const newMapeos = [...mapeosCampos];
                            newMapeos[i].texto_original = e.target.value;
                            setMapeosCampos(newMapeos);
                          }}
                          placeholder="Texto en plantilla"
                          className="flex-1"
                        />
                        <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <Select value={mapeo.campo_sistema} onValueChange={(v) => updateMapeo(i, v)}>
                          <SelectTrigger className="w-56">
                            <SelectValue placeholder="Campo del sistema" />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(
                              camposDisponibles.reduce((acc, c) => {
                                if (!acc[c.category]) acc[c.category] = [];
                                acc[c.category].push(c);
                                return acc;
                              }, {})
                            ).map(([cat, campos]) => (
                              <div key={cat}>
                                <div className="px-2 py-1 text-xs font-medium text-slate-500 uppercase">{cat}</div>
                                {campos.map(c => (
                                  <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                                ))}
                              </div>
                            ))}
                          </SelectContent>
                        </Select>
                        {mapeo.confianza && (
                          <Badge className={
                            mapeo.confianza === "alta" ? "bg-green-100 text-green-700" :
                            mapeo.confianza === "media" ? "bg-amber-100 text-amber-700" :
                            mapeo.confianza === "manual" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-700"
                          }>{mapeo.confianza}</Badge>
                        )}
                        <Button size="icon" variant="ghost" onClick={() => removeMapeo(i)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>

                  <div className="p-4 bg-slate-100 dark:bg-slate-700 rounded-lg">
                    <p className="text-sm font-medium mb-2">Campos Disponibles del Sistema:</p>
                    <div className="flex flex-wrap gap-1">
                      {camposDisponibles.slice(0, 15).map(c => (
                        <Badge key={c.key} variant="outline" className="text-xs font-mono">
                          {`{{${c.key}}}`}
                        </Badge>
                      ))}
                      {camposDisponibles.length > 15 && (
                        <Badge variant="outline" className="text-xs">+{camposDisponibles.length - 15} más</Badge>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Preview & Save */}
              {plantillaIAStep === 4 && (
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Nombre de la Plantilla</Label>
                      <Input
                        id="plantilla-nombre"
                        defaultValue={plantillaIAFile?.name?.replace(/\.[^/.]+$/, "") || ""}
                        placeholder="Nombre para identificar la plantilla"
                        data-testid="plantilla-nombre-input"
                      />
                    </div>
                    <div>
                      <Label>Tipo de Plantilla</Label>
                      <Select defaultValue={plantillaIAAnalysis?.tipo_plantilla || "recibo"}>
                        <SelectTrigger id="plantilla-tipo" data-testid="plantilla-tipo-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="recibo">Recibo de Nómina</SelectItem>
                          <SelectItem value="resumen">Resumen General</SelectItem>
                          <SelectItem value="reporte_tss">Reporte TSS</SelectItem>
                          <SelectItem value="carta_trabajo">Carta de Trabajo</SelectItem>
                          <SelectItem value="otro">Otro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Card className="dark:bg-slate-700/50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Resumen del Mapeo</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-500">Campos mapeados:</span>
                          <span className="font-medium">{mapeosCampos.length}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Confianza alta:</span>
                          <span className="font-medium text-green-600">{mapeosCampos.filter(m => m.confianza === "alta").length}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Archivo:</span>
                          <span className="font-medium">{plantillaIAFile?.name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Tamaño:</span>
                          <span className="font-medium">{(plantillaIAFile?.size / 1024).toFixed(1)} KB</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <p className="text-sm text-green-700 dark:text-green-300">
                      ✅ La plantilla está lista para guardar. Los campos mapeados se usarán automáticamente al generar recibos de nómina.
                    </p>
                  </div>
                </div>
              )}
            </ScrollArea>

            <DialogFooter className="border-t pt-4 dark:border-slate-700">
              <Button variant="outline" onClick={() => { resetPlantillaIA(); setShowPlantillaIADialog(false); }}>
                Cancelar
              </Button>
              {plantillaIAStep === 1 && (
                <Button onClick={handlePlantillaIAAnalyze} disabled={!plantillaIAFile || plantillaIAAnalyzing} data-testid="analyze-btn">
                  {plantillaIAAnalyzing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  Analizar con IA
                </Button>
              )}
              {plantillaIAStep === 2 && (
                <Button onClick={() => setPlantillaIAStep(3)}>
                  Continuar al Mapeo <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              )}
              {plantillaIAStep === 3 && (
                <>
                  <Button variant="outline" onClick={() => setPlantillaIAStep(2)}>Atrás</Button>
                  <Button onClick={() => setPlantillaIAStep(4)}>
                    Vista Previa <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </>
              )}
              {plantillaIAStep === 4 && (
                <>
                  <Button variant="outline" onClick={() => setPlantillaIAStep(3)}>Atrás</Button>
                  <Button 
                    onClick={() => {
                      const nombre = document.getElementById('plantilla-nombre')?.value;
                      const tipo = document.querySelector('[data-testid="plantilla-tipo-select"]')?.textContent?.toLowerCase() || "recibo";
                      handleSavePlantillaIA(nombre, tipo);
                    }} 
                    disabled={saving}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="save-plantilla-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                    Guardar Plantilla
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Banco TXT IA Dialog */}
        <Dialog open={showBancoTxtIADialog} onOpenChange={setShowBancoTxtIADialog}>
          <DialogContent className="max-w-3xl dark:bg-slate-800 max-h-[85vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-blue-500" />
                Analizar Formato TXT Bancario
              </DialogTitle>
            </DialogHeader>

            <ScrollArea className="flex-1 pr-4">
              <div className="space-y-4 py-4">
                {!bancoTxtAnalysis ? (
                  <>
                    <div className="p-6 border-2 border-dashed dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-700/30 text-center">
                      <FileCode className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                      <p className="font-medium mb-2">Seleccione un archivo TXT de ejemplo</p>
                      <p className="text-sm text-slate-500 mb-4">La IA analizará la estructura y sugerirá el mapeo de campos</p>
                      <Input
                        type="file"
                        accept=".txt"
                        onChange={(e) => setBancoTxtFile(e.target.files?.[0] || null)}
                        className="max-w-xs mx-auto"
                        data-testid="banco-txt-file-input"
                      />
                      {bancoTxtFile && (
                        <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg inline-flex items-center gap-2">
                          <FileText className="w-5 h-5 text-green-600" />
                          <span className="font-medium">{bancoTxtFile.name}</span>
                        </div>
                      )}
                    </div>
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        <strong>💡 Tip:</strong> Suba un archivo TXT de ejemplo del banco para que la IA detecte
                        automáticamente el formato (posicional o delimitado), longitud de registros y campos.
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="grid grid-cols-3 gap-4">
                      <Card className="dark:bg-slate-700/50">
                        <CardContent className="pt-4">
                          <p className="text-xs text-slate-500">Tipo de Formato</p>
                          <p className="font-medium capitalize">{bancoTxtAnalysis.analysis?.tipo_formato}</p>
                        </CardContent>
                      </Card>
                      <Card className="dark:bg-slate-700/50">
                        <CardContent className="pt-4">
                          <p className="text-xs text-slate-500">Longitud Registro</p>
                          <p className="font-medium">{bancoTxtAnalysis.analysis?.longitud_registro} caracteres</p>
                        </CardContent>
                      </Card>
                      <Card className="dark:bg-slate-700/50">
                        <CardContent className="pt-4">
                          <p className="text-xs text-slate-500">Banco Detectado</p>
                          <p className="font-medium">{bancoTxtAnalysis.analysis?.banco_detectado || "No identificado"}</p>
                        </CardContent>
                      </Card>
                    </div>

                    <Card className="dark:bg-slate-700/50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Campos Detectados</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {(bancoTxtAnalysis.analysis?.campos_detectados || []).map((campo, i) => (
                            <div key={i} className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-600/50 rounded text-sm">
                              <span className="font-medium">{campo.nombre_sugerido}</span>
                              <div className="flex items-center gap-4 text-slate-500">
                                <span>Pos: {campo.posicion_inicio}-{campo.posicion_fin}</span>
                                <span>Tipo: {campo.tipo}</span>
                                <Badge variant="outline">{campo.ejemplo}</Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="dark:bg-slate-700/50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Vista Previa del Archivo</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <pre className="bg-slate-900 text-green-400 p-3 rounded text-xs overflow-x-auto">
                          {(bancoTxtAnalysis.preview_lines || []).slice(0, 5).join("\n")}
                        </pre>
                      </CardContent>
                    </Card>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Nombre del Formato</Label>
                        <Input id="formato-nombre" placeholder="Ej: Popular - Nómina Mensual" />
                      </div>
                      <div>
                        <Label>Banco</Label>
                        <Select defaultValue={bancoTxtAnalysis.analysis?.banco_detectado?.toLowerCase().replace(/ /g, "_") || ""}>
                          <SelectTrigger id="formato-banco">
                            <SelectValue placeholder="Seleccione banco" />
                          </SelectTrigger>
                          <SelectContent>
                            {bancosRD.map(b => (
                              <SelectItem key={b.id} value={b.id}>{b.nombre}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </ScrollArea>

            <DialogFooter className="border-t pt-4 dark:border-slate-700">
              <Button variant="outline" onClick={() => { setShowBancoTxtIADialog(false); setBancoTxtFile(null); setBancoTxtAnalysis(null); }}>
                {bancoTxtAnalysis ? "Cerrar" : "Cancelar"}
              </Button>
              {!bancoTxtAnalysis ? (
                <Button onClick={handleBancoTxtAnalyze} disabled={!bancoTxtFile || bancoTxtAnalyzing} data-testid="analyze-banco-txt-btn">
                  {bancoTxtAnalyzing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  Analizar con IA
                </Button>
              ) : (
                <Button 
                  onClick={() => {
                    const nombre = document.getElementById('formato-nombre')?.value;
                    const banco = document.getElementById('formato-banco')?.textContent || "";
                    handleSaveBancoTxtFormat(nombre, banco);
                  }}
                  disabled={saving}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  Guardar Formato
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Configuración Envío Cumpleaños por Correo (solo admin) */}
        <Dialog open={showEnvioCumpleDialog} onOpenChange={setShowEnvioCumpleDialog}>
          <DialogContent className="max-w-md" data-testid="envio-cumpleanos-dialog">
            <DialogHeader>
              <DialogTitle className="text-[#0C314C] dark:text-white">Envío automático por correo</DialogTitle>
              <DialogDescription>
                Programe el envío mensual del diseño de cumpleaños (incrustado y adjunto) a los correos indicados.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="font-semibold">Envío automático activado</Label>
                <Switch
                  checked={!!envioCumpleCfg.enabled}
                  onCheckedChange={v => setEnvioCumpleCfg(p => ({ ...p, enabled: v }))}
                  data-testid="envio-cumpleanos-enabled-switch"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Día del mes</Label>
                  <Input
                    type="number" min={1} max={31}
                    value={envioCumpleCfg.dia}
                    onChange={e => setEnvioCumpleCfg(p => ({ ...p, dia: e.target.value }))}
                    data-testid="envio-cumpleanos-dia-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Hora (RD)</Label>
                  <Input
                    type="time"
                    value={envioCumpleCfg.hora}
                    onChange={e => setEnvioCumpleCfg(p => ({ ...p, hora: e.target.value }))}
                    data-testid="envio-cumpleanos-hora-input"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Correos destinatarios</Label>
                <Textarea
                  rows={3}
                  placeholder={"equipo@qasesores.com\notro@correo.com (separados por coma o línea)"}
                  value={envioCumpleCfg.correos}
                  onChange={e => setEnvioCumpleCfg(p => ({ ...p, correos: e.target.value }))}
                  data-testid="envio-cumpleanos-correos-textarea"
                />
              </div>
              {envioCumpleCfg.last_sent_periodo && (
                <p className="text-xs text-slate-500" data-testid="envio-cumpleanos-ultimo-envio">
                  Último envío: {envioCumpleCfg.last_sent_periodo}{envioCumpleCfg.last_sent_ok === false ? " (falló)" : envioCumpleCfg.last_sent_ok ? " (exitoso)" : ""}
                </p>
              )}
            </div>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={handleEnviarCumpleAhora}
                disabled={enviandoCumpleAhora || guardandoEnvioCumple}
                data-testid="envio-cumpleanos-enviar-ahora-btn"
              >
                {enviandoCumpleAhora ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Enviar ahora
              </Button>
              <Button
                onClick={handleGuardarEnvioCumple}
                disabled={guardandoEnvioCumple || enviandoCumpleAhora}
                className="bg-[#0C314C] hover:bg-[#16304f] text-white font-semibold"
                data-testid="envio-cumpleanos-guardar-btn"
              >
                {guardandoEnvioCumple ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
